// PDF Export Manager for Retirement Calculators
// Professional PDF generation with branding and comprehensive disclaimers

import jsPDF from 'jspdf';
import { FinancialConfig } from './FinancialConfig.js';
import { parseLumpSumWithdrawals } from './lumpSumWithdrawals.js';
import { parseLumpSumContributions } from './lumpSumContributions.js';

// Company details for disclaimers and branding
const COMPANY_DETAILS = {
    name: 'SuperCalc Pro Pty Ltd',
    acn: 'ACN 692 042 872',
    abn: 'ABN 31 692 042 872',
    website: 'www.supercalcpro.com.au',
    email: 'support@supercalcpro.com.au'
};

export class PDFExportManager {
    /**
     * Capture chart as image for PDF inclusion
     * @param {string} chartId - DOM element ID of the chart
     * @returns {Promise<string>} - Base64 image data or null if failed
     */
    static async captureChartImage(chartId) {
        try {
            const chartElement = document.getElementById(chartId);
            if (!chartElement) {
                console.warn(`Chart element #${chartId} not found`);
                return null;
            }

            // Find canvas within chart element (Chart.js creates a canvas)
            const canvas = chartElement.querySelector('canvas');
            if (!canvas) {
                console.warn(`No canvas found in #${chartId}`);
                return null;
            }

            // Convert canvas to base64 image
            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error(`Error capturing chart ${chartId}:`, error);
            return null;
        }
    }

    /**
     * Add chart to PDF with error handling
     */
    static addChartToPDF(doc, imageData, x, y, width, height, title = '') {
        if (!imageData) {
            // Show placeholder if chart capture failed
            doc.setFillColor(245, 247, 250);
            doc.rect(x, y, width, height, 'F');
            doc.setDrawColor(203, 213, 225);
            doc.rect(x, y, width, height, 'S');
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text('Chart unavailable', x + width/2, y + height/2, { align: 'center' });
            doc.setTextColor(0);
            return y + height;
        }

        try {
            // Add title if provided
            if (title) {
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text(title, x, y);
                y += 6;
            }

            // Add chart image
            doc.addImage(imageData, 'PNG', x, y, width, height);
            return y + height;
        } catch (error) {
            console.error('Error adding chart to PDF:', error);
            return y + height;
        }
    }
    /**
     * Draw a proper pie chart with filled wedges
     */
    static drawPieChart(doc, x, y, radius, data, labels, colors) {
        const total = data.reduce((sum, val) => sum + val, 0);
        let currentAngle = -Math.PI / 2; // Start at top
        
        // Draw each pie slice as a filled triangle/wedge
        data.forEach((value, index) => {
            if (value <= 0) return; // Skip zero values
            
            const sliceAngle = (value / total) * 2 * Math.PI;
            const endAngle = currentAngle + sliceAngle;
            
            // Create points for the wedge (use many points for smooth arc)
            const points = [[x, y]]; // Start at center
            const segments = Math.max(3, Math.floor(sliceAngle * 50)); // More segments for smoother arc
            
            for (let i = 0; i <= segments; i++) {
                const angle = currentAngle + (sliceAngle * i / segments);
                const px = x + radius * Math.cos(angle);
                const py = y + radius * Math.sin(angle);
                points.push([px, py]);
            }
            
            // Draw filled wedge
            doc.setFillColor(...colors[index]);
            doc.setDrawColor(255, 255, 255); // White border between slices
            doc.setLineWidth(0.5);
            
            // Use lines to create closed path
            if (points.length >= 3) {
                doc.lines(
                    points.slice(1).map((p, i) => {
                        const prev = points[i];
                        return [p[0] - prev[0], p[1] - prev[1]];
                    }),
                    points[0][0],
                    points[0][1],
                    [1, 1],
                    'FD'
                );
            }
            
            currentAngle = endAngle;
        });
        
        // Draw legend with color boxes and percentages
        let legendY = y - (data.length * 7) / 2;
        data.forEach((value, index) => {
            if (value <= 0) return;
            
            const percent = ((value / total) * 100).toFixed(1);
            const amount = Math.round(value).toLocaleString();
            
            // Color box
            doc.setFillColor(...colors[index]);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.3);
            doc.rect(x + radius + 10, legendY - 3, 6, 6, 'FD');
            
            // Label and value
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'bold');
            doc.text(`${labels[index]}`, x + radius + 19, legendY + 1);
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            doc.text(`$${amount} (${percent}%)`, x + radius + 19, legendY + 5);
            
            legendY += 10;
        });
        
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
    }

    /**
     * Draw a bar chart using jsPDF primitives
     */
    static drawBarChart(doc, x, y, width, height, data, labels, colors, maxValue = null) {
        const barWidth = width / data.length - 10;
        const max = maxValue || Math.max(...data);
        
        // Draw axes
        doc.setDrawColor(200, 200, 200);
        doc.line(x, y, x, y + height); // Y axis
        doc.line(x, y + height, x + width, y + height); // X axis
        
        // Draw bars
        data.forEach((value, index) => {
            const barHeight = (value / max) * height;
            const barX = x + (index * (barWidth + 10)) + 10;
            const barY = y + height - barHeight;
            
            // Bar
            doc.setFillColor(...(colors[index] || [59, 130, 246]));
            doc.rect(barX, barY, barWidth, barHeight, 'F');
            
            // Value label
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
            doc.text(`$${Math.round(value / 1000)}k`, barX + barWidth / 2, barY - 2, { align: 'center' });
            
            // X-axis label
            doc.setFontSize(7);
            const label = labels[index].length > 10 ? labels[index].substring(0, 10) + '...' : labels[index];
            doc.text(label, barX + barWidth / 2, y + height + 6, { align: 'center' });
        });
    }

    /**
     * Draw a line chart using jsPDF primitives
     */
    static drawLineChart(doc, x, y, width, height, dataPoints, labels, color = [59, 130, 246]) {
        const xStep = width / (dataPoints.length - 1);
        const max = Math.max(...dataPoints);
        const min = Math.min(...dataPoints);
        const range = max - min;
        
        // Draw axes
        doc.setDrawColor(200, 200, 200);
        doc.line(x, y, x, y + height);
        doc.line(x, y + height, x + width, y + height);
        
        // Draw line
        doc.setDrawColor(...color);
        doc.setLineWidth(1.5);
        for (let i = 0; i < dataPoints.length - 1; i++) {
            const x1 = x + (i * xStep);
            const y1 = y + height - ((dataPoints[i] - min) / range) * height;
            const x2 = x + ((i + 1) * xStep);
            const y2 = y + height - ((dataPoints[i + 1] - min) / range) * height;
            
            doc.line(x1, y1, x2, y2);
        }
        
        // Draw points
        doc.setFillColor(...color);
        dataPoints.forEach((point, index) => {
            const px = x + (index * xStep);
            const py = y + height - ((point - min) / range) * height;
            doc.circle(px, py, 1.5, 'F');
        });
        
        // Labels
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        [0, Math.floor(dataPoints.length / 2), dataPoints.length - 1].forEach(i => {
            if (labels[i]) {
                doc.text(labels[i], x + (i * xStep), y + height + 6, { align: 'center' });
            }
        });
    }

    /**
     * Add professional header with ACTUAL logo image and branding
     */
    static addProfessionalHeader(doc, reportTitle, subtitle = '') {
        const pageWidth = doc.internal.pageSize.width;
        
        // Blue gradient header background
        doc.setFillColor(30, 64, 175); // #1e40af
        doc.rect(0, 0, pageWidth, 35, 'F');
        
        // Try to load and add the actual logo image
        try {
            const logoImg = new Image();
            logoImg.src = '/logo.png';
            
            // If image loads, add it to PDF
            if (logoImg.complete || logoImg.width > 0) {
                doc.addImage(logoImg, 'PNG', 10, 6, 22, 22); // x, y, width, height
            } else {
                // Fallback: SC in white box if image doesn't load
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(12, 8, 18, 18, 2, 2, 'F');
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 64, 175);
                doc.text('SC', 21, 20, { align: 'center' });
            }
        } catch (err) {
            console.warn('Could not load logo image, using fallback:', err);
            // Fallback: SC in white box
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(12, 8, 18, 18, 2, 2, 'F');
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 64, 175);
            doc.text('SC', 21, 20, { align: 'center' });
        }
        
        // Company name
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.text('SuperCalc Pro Pty Ltd', 38, 14);
        
        // Tagline
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text('Professional Retirement Planning for Australians', 38, 21);
        
        // ACN/ABN
        doc.setFontSize(6);
        doc.text(`${COMPANY_DETAILS.acn} | ${COMPANY_DETAILS.abn}`, 38, 26);
        
        // Report title (right aligned)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(reportTitle, pageWidth - 12, 14, { align: 'right' });
        
        // Generation date (right aligned)
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        const dateStr = new Date().toLocaleString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        doc.text(`Generated: ${dateStr}`, pageWidth - 12, 21, { align: 'right' });
        
        if (subtitle) {
            doc.text(subtitle, pageWidth - 12, 27, { align: 'right' });
        }
        
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        
        return 40; // Return Y position after header
    }

    /**
     * Add standardized corporate disclaimer - STRONG legal protection
     * Automatically creates new page if needed to ensure disclaimer fits
     */
    static addCorporateDisclaimer(doc, currentYPos = null) {
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        
        // If currentYPos provided and not enough room (65px needed), add new page
        if (currentYPos && currentYPos > pageHeight - 65) {
            doc.addPage();
        }
        
        const disclaimerY = pageHeight - 48;
        
        // Disclaimer box with red border for emphasis
        doc.setFillColor(254, 242, 242); // Very light red background
        doc.setDrawColor(239, 68, 68); // Red border
        doc.setLineWidth(0.8);
        doc.rect(10, disclaimerY, pageWidth - 20, 46, 'FD');
        
        // Warning icon and title
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(220, 38, 38); // Red
        doc.text('IMPORTANT LEGAL DISCLAIMER', pageWidth / 2, disclaimerY + 5, { align: 'center' });
        
        // Disclaimer content
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(40, 40, 40);
        
        const lines = [
            'This report is for EDUCATIONAL and INFORMATIONAL PURPOSES ONLY. It does NOT constitute financial, taxation, legal, or investment advice.',
            '',
            'SuperCalc Pro is a calculation tool that projects scenarios based on YOUR inputs and assumptions. It does NOT provide personal recommendations',
            'or advice tailored to your individual circumstances. We are NOT licensed to provide financial advice and do NOT hold an Australian Financial',
            'Services License (AFSL).',
            '',
            'BEFORE making ANY financial decisions based on this report: (1) Consult a licensed financial adviser, (2) Consult a registered tax agent or',
            'accountant, (3) Verify all calculations independently, (4) Understand that past performance does not guarantee future results, and (5) Acknowledge',
            'that all projections are estimates based on assumptions and may not reflect actual outcomes.',
            '',
            'LIABILITY LIMITATION: To the maximum extent permitted by Australian law, SuperCalc Pro Pty Ltd and its directors, employees, and agents',
            'disclaim all liability for any loss, damage, cost, or expense (whether direct, indirect, or consequential) arising from your use of or reliance',
            'on this report. Use of this information is entirely at your own risk.',
            '',
            `${COMPANY_DETAILS.name} | ${COMPANY_DETAILS.acn} | ${COMPANY_DETAILS.abn} | ${COMPANY_DETAILS.website} | ${COMPANY_DETAILS.email}`
        ];
        
        let textY = disclaimerY + 11;
        lines.forEach(line => {
            if (line === '') {
                textY += 2;
            } else {
                doc.text(line, pageWidth / 2, textY, { align: 'center', maxWidth: pageWidth - 24 });
                textY += 2.8;
            }
        });
        
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
    }

    /**
     * Add executive summary with key metrics in boxes
     */
    static addExecutiveSummary(doc, yPos, keyMetrics) {
        const pageWidth = doc.internal.pageSize.width;
        
        // Section header
        doc.setFillColor(239, 246, 255); // Light blue background
        doc.rect(12, yPos, pageWidth - 24, 8, 'F');
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text('EXECUTIVE SUMMARY', 16, yPos + 5.5);
        doc.setTextColor(0, 0, 0);
        
        yPos += 13;
        
        // Key metrics in colored boxes
        const numMetrics = keyMetrics.length;
        const boxWidth = (pageWidth - 28) / Math.min(numMetrics, 4);
        const boxHeight = 22;
        
        keyMetrics.forEach((metric, idx) => {
            if (idx > 0 && idx % 4 === 0) {
                yPos += boxHeight + 4;
            }
            
            const col = idx % 4;
            const xPos = 14 + (col * boxWidth);
            
            // Metric box with border
            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(59, 130, 246); // Blue border
            doc.setLineWidth(0.5);
            doc.roundedRect(xPos, yPos, boxWidth - 3, boxHeight, 2, 2, 'FD');
            
            // Metric value (large, bold, colored)
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 64, 175);
            doc.text(metric.value, xPos + (boxWidth - 3) / 2, yPos + 10, { align: 'center' });
            
            // Metric label (small, gray)
            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(metric.label, xPos + (boxWidth - 3) / 2, yPos + 16, { align: 'center' });
            
            doc.setTextColor(0, 0, 0);
        });
        
        yPos += boxHeight + 8;
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
        
        return yPos;
    }

    /**
     * Add section header with colored background
     */
    static addSectionHeader(doc, yPos, title, icon = '') {
        const pageWidth = doc.internal.pageSize.width;
        
        // Section background
        doc.setFillColor(239, 246, 255);
        doc.rect(12, yPos, pageWidth - 24, 8, 'F');
        
        // Section title
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text(`${icon} ${title}`.trim(), 16, yPos + 5.5);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        
        return yPos + 12;
    }

    /**
     * Add highlighted info box
     */
    static addInfoBox(doc, yPos, content, type = 'info') {
        const pageWidth = doc.internal.pageSize.width;
        
        // Colors based on type
        const colors = {
            info: { bg: [219, 234, 254], border: [59, 130, 246], icon: '[i]' },
            success: { bg: [209, 250, 229], border: [16, 185, 129], icon: '[OK]' },
            warning: { bg: [254, 243, 199], border: [245, 158, 11], icon: '[!]' },
            error: { bg: [254, 226, 226], border: [239, 68, 68], icon: '[X]' }
        };
        
        const color = colors[type] || colors.info;
        
        // Pre-calculate wrapped lines to determine actual height needed
        doc.setFontSize(10);
        const lines = Array.isArray(content) ? content : [content];
        let totalWrappedLines = 0;
        lines.forEach(line => {
            const wrappedLines = doc.splitTextToSize(line, pageWidth - 40);
            totalWrappedLines += wrappedLines.length;
        });
        
        // Calculate box height based on ACTUAL wrapped lines
        const boxHeight = 8 + (totalWrappedLines * 5);
        
        // Box with colored border
        doc.setFillColor(...color.bg);
        doc.setDrawColor(...color.border);
        doc.setLineWidth(0.5);
        doc.rect(14, yPos, pageWidth - 28, boxHeight, 'FD');
        
        // Content - WITH PROPER TEXT WRAPPING
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        
        let textY = yPos + 6;
        lines.forEach(line => {
            // Split long lines to fit within box
            const wrappedLines = doc.splitTextToSize(line, pageWidth - 40);
            wrappedLines.forEach((wrappedLine, idx) => {
                if (idx === 0) {
                    doc.text(`${color.icon} ${wrappedLine}`, 18, textY);
                } else {
                    doc.text(`    ${wrappedLine}`, 18, textY);
                }
                textY += 5;
            });
        });
        
        const actualHeight = textY - yPos + 2;
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
        
        return yPos + actualHeight;
    }

    /**
     * Add a compact transparency/audit trail note to calculator PDFs.
     */
    static addCalculationTransparency(doc, yPos, calculatorType, inputs = {}, rows = []) {
        const linesByType = {
            basic: [
                `This calculator can consider: household status, age(s), super balances, retirement horizon, target income, other income/assets, homeowner status, Age Pension setting, pension tests/supplements, assumed return, inflation, pension growth, final balance target, and fees.`,
                `Rules applied: sustainable income calculation, Age Pension estimate when enabled, assets/income tests, deeming, supplements, fee deductions, inflation adjustment, and year-by-year balance reconciliation. Basic UI does not normally expose rent paid, so Rent Assistance is not part of the standard Basic result.`,
                `Trace available: ${rows?.length || 0} yearly rows covering balance, withdrawals, pension, other income, total income, returns, fees, and final balance.`,
            ],
            advanced: [
                `This calculator can consider: household details, ages/genders, super balances, TBC usage, Age Pension settings, homeowner/renter status, rent paid, Rent Assistance, Work Bonus, other income/assets, annuities, defined benefits, phased retirement, SG, strategy settings, historical/Monte Carlo sequences, asset allocation, alternative assets, lump sum withdrawals, lump sum contributions, and fees.`,
                `Rules applied: historical sequence testing, Monte Carlo assumptions, Age Pension eligibility/assets/income tests, deeming, supplements, Rent Assistance where eligible, minimum drawdowns, phased retirement SG, lump sum scheduling, and contribution cap flags.`,
                `Trace available: ${rows?.length || 0} detailed yearly rows covering income sources, balance, returns, lump sums, contributions, flags, and end balance.`,
            ],
            accumulation: [
                `This calculator can consider: household status, current/retirement ages, super balances, wages, SG, wage growth, concessional and non-concessional contributions, carry-forward, bring-forward, downsizer, spouse contribution, co-contribution, LISTO, TTR, work test, asset allocation, returns, inflation, and fees.`,
                `Rules applied: accumulation growth, SG from wages, contribution settings, enabled cap/eligibility checks, investment returns, franking assumptions where used, inflation adjustment, and fee deductions.`,
                `Trace available: ${rows?.length || 0} yearly rows covering projected balances and contribution/growth path where available.`,
            ],
        };

        const lines = linesByType[calculatorType] || linesByType.basic;
        yPos = this.addSectionHeader(doc, yPos, 'Calculation Transparency');
        return this.addInfoBox(doc, yPos, lines, 'info') + 4;
    }
    /**
     * Export data to CSV format
     */
    static exportToCSV(data, filename, headers) {
        // Create CSV content
        const csvRows = [];
        
        // Add headers
        csvRows.push(headers.join(','));
        
        // Add data rows
        data.forEach(row => {
            const values = headers.map(header => {
                const value = row[header.toLowerCase().replace(/ /g, '')] || row[header] || '';
                // Escape values that contain commas or quotes
                const escaped = String(value).replace(/"/g, '""');
                return escaped.includes(',') ? `"${escaped}"` : escaped;
            });
            csvRows.push(values.join(','));
        });
        
        // Create blob and download
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    /**
     * Export Basic Calculator results to CSV
     */
    static exportBasicToCSV(inputs, chartData) {
        if (!chartData || chartData.length === 0) {
            alert('No projection data to export. Please run the projection first.');
            return;
        }

        const headers = ['Year', 'Age', 'Balance', 'Withdrawal', 'Age Pension', 'Total Income', 'Returns', 'Fees'];
        const csvData = chartData.map((year, index) => ({
            Year: year.year || index + 1,
            Age: (inputs.yourAge || 65) + index,
            Balance: Math.round(year.balance || 0),
            Withdrawal: Math.round(year.withdrawal || 0),
            'Age Pension': Math.round(year.agePension || 0),
            'Total Income': Math.round((year.withdrawal || 0) + (year.agePension || 0)),
            Returns: Math.round(year.returns || 0),
            Fees: Math.round(year.fees || 0)
        }));

        this.exportToCSV(csvData, `Basic-Retirement-Data-${new Date().toISOString().split('T')[0]}.csv`, headers);
    }

    /**
     * Export Advanced Calculator results to CSV
     */
    static exportAdvancedToCSV(historicalSeries, inputs) {
        if (!historicalSeries || historicalSeries.length === 0) {
            alert('No projection data to export. Please run the projection first.');
            return;
        }

        const headers = ['Year', 'Age', 'Balance', 'Strategy Withdrawal', 'Lump Sum', 'Super Contributions', 'Contribution Flags', 'Returns', 'Age Pension', 'Other Income', 'Total Income'];
        const csvData = historicalSeries.map((year, index) => ({
            Year: (inputs.startYear || 2025) + index,
            Age: (inputs.yourAge || 65) + index,
            Balance: Math.round(year.balance || 0),
            'Strategy Withdrawal': Math.round(year.withdrawalFromSuper || year.withdrawal || 0),
            'Lump Sum': Math.round(year.lumpSumWithdrawal || 0),
            'Super Contributions': Math.round(year.lumpSumContribution || 0),
            'Contribution Flags': (year.contributionFlags || []).map((flag) => flag.message).join(' | '),
            Returns: Math.round(year.returns || 0),
            'Age Pension': Math.round(year.pension || year.agePension || 0),
            'Other Income': Math.round(year.otherIncome || 0),
            'Total Income': Math.round(year.totalIncome || 0)
        }));

        this.exportToCSV(csvData, `Advanced-Retirement-Data-${new Date().toISOString().split('T')[0]}.csv`, headers);
    }

    /**
     * Export Accumulation Calculator results to CSV
     */
    static exportAccumulationToCSV(results, inputs) {
        if (!results || (!results.yearlyData && !results.yearByYear) || (results.yearlyData && results.yearlyData.length === 0)) {
            alert('No projection data to export. Please run the calculation first.');
            return;
        }

        const yearData = results.yearlyData || results.yearByYear || [];
        const isCouple = inputs.accumulationRelationshipStatus === 'couple';
        
        const headers = isCouple 
            ? ['Year', 'Your Age', 'Partner Age', 'Your Balance', 'Partner Balance', 'Total Balance']
            : ['Year', 'Your Age', 'Your Balance'];
            
        const csvData = yearData.map((row) => {
            if (isCouple) {
                return {
                    Year: row.year,
                    'Your Age': row.userAge,
                    'Partner Age': row.partnerAge || '-',
                    'Your Balance': Math.round(row.userBalance || 0),
                    'Partner Balance': Math.round(row.partnerBalance || 0),
                    'Total Balance': Math.round(row.totalBalance || 0)
                };
            } else {
                return {
                    Year: row.year,
                    'Your Age': row.userAge,
                    'Your Balance': Math.round(row.userBalance || row.totalBalance || 0)
                };
            }
        });

        this.exportToCSV(csvData, `Accumulation-Data-${new Date().toISOString().split('T')[0]}.csv`, headers);
    }

    /**
     * Helper: Draw a simple table with headers and data
     */
    static drawTable(doc, startY, headers, data, columnWidths) {
        let yPos = startY;
        const xStart = 14;
        const rowHeight = 7;
        const cellPadding = 2;

        // Draw header row
        doc.setFillColor(52, 73, 94);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        
        let xPos = xStart;
        headers.forEach((header, i) => {
            doc.rect(xPos, yPos, columnWidths[i], rowHeight, 'F');
            doc.text(header, xPos + cellPadding, yPos + 5);
            xPos += columnWidths[i];
        });
        
        yPos += rowHeight;

        // Draw data rows
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);

        data.forEach((row, rowIndex) => {
            // Alternate row colors
            if (rowIndex % 2 === 0) {
                doc.setFillColor(245, 245, 245);
                let xPos = xStart;
                headers.forEach((_, i) => {
                    doc.rect(xPos, yPos, columnWidths[i], rowHeight, 'F');
                    xPos += columnWidths[i];
                });
            }

            let xPos = xStart;
            row.forEach((cell, i) => {
                const text = String(cell);
                doc.text(text, xPos + cellPadding, yPos + 5);
                xPos += columnWidths[i];
            });
            yPos += rowHeight;
        });

        return yPos + 5;
    }

    /**
     * Helper: Draw key-value pairs as a list
     */
    static drawKeyValuePairs(doc, startY, pairs, keyWidth = 70) {
        let yPos = startY;
        doc.setFontSize(10);

        pairs.forEach(([key, value]) => {
            doc.setFont(undefined, 'bold');
            doc.text(key + ':', 14, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(String(value), 14 + keyWidth, yPos);
            yPos += 6;
        });

        return yPos + 5;
    }

    /**
     * @param {boolean|{ returnBase64?: boolean, forEmail?: boolean }} exportOptions
     */
    static parseBasicExportOptions(exportOptions) {
        if (exportOptions === true) {
            return { returnBase64: true, forEmail: false };
        }
        if (!exportOptions || typeof exportOptions !== 'object') {
            return { returnBase64: false, forEmail: false };
        }
        return {
            returnBase64: !!exportOptions.returnBase64,
            forEmail: !!exportOptions.forEmail
        };
    }

    /** Year 1, every 5th year, and final year — keeps email PDF small but useful. */
    static getMilestoneProjectionRows(chartData, yourAge = 65) {
        if (!chartData?.length) {
            return [];
        }
        const indices = new Set([0]);
        for (let i = 4; i < chartData.length - 1; i += 5) {
            indices.add(i);
        }
        if (chartData.length > 1) {
            indices.add(chartData.length - 1);
        }
        return [...indices].sort((a, b) => a - b).map((idx) => {
            const row = chartData[idx];
            const withdrawal = row.withdrawal || 0;
            const pension = row.agePension || 0;
            const other = row.otherIncome || 0;
            return {
                year: idx + 1,
                age: yourAge + idx,
                balance: row.balance || 0,
                totalIncome: withdrawal + pension + other
            };
        });
    }

    /**
     * Compact, branded PDF for email attachment (typically 1–2 pages, well under size limits).
     */
    static exportBasicCalculatorForEmail(inputs, chartData, whatIfResults = {}) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        let yPos = this.addProfessionalHeader(
            doc,
            'Your Retirement Snapshot',
            'Basic Calculator — Personalised Report'
        );

        const totalSuper = (inputs.yourSuper || 0) + (inputs.partnerSuper || 0);
        const firstRow = chartData?.[0];
        const lastRow = chartData?.length ? chartData[chartData.length - 1] : null;
        const msi = Math.round(
            firstRow
                ? (firstRow.withdrawal || 0) + (firstRow.agePension || 0) + (firstRow.otherIncome || 0)
                : (inputs.maxSustainableIncome || 0)
        );
        const finalBalance = Math.round(lastRow?.balance ?? 0);
        const agePensionAvg = chartData?.length
            ? Math.round(chartData.reduce((sum, r) => sum + (r.agePension || 0), 0) / chartData.length)
            : Math.round(inputs.agePensionEstimate || 0);
        const yourAge = inputs.yourAge || 65;
        const periodYears = inputs.retirementPeriod || chartData?.length || 30;

        // Hero income banner
        doc.setFillColor(22, 101, 52);
        doc.roundedRect(14, yPos, pageWidth - 28, 30, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('MAXIMUM SUSTAINABLE INCOME (YEAR 1)', pageWidth / 2, yPos + 9, { align: 'center' });
        doc.setFontSize(20);
        doc.text(`$${msi.toLocaleString()} / year`, pageWidth / 2, yPos + 20, { align: 'center' });
        doc.setFontSize(7.5);
        doc.setFont(undefined, 'normal');
        const statusLabel = inputs.relationshipStatus === 'couple' ? 'Couple' : 'Single';
        doc.text(
            `${periodYears}-year projection · ${statusLabel} · You: age ${yourAge}${inputs.relationshipStatus === 'couple' ? ` · Partner: age ${inputs.partnerAge || '—'}` : ''}`,
            pageWidth / 2,
            yPos + 27,
            { align: 'center' }
        );
        doc.setTextColor(0, 0, 0);
        yPos += 38;

        yPos = this.addExecutiveSummary(doc, yPos, [
            { label: 'Total super (start)', value: `$${totalSuper.toLocaleString()}` },
            { label: 'Super balance (end)', value: `$${finalBalance.toLocaleString()}` },
            { label: 'Avg Age Pension', value: `$${agePensionAvg.toLocaleString()}/yr` }
        ]);

        this.addSectionHeader(doc, yPos, 'Your scenario');
        yPos += 10;

        const scenarioRows = [
            ['Relationship', statusLabel],
            ['Your super', `$${(inputs.yourSuper || 0).toLocaleString()}`],
            ...(inputs.relationshipStatus === 'couple'
                ? [['Partner super', `$${(inputs.partnerSuper || 0).toLocaleString()}`]]
                : []),
            ['Assumed return', `${inputs.assumedReturn ?? 7}% p.a.`],
            ['Inflation', `${inputs.assumedInflation ?? 2}% p.a.`],
            ['Age Pension modelled', inputs.includePension ? 'Yes' : 'No'],
            ['Homeowner', inputs.isHomeowner ? 'Yes' : 'No']
        ];

        doc.setFontSize(9);
        const col1 = 18;
        const col2 = 105;
        scenarioRows.forEach(([label, value]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${label}:`, col1, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(String(value), col2, yPos);
            yPos += 5;
        });
        yPos += 4;

        const milestones = this.getMilestoneProjectionRows(chartData, yourAge);
        if (milestones.length > 0) {
            if (yPos > pageHeight - 55) {
                doc.addPage();
                yPos = 20;
            }
            yPos = this.addSectionHeader(doc, yPos, 'Projection milestones');
            yPos += 4;

            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.setFillColor(241, 245, 249);
            doc.rect(14, yPos - 3, pageWidth - 28, 7, 'F');
            const cols = [18, 38, 68, 118, 158];
            ['Year', 'Age', 'Super balance', 'Total income'].forEach((h, i) => {
                doc.text(h, cols[i], yPos);
            });
            yPos += 6;
            doc.setFont(undefined, 'normal');
            milestones.forEach((row, idx) => {
                if (idx % 2 === 0) {
                    doc.setFillColor(248, 250, 252);
                    doc.rect(14, yPos - 3, pageWidth - 28, 5.5, 'F');
                }
                doc.text(String(row.year), cols[0], yPos);
                doc.text(String(row.age), cols[1], yPos);
                doc.text(`$${Math.round(row.balance).toLocaleString()}`, cols[2], yPos);
                doc.text(`$${Math.round(row.totalIncome).toLocaleString()}`, cols[3], yPos);
                yPos += 5.5;
            });
            yPos += 6;
        }

        const whatIfLines = [];
        if (whatIfResults?.liveToAge) {
            whatIfLines.push(
                `Live to age ${inputs.whatIfLiveToAge || '—'}: final balance $${Math.round(whatIfResults.liveToAge.finalBalance || 0).toLocaleString()}`
            );
        }
        if (whatIfResults?.moreIncome) {
            whatIfLines.push(
                `Extra income: max $${Math.round(whatIfResults.moreIncome.maxIncome || 0).toLocaleString()}/yr`
            );
        }
        if (whatIfResults?.annualFees || whatIfResults?.fees) {
            const fees = whatIfResults.annualFees || whatIfResults.fees;
            whatIfLines.push(
                `Fee change: final balance $${Math.round(fees.finalBalance || 0).toLocaleString()}`
            );
        }
        if (whatIfResults?.sequenceRisk) {
            whatIfLines.push(
                `Sequence risk: final balance $${Math.round(whatIfResults.sequenceRisk.finalBalance || 0).toLocaleString()}`
            );
        }

        if (whatIfLines.length > 0) {
            if (yPos > pageHeight - 40) {
                doc.addPage();
                yPos = 20;
            }
            yPos = this.addSectionHeader(doc, yPos, 'What-if highlights');
            yPos += 6;
            doc.setFontSize(8);
            whatIfLines.forEach((line) => {
                doc.text(`• ${line}`, 18, yPos);
                yPos += 5;
            });
            yPos += 4;
        }

        if (yPos > pageHeight - 52) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFillColor(239, 246, 255);
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.4);
        doc.roundedRect(14, yPos, pageWidth - 28, 22, 2, 2, 'FD');
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text('Next step', 18, yPos + 7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(40, 40, 40);
        doc.setFontSize(8);
        doc.text(
            'Stress-test this plan with Monte Carlo and historical scenarios in the Advanced Calculator at supercalcpro.com.au',
            18,
            yPos + 13,
            { maxWidth: pageWidth - 36 }
        );
        yPos += 28;

        this.addCorporateDisclaimer(doc, yPos);

        return doc.output('datauristring').replace(/^data:application\/pdf(?:;[^,]*)?;base64,/, '');
    }

    /**
     * Generate PDF for Basic Retirement Calculator
     */
    static exportBasicCalculator(inputs, chartData, whatIfResults, chartImages = {}) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        
        // Professional header with logo
        let yPos = this.addProfessionalHeader(doc, 'Basic Retirement Projection', 'Simple Calculator Report');

        // ===== EXECUTIVE SUMMARY =====
        const totalSuper = (inputs.yourSuper || 0) + (inputs.partnerSuper || 0);
        const maxSustainableIncome = inputs.maxSustainableIncome || 0;
        const successRate = inputs.successRate || 0;

        // Key metrics in colored boxes - ARRAY format
        const keyMetrics = [
            {
                label: 'Maximum Sustainable Income',
                value: `$${maxSustainableIncome.toLocaleString()}/year`
            },
            {
                label: 'Success Rate',
                value: `${successRate}%`
            },
            {
                label: 'Total Super Balance',
                value: `$${totalSuper.toLocaleString()}`
            }
        ];

        yPos = this.addExecutiveSummary(doc, yPos, keyMetrics);
        yPos += 8;

        // Additional context
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, pageWidth - 28, 12, 'F');
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`Retirement Period: ${inputs.retirementPeriod || 30} years | Super will last until age ${(inputs.yourAge || 65) + (inputs.retirementPeriod || 30)}`, 18, yPos + 5);
        doc.text(`Scenario testing indicates ${successRate}% likelihood this scenario maintains positive balance`, 18, yPos + 10);
        yPos += 17;

        // Two-column layout
        const col1X = 14;
        const col2X = pageWidth / 2 + 7;
        const startY = yPos;

        // Left column - Personal Details
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Personal Details', col1X, yPos);
        yPos += 6;

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        const detailsPairs = [
            ['Status', inputs.relationshipStatus === 'couple' ? 'Couple' : 'Single'],
            ['Your Age', inputs.yourAge],
            ['Your Super', `$${(inputs.yourSuper || 0).toLocaleString()}`],
            ...(inputs.relationshipStatus === 'couple' ? [
                ['Partner Age', inputs.partnerAge],
                ['Partner Super', `$${(inputs.partnerSuper || 0).toLocaleString()}`]
            ] : []),
            ['Total Super', `$${totalSuper.toLocaleString()}`],
            ['Desired Income', `$${(inputs.desiredAnnualIncome || 0).toLocaleString()}/yr`],
            ['Other Taxable', `$${(inputs.otherTaxableIncome || 0).toLocaleString()}/yr`],
            ['Other Non-Tax', `$${(inputs.otherNonTaxableIncome || 0).toLocaleString()}/yr`],
            ['Homeowner', inputs.isHomeowner ? 'Yes' : 'No'],
            ['Age Pension', inputs.includePension ? 'Yes' : 'No']
        ];

        detailsPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col1X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col1X + 30, yPos);
            yPos += 4.5;
        });

        // Right column - Assumptions & Fees
        yPos = startY;
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Assumptions', col2X, yPos);
        yPos += 5;

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        const assumptionsPairs = [
            ['Return Rate', `${inputs.assumedReturn || 7}% p.a.`],
            ['Inflation', `${inputs.assumedInflation || 2}% p.a.`],
            ['Pension Growth', `${inputs.pensionGrowthRate || 0.3}% p.a.`]
        ];

        assumptionsPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col2X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col2X + 30, yPos);
            yPos += 4.5;
        });

        yPos += 2;
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Fees', col2X, yPos);
        yPos += 5;

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        const feesPairs = [
            ['Fixed Admin', `$${inputs.adminFees || 1000}/year`],
            ['Admin %', `${inputs.adminFeePercentage || 0.1}%`],
            ['Investment', `${inputs.investmentFees || 0.5}%`],
            ['Insurance', `$${inputs.insuranceFees || 0}/year`]
        ];

        feesPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col2X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col2X + 25, yPos);
            yPos += 4.5;
        });

        // Reset yPos below both columns - tighter calculation
        const leftColumnHeight = detailsPairs.length * 4.5;
        const rightColumnHeight = (assumptionsPairs.length * 4.5) + 7 + (feesPairs.length * 4.5);
        yPos = startY + Math.max(leftColumnHeight, rightColumnHeight) + 8;

        yPos = this.addCalculationTransparency(doc, yPos, 'basic', inputs, chartData);

        // Age Pension Estimate
        if (inputs.agePensionEstimate) {
            doc.setFillColor(240, 255, 240);
            doc.rect(14, yPos, pageWidth - 28, 16, 'F');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Age Pension Estimate (Average Annual)', 18, yPos + 6);
            doc.setFontSize(12);
            doc.setTextColor(0, 128, 0);
            doc.text(`$${(inputs.agePensionEstimate || 0).toLocaleString()}/year`, 18, yPos + 12);
            doc.setTextColor(0);
            yPos += 20;
        }

        // ===== COMPLETE YEAR-BY-YEAR DATA (ALL YEARS) =====
        if (chartData && chartData.length > 0) {
            if (yPos > pageHeight - 50) {
                doc.addPage();
                yPos = 20;
            } else {
                yPos += 3;
            }

            this.addSectionHeader(doc, yPos, 'Year-by-Year Projection (Complete Data)');
            yPos += 10;

            // Summary statistics
            const totalYears = chartData.length;
            let finalBalance = 0;
            for (let i = chartData.length - 1; i >= 0; i--) {
                const row = chartData[i];
                if ((row.withdrawal || 0) > 0 || (row.totalIncome || 0) > 0) {
                    finalBalance = row.endingBalance ?? row.balance ?? 0;
                    break;
                }
            }
            if (!finalBalance && totalYears) {
                const last = chartData[totalYears - 1];
                finalBalance = last?.endingBalance ?? last?.balance ?? 0;
            }
            const avgWithdrawal = chartData.reduce((sum, r) => sum + (r.withdrawal || 0), 0) / totalYears;
            const avgPension = chartData.reduce((sum, r) => sum + (r.agePension || r.pension || 0), 0) / totalYears;

            doc.setFillColor(248, 250, 252);
            doc.rect(14, yPos, pageWidth - 28, 14, 'F');
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('Summary Statistics:', 18, yPos + 5);
            doc.setFont(undefined, 'normal');
            doc.text(`Total Years: ${totalYears} | Final Balance: $${Math.round(finalBalance).toLocaleString()}`, 18, yPos + 9);
            doc.text(`Average Withdrawal: $${Math.round(avgWithdrawal).toLocaleString()}/yr | Average Age Pension: $${Math.round(avgPension).toLocaleString()}/yr`, 18, yPos + 13);
            yPos += 18;

            // Table headers
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setFillColor(241, 245, 249);
            doc.rect(14, yPos - 3, pageWidth - 28, 7, 'F');
            const colX = [14, 28, 52, 76, 100, 124, 148, 172];
            doc.text('Year', colX[0], yPos);
            doc.text('Age', colX[1], yPos);
            doc.text('Balance', colX[2], yPos);
            doc.text('Withdraw', colX[3], yPos);
            doc.text('Pension', colX[4], yPos);
            doc.text('Other', colX[5], yPos);
            doc.text('Total', colX[6], yPos);
            yPos += 5;

            // Draw line
            doc.setDrawColor(148, 163, 184);
            doc.line(14, yPos, pageWidth - 14, yPos);
            yPos += 3;

            // Data (ALL years for accountant)
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            chartData.forEach((row, idx) => {
                if (yPos > pageHeight - 15) {
                    doc.addPage();
                    yPos = 20;
                    // Re-print headers
                    doc.setFont(undefined, 'bold');
                    doc.setFillColor(241, 245, 249);
                    doc.rect(14, yPos - 3, pageWidth - 28, 7, 'F');
                    doc.text('Year', colX[0], yPos);
                    doc.text('Age', colX[1], yPos);
                    doc.text('Balance', colX[2], yPos);
                    doc.text('Withdraw', colX[3], yPos);
                    doc.text('Pension', colX[4], yPos);
                    doc.text('Other', colX[5], yPos);
                    doc.text('Total', colX[6], yPos);
                    yPos += 5;
                    doc.setDrawColor(148, 163, 184);
                    doc.line(14, yPos, pageWidth - 14, yPos);
                    yPos += 3;
                    doc.setFont(undefined, 'normal');
                }
                
                // Alternate row colors
                if (idx % 2 === 0) {
                    doc.setFillColor(250, 250, 250);
                    doc.rect(14, yPos - 2.5, pageWidth - 28, 4, 'F');
                }

                const age = (inputs.yourAge || 65) + idx;
                doc.text(`${idx + 1}`, colX[0], yPos);
                doc.text(`${age}`, colX[1], yPos);
                doc.text(`$${Math.round(row.balance || 0).toLocaleString()}`, colX[2], yPos);
                doc.text(`$${Math.round(row.withdrawal || 0).toLocaleString()}`, colX[3], yPos);
                doc.text(`$${Math.round(row.agePension || 0).toLocaleString()}`, colX[4], yPos);
                doc.text(`$${Math.round(row.otherIncome || 0).toLocaleString()}`, colX[5], yPos);
                const totalInc = Math.round((row.withdrawal || 0) + (row.agePension || 0) + (row.otherIncome || 0));
                doc.text(`$${totalInc.toLocaleString()}`, colX[6], yPos);
                yPos += 4.5;
            });
            yPos += 8;
        }

        // ===== CHARTS SECTION =====
        // Page with first two charts - each taking ~45% of page height
        if (chartImages.balanceChart || chartImages.incomeChart) {
            doc.addPage();
            yPos = 15;
            
            // Section header
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Visual Analysis', 14, yPos);
            yPos += 8;
            
            const imgWidth = pageWidth - 28;
            // Each chart gets ~45% of usable page height (page minus margins and header)
            const usableHeight = pageHeight - 30; // 15 top + 15 bottom margin
            const chartHeight = (usableHeight - 20) / 2; // Split in half with 20px gap total
            
            // Chart 1: Balance Chart (top half)
            if (chartImages.balanceChart) {
                try {
                    doc.setFontSize(10);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('1. Super Balance Projection Over Time', 14, yPos);
                    yPos += 4;
                    
                    const chartData = typeof chartImages.balanceChart === 'string' ? chartImages.balanceChart : chartImages.balanceChart.data;
                    
                    if (chartData) {
                        doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, chartHeight - 10);
                        yPos += chartHeight - 5;
                    } else {
                        // Placeholder
                        doc.setFillColor(245, 247, 250);
                        doc.rect(14, yPos, imgWidth, chartHeight - 10, 'F');
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeight - 10) / 2, { align: 'center' });
                        doc.setTextColor(0);
                        yPos += chartHeight - 5;
                    }
                } catch (err) {
                    console.warn('Could not add balance chart to PDF:', err);
                    yPos += chartHeight - 5;
                }
            }
            
            // Chart 2: Income Chart (bottom half)
            if (chartImages.incomeChart) {
                try {
                    doc.setFontSize(10);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('2. Annual Income by Source', 14, yPos);
                    yPos += 4;
                    
                    const chartData = typeof chartImages.incomeChart === 'string' ? chartImages.incomeChart : chartImages.incomeChart.data;
                    
                    if (chartData) {
                        doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, chartHeight - 10);
                        yPos += chartHeight - 5;
                    } else {
                        // Placeholder
                        doc.setFillColor(245, 247, 250);
                        doc.rect(14, yPos, imgWidth, chartHeight - 10, 'F');
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeight - 10) / 2, { align: 'center' });
                        doc.setTextColor(0);
                        yPos += chartHeight - 5;
                    }
                } catch (err) {
                    console.warn('Could not add income chart to PDF:', err);
                }
            }
        }

        // Chart 3: Target Income - on its own page, same size, disclaimer right below
        if (chartImages.incomeChartTarget) {
            doc.addPage();
            yPos = 15;
            
            const imgWidth = pageWidth - 28;
            const usableHeight = pageHeight - 30;
            const chartHeight = (usableHeight - 20) / 2; // Same size as first two charts
            
            try {
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('3. Annual Income - Target Income Scenario', 14, yPos);
                yPos += 4;
                
                const chartData = typeof chartImages.incomeChartTarget === 'string' ? chartImages.incomeChartTarget : chartImages.incomeChartTarget.data;
                
                if (chartData) {
                    doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, chartHeight - 10);
                    yPos += chartHeight - 5;
                } else {
                    // Placeholder
                    doc.setFillColor(245, 247, 250);
                    doc.rect(14, yPos, imgWidth, chartHeight - 10, 'F');
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                    doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeight - 10) / 2, { align: 'center' });
                    doc.setTextColor(0);
                    yPos += chartHeight - 5;
                }
            } catch (err) {
                console.warn('Could not add target income chart to PDF:', err);
                yPos += chartHeight - 5;
            }
        }

        // What-If Scenarios - only if we didn't just add target chart (which has disclaimer right after)
        if (whatIfResults && Object.values(whatIfResults).some(r => r !== null) && !chartImages.incomeChartTarget) {
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = 20;
            } else {
                yPos += 3;
            }

            this.addSectionHeader(doc, yPos, 'What-If Scenarios');
            yPos += 10;

            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');

            if (whatIfResults.liveToAge) {
                doc.setFont(undefined, 'bold');
                doc.text(`Live to Age ${inputs.whatIfLiveToAge}:`, 14, yPos);
                yPos += 6;
                doc.setFont(undefined, 'normal');
                doc.text(`   Final Balance: $${Math.round(whatIfResults.liveToAge.finalBalance).toLocaleString()}`, 14, yPos);
                yPos += 5;
                doc.text(`   ${whatIfResults.liveToAge.outcome}`, 14, yPos);
                yPos += 10;
            }

            if (whatIfResults.moreIncome) {
                doc.setFont(undefined, 'bold');
                doc.text(`Additional Income (+$${inputs.whatIfMoreIncome?.toLocaleString()}):`, 14, yPos);
                yPos += 5;
                doc.setFont(undefined, 'normal');
                doc.text(`   Max Income: $${Math.round(whatIfResults.moreIncome.maxIncome).toLocaleString()}`, 14, yPos);
                yPos += 4.5;
                doc.text(`   ${whatIfResults.moreIncome.outcome}`, 14, yPos);
                yPos += 8;
            }

            if (whatIfResults.annualFees) {
                doc.setFont(undefined, 'bold');
                doc.text(`Higher Fees (${inputs.whatIfAnnualFees}%):`, 14, yPos);
                yPos += 5;
                doc.setFont(undefined, 'normal');
                doc.text(`   Final Balance: $${Math.round(whatIfResults.annualFees.finalBalance).toLocaleString()}`, 14, yPos);
                yPos += 4.5;
                doc.text(`   ${whatIfResults.annualFees.impact}`, 14, yPos);
                yPos += 8;
            }

            if (whatIfResults.sequenceRisk) {
                doc.setFont(undefined, 'bold');
                doc.text(`Sequence Risk (${inputs.whatIfSequenceRisk}%):`, 14, yPos);
                yPos += 5;
                doc.setFont(undefined, 'normal');
                doc.text(`   Final Balance: $${Math.round(whatIfResults.sequenceRisk.finalBalance).toLocaleString()}`, 14, yPos);
                yPos += 4.5;
                doc.text(`   ${whatIfResults.sequenceRisk.impact}`, 14, yPos);
                yPos += 8;
            }

            if (whatIfResults.survivingSpouse && !whatIfResults.survivingSpouse.error) {
                doc.setFont(undefined, 'bold');
                doc.text(`Surviving Spouse:`, 14, yPos);
                yPos += 5;
                doc.setFont(undefined, 'normal');
                doc.text(`   Couple Income: $${Math.round(whatIfResults.survivingSpouse.coupleIncome).toLocaleString()}`, 14, yPos);
                yPos += 4.5;
                doc.text(`   Surviving Spouse: $${Math.round(whatIfResults.survivingSpouse.survivingSpouseIncome).toLocaleString()}`, 14, yPos);
                yPos += 4.5;
                doc.text(`   ${whatIfResults.survivingSpouse.outcome}`, 14, yPos);
            }
        }

        // Corporate Disclaimer - STRONG legal protection
        // If target income chart was last, disclaimer goes right below it (yPos already positioned)
        // Otherwise check if we need a new page
        if (chartImages.incomeChartTarget) {
            // Target chart was last - disclaimer goes right below it, no new page
            this.addCorporateDisclaimer(doc, yPos);
        } else {
            // No target chart or it wasn't last - normal disclaimer placement
            if (yPos > pageHeight - 65) {
                doc.addPage();
                yPos = 20;
            }
            this.addCorporateDisclaimer(doc, yPos);
        }

        const exportOptions = this.parseBasicExportOptions(arguments[4]);
        if (exportOptions.returnBase64) {
            return doc.output('datauristring').replace(/^data:application\/pdf(?:;[^,]*)?;base64,/, '');
        }
        doc.save(`SuperCalcPro-Basic-Retirement-${new Date().toISOString().split('T')[0]}.pdf`);
    }

    /**
     * Generate PDF for Advanced Retirement Calculator - PROFESSIONAL VERSION
     */
    static exportAdvancedCalculator(inputs, historicalResults, monteCarloResults, withdrawalStrategy, chartImages = {}) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        
        // Professional header with logo
        let yPos = this.addProfessionalHeader(doc, 'Advanced Retirement Analysis', 'Monte Carlo & Historical Projection');

        const totalSuper = (inputs.yourSuper || 0) + (inputs.partnerSuper || 0);
        const sustainableIncome = historicalResults?.sustainableIncome || 0;
        
        // ===== EXECUTIVE SUMMARY =====
        // Key metrics in colored boxes - MUST BE ARRAY
        const keyMetrics = [
            {
                label: 'Maximum Sustainable Income',
                value: `$${sustainableIncome.toLocaleString()}/year`
            },
            {
                label: 'Monte Carlo Success Rate',
                value: `${monteCarloResults?.successRate || 'N/A'}%`
            },
            {
                label: 'Total Super Balance',
                value: `$${totalSuper.toLocaleString()}`
            }
        ];

        yPos = this.addExecutiveSummary(doc, yPos, keyMetrics);
        yPos += 5;

        // Strategy and period info
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, pageWidth - 28, 18, 'F');
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Withdrawal Strategy: ${this.getStrategyName(withdrawalStrategy)}`, 18, yPos + 6);
        doc.text(`Analysis Period: ${inputs.startYear || 1980} to ${(inputs.startYear || 1980) + (inputs.periodLength || 35) - 1} (${inputs.periodLength || 35} years)`, 18, yPos + 12);
        yPos += 23;

        yPos = this.addCalculationTransparency(
            doc,
            yPos,
            'advanced',
            inputs,
            historicalResults?.yearlyData || historicalResults?.incomeByYear || []
        );

        // Two-column layout for inputs
        const col1X = 14;
        const col2X = pageWidth / 2 + 7;
        const startY = yPos;

        // Left column - Personal Details
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Personal Details', col1X, yPos);
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const personalPairs = [
            ['Status', inputs.relationshipStatus === 'couple' ? 'Couple' : 'Single'],
            ['Your Age', inputs.yourAge || 65],
            ...(inputs.yourRetirementAge ? [['Your Retirement Age', inputs.yourRetirementAge]] : []),
            ['Your Super', `$${(inputs.yourSuper || 0).toLocaleString()}`],
            ...(inputs.relationshipStatus === 'couple' ? [
                ['Partner Age', inputs.partnerAge || 65],
                ...(inputs.partnerRetirementAge ? [['Partner Retirement Age', inputs.partnerRetirementAge]] : []),
                ['Partner Super', `$${(inputs.partnerSuper || 0).toLocaleString()}`]
            ] : []),
            ['Total Super', `$${totalSuper.toLocaleString()}`],
            ['Other Income', `$${(inputs.otherTaxableIncome || 0).toLocaleString()}/yr`],
            ['Homeowner', inputs.isHomeowner ? 'Yes' : 'No'],
            ['Age Pension', inputs.includePension ? 'Yes' : 'No']
        ];

        personalPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col1X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col1X + 35, yPos);
            yPos += 5;
        });

        // Right column - Portfolio & Fees
        yPos = startY;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Portfolio Allocation', col2X, yPos);
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        
        // Check if data is from SMSF transfer
        const fromSMSF = inputs.transferFromSMSF || false;
        
        const portfolioPairs = [
            ['S&P 500', `${inputs.sp500 || 0}%`],
            ['Aus Shares', `${inputs.ausShares || 0}%`],
            ['Intl Shares', `${inputs.intlShares || 0}%`],
            ['Bonds', `${inputs.bonds || 0}%`],
            ['Cash', `${inputs.cash || 0}%`]
        ];

        // Add infrastructure if present
        if (inputs.infrastructurePct && inputs.infrastructurePct > 0) {
            portfolioPairs.push(['Infrastructure', `${inputs.infrastructurePct.toFixed(1)}%`]);
        }

        portfolioPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col2X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col2X + 30, yPos);
            yPos += 5;
        });

        // Add SMSF transfer indicator if applicable
        if (fromSMSF) {
            yPos += 2;
            doc.setFontSize(8);
            doc.setTextColor(59, 130, 246);
            doc.text('(From SMSF Transfer)', col2X, yPos);
            doc.setTextColor(0);
            doc.setFontSize(10);
            yPos += 3;
        }

        yPos += 3;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Fees', col2X, yPos);
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const feesPairs = [
            ['Admin', `$${inputs.adminFees || 0} + ${inputs.adminFeePercentage || 0}%`],
            ['Investment', `${inputs.investmentFees || 0}%`],
            ['Transaction', `${inputs.transactionCosts || 0.08}%`],
            ['Insurance', `$${inputs.insuranceFees || 0}`]
        ];

        feesPairs.forEach(([key, val]) => {
            doc.setFont(undefined, 'bold');
            doc.text(`${key}:`, col2X, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(`${val}`, col2X + 25, yPos);
            yPos += 5;
        });

        // Reset yPos to continue below both columns
        yPos = startY + Math.max(personalPairs.length * 5, portfolioPairs.length * 5 + feesPairs.length * 5 + 15) + 5;

        // Add phased retirement callout if enabled
        const isPhasedRetirement = inputs.isPhasedRetirement && inputs.relationshipStatus === 'couple' &&
                                    inputs.yourRetirementAge && inputs.partnerRetirementAge &&
                                    inputs.yourRetirementAge !== inputs.partnerRetirementAge;
        if (isPhasedRetirement) {
            this.addInfoBox(doc, yPos,
                `📅 Phased Retirement Enabled: This analysis models realistic retirement where partners retire at different ages. ` +
                `Partner A retires at age ${inputs.yourRetirementAge}, Partner B at age ${inputs.partnerRetirementAge}. ` +
                `The simulation accounts for continued employment income and super contributions from the working partner until both are retired.`,
                'info'
            );
            yPos += 27;
        }

        // Withdrawal Strategy on same page
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Withdrawal Strategy', 14, yPos);
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        
        if (withdrawalStrategy === 'fixed' || withdrawalStrategy === 'Fixed') {
            doc.text(`Strategy: Fixed Income | Target: $${(inputs.targetIncome || 0).toLocaleString()}/yr`, 14, yPos);
        } else if (withdrawalStrategy === 'DynamicSWR') {
            doc.text(`Strategy: Dynamic SWR | Initial Rate: ${inputs.swrRate || 4}%`, 14, yPos);
        } else if (withdrawalStrategy === 'VanguardGuardrails') {
            doc.text(`Strategy: Vanguard Guardrails | Initial Rate: ${inputs.vanguardInitialRate || 4.5}%`, 14, yPos);
        } else if (withdrawalStrategy === 'FloorAndCeiling') {
            doc.text(`Strategy: Floor & Ceiling | Initial: ${inputs.floorCeilingInitialRate || 5}%, Floor: ${inputs.floorCeilingFloorRate || 4}%, Ceiling: ${inputs.floorCeilingCeilingRate || 6}%`, 14, yPos);
        }
        yPos += 6;

        const plannedLumpSums = parseLumpSumWithdrawals(inputs);
        if (plannedLumpSums.length > 0) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Planned lump sum withdrawals:', 14, yPos);
            yPos += 5;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            plannedLumpSums.forEach((row, idx) => {
                const when = row.scheduleType === 'year'
                    ? `plan year ${row.scheduleValue}`
                    : `age ${row.scheduleValue}`;
                const label = row.label ? ` (${row.label})` : '';
                doc.text(`${idx + 1}. $${Math.round(row.amount).toLocaleString()} at ${when}${label}`, 18, yPos);
                yPos += 4;
            });
            yPos += 4;
        } else {
            yPos += 2;
        }

        const plannedContributions = parseLumpSumContributions(inputs);
        if (plannedContributions.length > 0) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Planned lump sum super contributions:', 14, yPos);
            yPos += 5;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            plannedContributions.forEach((row, idx) => {
                const when = row.scheduleType === 'year'
                    ? `plan year ${row.scheduleValue}`
                    : `age ${row.scheduleValue}`;
                const recipient = row.recipient === 'partner' ? 'partner' : row.recipient === 'split' ? 'split' : 'you';
                const type = row.contributionType.replace(/([A-Z])/g, ' $1').toLowerCase();
                const label = row.label ? ` (${row.label})` : '';
                doc.text(`${idx + 1}. $${Math.round(row.amount).toLocaleString()} ${type} to ${recipient} at ${when}${label}`, 18, yPos);
                yPos += 4;
            });
            yPos += 4;
        }

        // ===== ALTERNATIVE ASSETS SECTION =====
        const hasAlternativeAssets = (inputs.residentialProperties && inputs.residentialProperties.length > 0) ||
                                     (inputs.commercialProperties && inputs.commercialProperties.length > 0) ||
                                     (inputs.alternativeAssets && inputs.alternativeAssets.length > 0) ||
                                     (inputs.collectiblesAssets && inputs.collectiblesAssets.length > 0);

        if (hasAlternativeAssets) {
            // Check if we need a new page
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = this.addProfessionalHeader(doc, 'Advanced Retirement Analysis', 'Alternative Assets');
            }

            doc.setFillColor(248, 250, 252);
            doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Alternative Assets Portfolio', 18, yPos + 6);
            doc.setTextColor(0);
            yPos += 12;

            // Residential Properties
            if (inputs.residentialProperties && inputs.residentialProperties.length > 0) {
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Residential Properties', 14, yPos);
                yPos += 6;

                inputs.residentialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.name || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(prop.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Rental Income: $${(prop.rentalIncome || 0).toLocaleString()}/yr`, 90, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}%`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Commercial Properties
            if (inputs.commercialProperties && inputs.commercialProperties.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = this.addProfessionalHeader(doc, 'Advanced Retirement Analysis', 'Alternative Assets (cont.)');
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Commercial Properties', 14, yPos);
                yPos += 6;

                inputs.commercialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.name || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(prop.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Rental Income: $${(prop.rentalIncome || 0).toLocaleString()}/yr`, 90, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}%`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Infrastructure & Alternative Assets
            if (inputs.alternativeAssets && inputs.alternativeAssets.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = this.addProfessionalHeader(doc, 'Advanced Retirement Analysis', 'Alternative Assets (cont.)');
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Infrastructure & Alternative Investments', 14, yPos);
                yPos += 6;

                inputs.alternativeAssets.forEach((asset, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${asset.name || `Asset ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(asset.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Expected Return: ${(asset.returnRate || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Collectibles (Non-Productive Assets)
            if (inputs.collectiblesAssets && inputs.collectiblesAssets.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = this.addProfessionalHeader(doc, 'Advanced Retirement Analysis', 'Alternative Assets (cont.)');
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Collectibles (Non-Productive Assets)', 14, yPos);
                yPos += 6;

                doc.setFontSize(8);
                doc.setFont(undefined, 'italic');
                doc.setTextColor(100);
                doc.text('Note: Collectibles do not generate income but are tracked for total wealth.', 14, yPos);
                doc.setTextColor(0);
                yPos += 6;

                inputs.collectiblesAssets.forEach((asset, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${asset.name || `Collectible ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(asset.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Capital Growth: ${(asset.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            yPos += 4;
        }

        // ===== MONTE CARLO RESULTS (Same Page) =====
        if (monteCarloResults && monteCarloResults.successRate !== undefined) {
            doc.setFillColor(240, 255, 240);
            doc.rect(14, yPos, pageWidth - 28, 22, 'F');
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Monte Carlo (1,000 simulations):', 18, yPos + 7);
            doc.setFontSize(16);
            doc.setTextColor(0, 128, 0);
            doc.text(`Success Rate: ${monteCarloResults.successRate}%`, 18, yPos + 16);
            doc.setTextColor(0);
            yPos += 26;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`Testing target income of $${(inputs.targetIncome || 0).toLocaleString()}/year`, 14, yPos);
            yPos += 6;

            const mcPairs = [
                ['10th Percentile', `$${Math.round(monteCarloResults.percentile10 || 0).toLocaleString()}`],
                ['50th Percentile', `$${Math.round(monteCarloResults.medianFinalBalance || 0).toLocaleString()}`],
                ['90th Percentile', `$${Math.round(monteCarloResults.percentile90 || 0).toLocaleString()}`]
            ];

            mcPairs.forEach(([key, val]) => {
                doc.setFont(undefined, 'bold');
                doc.text(`${key}:`, 14, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(`${val}`, 60, yPos);
                yPos += 5;
            });
            yPos += 6;
        }

        // ===== ROLLING PERIOD ANALYSIS =====
        if (historicalResults.rollingPeriodStats) {
            doc.setFillColor(255, 250, 240);
            doc.rect(14, yPos, pageWidth - 28, 26, 'F');
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Rolling Period Analysis', 18, yPos + 7);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`${historicalResults.rollingPeriodStats.count} historical ${inputs.periodLength}-year periods analyzed`, 18, yPos + 13);
            yPos += 18;
            
            doc.setFontSize(9);
            const col1 = 18;
            const col2 = 80;
            const col3 = 142;
            doc.setFont(undefined, 'bold');
            doc.text('Worst Period:', col1, yPos);
            doc.text('Median Period:', col2, yPos);
            doc.text('Best Period:', col3, yPos);
            yPos += 5;
            doc.setFont(undefined, 'normal');
            doc.text(`$${historicalResults.rollingPeriodStats.worst.toLocaleString()}/yr`, col1, yPos);
            doc.text(`$${historicalResults.rollingPeriodStats.median.toLocaleString()}/yr`, col2, yPos);
            doc.text(`$${historicalResults.rollingPeriodStats.best.toLocaleString()}/yr`, col3, yPos);
            yPos += 10;
        }

        // ===== COMPREHENSIVE YEAR-BY-YEAR DATA (ALL YEARS) =====
        if (historicalResults.yearlyData && historicalResults.yearlyData.length > 0) {
            // Check if we need a new page
            if (yPos > pageHeight - 40) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            this.addSectionHeader(doc, yPos, 'Year-by-Year Projection (Complete Data)');
            yPos += 10;

            // Summary statistics box
            const totalYears = historicalResults.yearlyData.length;
            const finalBalance = historicalResults.yearlyData[totalYears - 1]?.balance || 0;
            const avgWithdrawal = historicalResults.yearlyData.reduce((sum, r) => sum + (r.withdrawalFromSuper || 0), 0) / totalYears;
            const avgLumpSum = historicalResults.yearlyData.reduce((sum, r) => sum + (r.lumpSumWithdrawal || 0), 0) / totalYears;
            const avgContribution = historicalResults.yearlyData.reduce((sum, r) => sum + (r.lumpSumContribution || 0), 0) / totalYears;
            const avgPension = historicalResults.yearlyData.reduce((sum, r) => sum + (r.pension || 0), 0) / totalYears;
            const avgReturn = historicalResults.yearlyData.reduce((sum, r) => sum + (r.realReturn || 0), 0) / totalYears;

            doc.setFillColor(248, 250, 252);
            doc.rect(14, yPos, pageWidth - 28, 24, 'F');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Summary Statistics:', 18, yPos + 7);
            doc.setFont(undefined, 'normal');
            doc.text(`Total Years: ${totalYears} | Final Balance: $${Math.round(finalBalance).toLocaleString()}`, 18, yPos + 13);
            doc.text(`Average Strategy Withdrawal: $${Math.round(avgWithdrawal).toLocaleString()}/yr | Average Lump Sum: $${Math.round(avgLumpSum).toLocaleString()}/yr | Average Pension: $${Math.round(avgPension).toLocaleString()}/yr`, 18, yPos + 19);
            doc.text(`Average Super Contributions: $${Math.round(avgContribution).toLocaleString()}/yr | Average Real Return: ${(avgReturn * 100).toFixed(2)}% p.a.`, 18, yPos + 23);
            yPos += 29;

            // Table headers (8 columns — includes lump sum draws)
            doc.setFontSize(8);
            doc.setFont(undefined, 'bold');
            doc.setFillColor(241, 245, 249);
            doc.rect(14, yPos - 3, pageWidth - 28, 7, 'F');
            const colX = [14, 23, 40, 57, 73, 88, 107, 127, 147];
            const printYearTableHeaders = () => {
                doc.text('Year', colX[0], yPos);
                doc.text('Balance', colX[1], yPos);
                doc.text('Withdraw', colX[2], yPos);
                doc.text('Lump sum', colX[3], yPos);
                doc.text('Contrib', colX[4], yPos);
                doc.text('Pension', colX[5], yPos);
                doc.text('Other Inc', colX[6], yPos);
                doc.text('Total Inc', colX[7], yPos);
                doc.text('Return', colX[8], yPos);
            };
            printYearTableHeaders();
            yPos += 5;

            // Draw line under headers
            doc.setDrawColor(148, 163, 184);
            doc.line(14, yPos, pageWidth - 14, yPos);
            yPos += 3;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            historicalResults.yearlyData.forEach((row, idx) => {
                // Check if we need a new page - increased margin to prevent cut-off
                if (yPos > pageHeight - 25) {
                    doc.addPage();
                    yPos = 20;
                    // Re-print headers on new page
                    doc.setFont(undefined, 'bold');
                    doc.setFontSize(8);
                    doc.setFillColor(241, 245, 249);
                    doc.rect(14, yPos - 3, pageWidth - 28, 6, 'F');
                    printYearTableHeaders();
                    yPos += 4;
                    doc.setDrawColor(148, 163, 184);
                    doc.line(14, yPos, pageWidth - 14, yPos);
                    yPos += 3;
                    doc.setFont(undefined, 'normal');
                }

                const year = row.yearIndex || idx + 1;
                const yearLabel = inputs.startYear ? `${inputs.startYear + idx}` : `${year}`;
                
                // Alternate row colors for readability
                if (idx % 2 === 0) {
                    doc.setFillColor(250, 250, 250);
                    doc.rect(14, yPos - 2.5, pageWidth - 28, 3.5, 'F');
                }

                doc.text(yearLabel.toString(), colX[0], yPos);
                doc.text(`$${Math.round(row.balance || 0).toLocaleString()}`, colX[1], yPos);
                doc.text(`$${Math.round(row.withdrawalFromSuper || 0).toLocaleString()}`, colX[2], yPos);
                doc.text(`$${Math.round(row.lumpSumWithdrawal || 0).toLocaleString()}`, colX[3], yPos);
                doc.text(`$${Math.round(row.lumpSumContribution || 0).toLocaleString()}`, colX[4], yPos);
                doc.text(`$${Math.round(row.pension || 0).toLocaleString()}`, colX[5], yPos);
                doc.text(`$${Math.round((row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0)).toLocaleString()}`, colX[6], yPos);
                doc.text(`$${Math.round(row.totalIncome || 0).toLocaleString()}`, colX[7], yPos);
                doc.text(`${((row.realReturn || 0) * 100).toFixed(2)}%`, colX[8], yPos);
                yPos += 5; // Increased spacing to prevent cut-off
                const contributionFlags = (row.contributionFlags || []).map((flag) => flag.message).join(' ');
                if (contributionFlags) {
                    doc.setFontSize(7);
                    doc.setTextColor(180, 83, 9);
                    doc.text(`Contribution flag: ${contributionFlags}`, 18, yPos);
                    doc.setTextColor(0, 0, 0);
                    doc.setFontSize(8);
                    yPos += 5;
                }
            });
            yPos += 5;
        }

        // ===== METHODOLOGY & ASSUMPTIONS SECTION =====
        doc.addPage();
        yPos = 20;
        
        this.addSectionHeader(doc, yPos, 'Methodology & Assumptions');
        yPos += 10;

        // Assumptions detail box
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, pageWidth - 28, 68, 'F');
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Key Assumptions:', 18, yPos + 7);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        
        const assumptions = [
            `Historical Analysis Period: ${inputs.startYear || 1980} to ${(inputs.startYear || 1980) + (inputs.periodLength || 35) - 1}`,
            `Portfolio Allocation: S&P 500 ${inputs.sp500 || 0}%, Australian Shares ${inputs.ausShares || 0}%, International Shares ${inputs.intlShares || 0}%, Bonds ${inputs.bonds || 0}%, Cash ${inputs.cash || 0}%`,
            `Fees: Admin $${inputs.adminFees || 0} + ${inputs.adminFeePercentage || 0}%, Investment ${inputs.investmentFees || 0}%, Transaction ${inputs.transactionCosts || 0.08}%`,
            `Age Pension: ${inputs.includePension ? 'Included' : 'Not included'} | Homeowner: ${inputs.isHomeowner ? 'Yes' : 'No'}`,
            `Withdrawal Strategy: ${this.getStrategyName(withdrawalStrategy)}`,
            `Monte Carlo Simulations: ${monteCarloResults?.simulationCount || 1000} simulations with random market returns`
        ];
        
        assumptions.forEach((assumption, idx) => {
            doc.text(`• ${assumption}`, 18, yPos + 14 + (idx * 9));
        });
        yPos += 73;

        // Methodology explanation
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Calculation Methodology:', 14, yPos);
        yPos += 7;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        
        const methodology = [
            'Historical Data: Uses comprehensive market returns spanning 1928-2025 (98 years) to model realistic retirement scenarios across all economic cycles.',
            'Historical Analysis: Tests this scenario against actual market returns from the selected start year through the retirement period.',
            'Monte Carlo Simulation: Generates 1,000 random market scenarios using historical return distributions and volatility to illustrate a range of possible outcomes.',
            'Rolling Period Analysis: Tests this scenario across all possible historical start dates to identify worst, median, and best case historical outcomes.',
            'Withdrawal Strategy: Applies the selected strategy (Fixed, Dynamic SWR, Vanguard Guardrails, or Floor & Ceiling) to determine annual withdrawals in each scenario.',
            'Age Pension: Estimated using Centrelink rates and asset/income tests applicable during each year of the scenario.',
            'Tax: Withdrawals and income are estimated using Australian superannuation tax rules for accumulation and pension phases.'
        ];
        
        methodology.forEach((method, idx) => {
            if (yPos > pageHeight - 25) {
                doc.addPage();
                yPos = 20;
            }
            // Properly wrap long methodology text
            const wrappedText = doc.splitTextToSize(`${idx + 1}. ${method}`, pageWidth - 36);
            wrappedText.forEach((line) => {
                doc.text(line, 18, yPos);
                yPos += 6;
            });
            yPos += 2; // Small gap between methods
        });
        yPos += 6;

        // ===== DETAILED MONTE CARLO ANALYSIS =====
        if (monteCarloResults && monteCarloResults.successRate !== undefined) {
            if (yPos > pageHeight - 50) {
                doc.addPage();
                yPos = 20;
            }
            
            this.addSectionHeader(doc, yPos, 'Monte Carlo Simulation - Detailed Results');
            yPos += 10;

            // Monte Carlo statistics box
            doc.setFillColor(240, 255, 240);
            doc.rect(14, yPos, pageWidth - 28, 52, 'F');
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Simulation Results:', 18, yPos + 7);
            doc.setFont(undefined, 'normal');
            doc.setFontSize(10);
            
            const mcStats = [
                `Success Rate: ${monteCarloResults.successRate}% of simulations ended with positive balance`,
                `10th Percentile Final Balance: $${Math.round(monteCarloResults.percentile10 || 0).toLocaleString()} (worst 10% of outcomes)`,
                `50th Percentile (Median): $${Math.round(monteCarloResults.medianFinalBalance || 0).toLocaleString()} (typical outcome)`,
                `90th Percentile Final Balance: $${Math.round(monteCarloResults.percentile90 || 0).toLocaleString()} (best 10% of outcomes)`,
                `Target Income Tested: $${(inputs.targetIncome || monteCarloResults.targetIncome || 0).toLocaleString()}/year`
            ];
            
            mcStats.forEach((stat, idx) => {
                doc.text(`• ${stat}`, 18, yPos + 14 + (idx * 8));
            });
            yPos += 57;

            // Monte Carlo interpretation
            this.addInfoBox(doc, yPos, 
                `The Monte Carlo simulation tests this scenario against ${monteCarloResults.simulationCount || 1000} randomly generated market scenarios. ` +
                `An ${monteCarloResults.successRate}% success rate means this scenario would have succeeded in ${monteCarloResults.successRate} out of 100 historical-like scenarios. ` +
                `The percentile values show the range of possible outcomes in these illustrative scenarios - the 10th percentile represents a conservative estimate, while the 90th percentile shows optimistic outcomes.`,
                'info'
            );
            yPos += 25;
        }

        // ===== ROLLING PERIOD DETAILED ANALYSIS =====
        if (historicalResults.rollingPeriodStats) {
            if (yPos > pageHeight - 60) {
                doc.addPage();
                yPos = 20;
            }
            
            this.addSectionHeader(doc, yPos, 'Rolling Period Analysis - Detailed Breakdown');
            yPos += 10;

            // Rolling period explanation
            this.addInfoBox(doc, yPos,
                `Rolling Period Analysis tests this scenario across ${historicalResults.rollingPeriodStats.count} different ${inputs.periodLength || 35}-year historical periods. ` +
                `This illustrates how this scenario would have performed under different historical market conditions - from the worst bear markets to the best bull markets. ` +
                `The median represents the "typical" historical outcome in this analysis, while worst/best show the extremes.`,
                'info'
            );
            yPos += 25;

            // Rolling period detailed stats
            doc.setFillColor(255, 250, 240);
            doc.rect(14, yPos, pageWidth - 28, 40, 'F');
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Rolling Period Statistics:', 18, yPos + 7);
            doc.setFont(undefined, 'normal');
            doc.setFontSize(10);
            
            doc.text(`• Total Periods Analyzed: ${historicalResults.rollingPeriodStats.count}`, 18, yPos + 14);
            doc.text(`• Worst Period Income: $${historicalResults.rollingPeriodStats.worst.toLocaleString()}/year`, 18, yPos + 22);
            doc.text(`• Median Period Income: $${historicalResults.rollingPeriodStats.median.toLocaleString()}/year`, 18, yPos + 30);
            doc.text(`• Best Period Income: $${historicalResults.rollingPeriodStats.best.toLocaleString()}/year`, 18, yPos + 38);
            yPos += 45;
        }

        // ===== VISUAL ANALYSIS SECTION =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'Visual Analysis');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        doc.text('Note: Charts are captured from the calculator display at the time of export.', 14, yPos);
        doc.text('For interactive charts and detailed analysis, please use the online calculator.', 14, yPos + 5);
        doc.setTextColor(0);
        yPos += 14;

        // ===== CHARTS - Two charts per page, large size =====
        const imgWidth = pageWidth - 28;
        const usableHeight = pageHeight - 30;
        const chartHeightLarge = (usableHeight - 25) / 2; // Two charts per page, ~45% each
        
        // Chart 1 & 2 on same page
        if ((chartImages.balanceChart && chartImages.balanceChart.data) || (chartImages.incomeChart && chartImages.incomeChart.data)) {
            doc.addPage();
            yPos = 15;
            
            // Chart 1: Balance Projection
            if (chartImages.balanceChart && chartImages.balanceChart.data) {
                try {
                    doc.setFontSize(10);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('1. Super Balance Projection Over Time', 14, yPos);
                    yPos += 4;
                    
                    const chartData = typeof chartImages.balanceChart === 'string' ? chartImages.balanceChart : (chartImages.balanceChart?.data || null);
                    
                    if (chartData) {
                        doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, chartHeightLarge - 10);
                        yPos += chartHeightLarge - 5;
                    } else {
                        doc.setFillColor(245, 247, 250);
                        doc.rect(14, yPos, imgWidth, chartHeightLarge - 10, 'F');
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeightLarge - 10) / 2, { align: 'center' });
                        doc.setTextColor(0);
                        yPos += chartHeightLarge - 5;
                    }
                } catch (err) {
                    console.warn('Could not add balance chart to PDF:', err);
                    yPos += chartHeightLarge - 5;
                }
            }

            // Chart 2: Income Sources
            if (chartImages.incomeChart && chartImages.incomeChart.data) {
                try {
                    doc.setFontSize(10);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('2. Annual Income by Source', 14, yPos);
                    yPos += 4;
                    
                    const chartData2 = typeof chartImages.incomeChart === 'string' ? chartImages.incomeChart : (chartImages.incomeChart?.data || null);
                    
                    if (chartData2) {
                        doc.addImage(chartData2, 'PNG', 14, yPos, imgWidth, chartHeightLarge - 10);
                        yPos += chartHeightLarge - 5;
                    } else {
                        doc.setFillColor(245, 247, 250);
                        doc.rect(14, yPos, imgWidth, chartHeightLarge - 10, 'F');
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeightLarge - 10) / 2, { align: 'center' });
                        doc.setTextColor(0);
                        yPos += chartHeightLarge - 5;
                    }
                } catch (err) {
                    console.warn('Could not add income chart to PDF:', err);
                }
            }
        }

        // Chart 3: Rolling Period Analysis - on its own page
        if (chartImages.rollingChart && (typeof chartImages.rollingChart === 'string' || chartImages.rollingChart.data)) {
            doc.addPage();
            yPos = 15;
            
            try {
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('3. Rolling Period Analysis - Historical Testing', 14, yPos);
                yPos += 4;
                
                const chartData3 = typeof chartImages.rollingChart === 'string' ? chartImages.rollingChart : (chartImages.rollingChart?.data || null);
                
                if (chartData3) {
                    doc.addImage(chartData3, 'PNG', 14, yPos, imgWidth, chartHeightLarge - 10);
                    yPos += chartHeightLarge - 5;
                } else {
                    doc.setFillColor(245, 247, 250);
                    doc.rect(14, yPos, imgWidth, chartHeightLarge - 10, 'F');
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                    doc.text('Chart unavailable', pageWidth / 2, yPos + (chartHeightLarge - 10) / 2, { align: 'center' });
                    doc.setTextColor(0);
                    yPos += chartHeightLarge - 5;
                }
                
                // Add interpretation below rolling period chart
                if (historicalResults.rollingPeriodStats) {
                    doc.setFillColor(255, 250, 240);
                    doc.rect(14, yPos, pageWidth - 28, 20, 'F');
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'italic');
                    doc.setTextColor(92, 92, 92);
                    doc.text(`Historical Analysis: Tested across ${historicalResults.rollingPeriodStats.count} periods. ` +
                        `Worst: $${historicalResults.rollingPeriodStats.worst.toLocaleString()}/yr, ` +
                        `Median: $${historicalResults.rollingPeriodStats.median.toLocaleString()}/yr, ` +
                        `Best: $${historicalResults.rollingPeriodStats.best.toLocaleString()}/yr.`, 
                        18, yPos + 8, { maxWidth: pageWidth - 36 });
                    doc.setTextColor(0);
                    doc.setFont(undefined, 'normal');
                    yPos += 25;
                }
            } catch (err) {
                console.warn('Could not add rolling chart to PDF:', err);
            }
        }

        // If no charts were captured at all, show a note
        const hasAnyChart = (chartImages.balanceChart && (typeof chartImages.balanceChart === 'string' || chartImages.balanceChart.data)) || 
                           (chartImages.incomeChart && (typeof chartImages.incomeChart === 'string' || chartImages.incomeChart.data)) || 
                           (chartImages.rollingChart && (typeof chartImages.rollingChart === 'string' || chartImages.rollingChart.data));
        
        if (!hasAnyChart) {
            if (yPos > pageHeight - 30) {
                doc.addPage();
                yPos = 20;
            }
            doc.setFillColor(255, 255, 220);
            doc.rect(14, yPos, pageWidth - 28, 15, 'F');
            doc.setFontSize(8);
            doc.text('Charts could not be captured. For interactive charts, please use the online calculator.', 18, yPos + 8);
            yPos += 20;
        }

        // ===== WHAT-IF SCENARIOS SECTION =====
        if (yPos > pageHeight - 80) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'What-If Scenario Testing');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const whatifIntro = `The Advanced Calculator includes three "What-If" scenario tests that allow you to explore how changes to key assumptions affect your retirement outcomes. These scenarios use the SAME calculation engine and logic as the main analysis (single source of truth), changing only the specific variable being tested.`;
        const wrappedWhatifIntro = doc.splitTextToSize(whatifIntro, pageWidth - 28);
        doc.text(wrappedWhatifIntro, 14, yPos);
        yPos += wrappedWhatifIntro.length * 5 + 8;

        // What-If scenario descriptions
        const whatifScenarios = [
            {
                name: 'Live to Age',
                desc: 'Tests if your portfolio survives for a different retirement period. If it fails, calculates the maximum sustainable income for that period. Useful for testing longevity risk.'
            },
            {
                name: 'Annual Fees',
                desc: 'Tests the impact of changing your annual fees (as a percentage change). Shows how fee increases or decreases affect your sustainable income and final balance.'
            },
            {
                name: 'Sequence Risk',
                desc: 'Tests the impact of poor market returns in early retirement years (e.g., -20% for first 3 years). Demonstrates the critical importance of early retirement market performance.'
            }
        ];

        whatifScenarios.forEach((scenario, idx) => {
            if (yPos > pageHeight - 25) {
                doc.addPage();
                yPos = 20;
            }
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`${idx + 1}. ${scenario.name}`, 18, yPos);
            yPos += 5;
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            const wrappedDesc = doc.splitTextToSize(scenario.desc, pageWidth - 36);
            doc.text(wrappedDesc, 22, yPos);
            yPos += wrappedDesc.length * 4.5 + 4;
        });

        yPos += 3;

        // Corporate Disclaimer - STRONG legal protection (auto-creates page if needed)
        this.addCorporateDisclaimer(doc, yPos);

        // Save or return base64 for email attachment
        if (arguments[5] === true) {
            return doc.output('datauristring').replace(/^data:application\/pdf(?:;[^,]*)?;base64,/, '');
        }
        doc.save(`SuperCalcPro-Advanced-Analysis-${new Date().toISOString().split('T')[0]}.pdf`);
    }

    /**
     * Generate PDF for Accumulation Calculator - COMPREHENSIVE PROFESSIONAL VERSION
     */
    static exportAccumulationCalculator(inputs, results, chartImages = {}) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        
        // Professional header with logo
        let yPos = this.addProfessionalHeader(doc, 'Super Accumulation Projection', 'Growth Phase Calculator');

        // ===== EXECUTIVE SUMMARY =====
        // FIX: Accumulation returns finalBalances.total, not finalBalance
        const finalBalance = results.finalBalances?.total || results.finalBalances?.median || results.finalBalance || 0;
        const totalContributions = results.totalContributions || 0;
        const startBalance = (inputs.currentSuper || 0) + (inputs.partnerCurrentSuper || 0);
        const investmentGrowth = finalBalance - startBalance - totalContributions;
        const yearsUntilRetirement = (inputs.retirementAge || 67) - (inputs.currentAge || 30);
        
        // Calculate real (inflation-adjusted) final balance
        const inflationRate = (inputs.assumedInflation || 2.5) / 100;
        const finalBalanceReal = finalBalance / Math.pow(1 + inflationRate, yearsUntilRetirement);

        const keyMetrics = [
            {
                label: 'Projected Super at Retirement (Nominal)',
                value: `$${Math.round(finalBalance).toLocaleString()}`
            },
            {
                label: 'Projected Super (Today\'s Dollars)',
                value: `$${Math.round(finalBalanceReal).toLocaleString()}`
            },
            {
                label: 'Total Contributions',
                value: `$${Math.round(totalContributions).toLocaleString()}`
            },
            {
                label: 'Investment Growth',
                value: `$${Math.round(investmentGrowth).toLocaleString()}`
            }
        ];

        yPos = this.addExecutiveSummary(doc, yPos, keyMetrics);
        yPos += 5;

        yPos = this.addCalculationTransparency(
            doc,
            yPos,
            'accumulation',
            inputs,
            results?.yearlyData || results?.combinedData || results?.chartData || []
        );

        // Additional context
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, pageWidth - 28, 20, 'F');
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Years Until Retirement: ${yearsUntilRetirement} | Current Age: ${inputs.currentAge || 30} | Retirement Age: ${inputs.retirementAge || 67}`, 18, yPos + 6);
        
        // Calculate percentages safely (avoid division by zero)
        const growthPercent = finalBalance > 0 ? ((investmentGrowth/finalBalance)*100).toFixed(1) : 0;
        const contribPercent = finalBalance > 0 ? (((totalContributions + startBalance)/finalBalance)*100).toFixed(1) : 0;
        doc.text(`This scenario shows ${growthPercent}% of final balance from investment growth, ${contribPercent}% from contributions + starting balance`, 18, yPos + 11);
        
        doc.setFont(undefined, 'italic');
        doc.setFontSize(9);
        doc.setTextColor(75, 85, 99);
        doc.text(`Real (today's dollars) accounts for ${((inflationRate * 100).toFixed(1))}% annual inflation over ${yearsUntilRetirement} years. Purchasing power is ${((finalBalanceReal/finalBalance)*100).toFixed(0)}% of nominal value.`, 18, yPos + 16);
        doc.setTextColor(0);
        doc.setFont(undefined, 'normal');
        yPos += 25;

        // Your Details
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Your Details', 14, yPos);
        yPos += 7;

        const isCouple = inputs.accumulationRelationshipStatus === 'couple';
        const detailsPairs = [
            ['Current Age', inputs.currentAge],
            ['Retirement Age', inputs.retirementAge],
            ['Years to Retirement', inputs.retirementAge - inputs.currentAge],
            ['Current Super', `$${inputs.currentSuper?.toLocaleString()}`],
            ['Current Wage', `$${inputs.currentWage?.toLocaleString()}`],
            ...(isCouple ? [
                ['Partner Age', inputs.partnerCurrentAge],
                ['Partner Super', `$${inputs.partnerCurrentSuper?.toLocaleString()}`],
                ['Partner Wage', `$${inputs.partnerCurrentWage?.toLocaleString()}`]
            ] : [])
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, detailsPairs);

        // Contributions
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Contribution Strategy', 14, yPos);
        yPos += 7;

        const contribPairs = [
            ['Concessional (p.a.)', `$${inputs.additionalContributions?.toLocaleString() || 0}`],
            ['Non-Concessional (p.a.)', `$${inputs.nonConcessionalContributions?.toLocaleString() || 0}`],
            ['Bring-Forward', inputs.enableBringForward ? 'Enabled' : 'Not Used'],
            ['Gov Co-Contribution', inputs.enableCoContribution ? 'Modelled if eligible' : 'Off'],
            ['LISTO', inputs.enableLISTO !== false ? 'Modelled if eligible (wage as ATI proxy)' : 'Off'],
            ...(inputs.downsizerContribution > 0 ? [
                ['Downsizer', `$${inputs.downsizerContribution.toLocaleString()} (Yr ${inputs.downsizerYear})`]
            ] : [])
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, contribPairs);

        // TTR Strategy
        if (inputs.enableTTR) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('TTR Strategy', 14, yPos);
            yPos += 6;

            const ttrPairs = [
                ['Start Age', inputs.ttrStartAge],
                ['Drawdown Rate', `${inputs.ttrDrawdownRate}%`]
            ];

            yPos = this.drawKeyValuePairs(doc, yPos, ttrPairs, 50);
        }

        // ===== ALTERNATIVE ASSETS SECTION =====
        const hasAlternativeAssets = (inputs.residentialProperties && inputs.residentialProperties.length > 0) ||
                                     (inputs.commercialProperties && inputs.commercialProperties.length > 0) ||
                                     (inputs.alternativeAssets && inputs.alternativeAssets.length > 0) ||
                                     (inputs.collectiblesAssets && inputs.collectiblesAssets.length > 0);

        if (hasAlternativeAssets) {
            // Check if we need a new page
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFillColor(248, 250, 252);
            doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Alternative Assets Portfolio', 18, yPos + 6);
            doc.setTextColor(0);
            
            // Add SMSF transfer indicator if applicable
            if (inputs.transferFromSMSF) {
                doc.setFontSize(8);
                doc.setTextColor(59, 130, 246);
                doc.text('(From SMSF Transfer)', 100, yPos + 6);
                doc.setTextColor(0);
            }
            
            yPos += 12;

            // Residential Properties
            if (inputs.residentialProperties && inputs.residentialProperties.length > 0) {
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Residential Properties', 14, yPos);
                yPos += 6;

                inputs.residentialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.name || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(prop.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Rental Income: $${(prop.rentalIncome || 0).toLocaleString()}/yr`, 90, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}%`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Commercial Properties
            if (inputs.commercialProperties && inputs.commercialProperties.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Commercial Properties', 14, yPos);
                yPos += 6;

                inputs.commercialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.name || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(prop.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Rental Income: $${(prop.rentalIncome || 0).toLocaleString()}/yr`, 90, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}%`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Infrastructure & Alternative Assets
            if (inputs.alternativeAssets && inputs.alternativeAssets.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Infrastructure & Alternative Investments', 14, yPos);
                yPos += 6;

                inputs.alternativeAssets.forEach((asset, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${asset.name || `Asset ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(asset.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Expected Return: ${(asset.returnRate || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            // Collectibles (Non-Productive Assets)
            if (inputs.collectiblesAssets && inputs.collectiblesAssets.length > 0) {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text('Collectibles (Non-Productive Assets)', 14, yPos);
                yPos += 6;

                doc.setFontSize(8);
                doc.setFont(undefined, 'italic');
                doc.setTextColor(100);
                doc.text('Note: Collectibles do not generate income but are tracked for total wealth.', 14, yPos);
                doc.setTextColor(0);
                yPos += 6;

                inputs.collectiblesAssets.forEach((asset, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${asset.name || `Collectible ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Value: $${(asset.value || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Capital Growth: ${(asset.capitalGrowth || 0).toFixed(1)}% p.a.`, 90, yPos);
                    yPos += 6;
                });
            }

            yPos += 4;
        }

        // Results
        if (results && results.finalBalances) {
            if (yPos > pageHeight - 60) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Projection Results', 14, yPos);
            yPos += 7;

            // Use finalBalance and finalBalanceReal already calculated above (consistent)
            const resultsPairs = [
                ['Starting Balance', `$${Math.round(startBalance).toLocaleString()}`],
                ['Final Balance (Nominal)', `$${Math.round(finalBalance).toLocaleString()}`],
                ['Final Balance (Real)', `$${Math.round(finalBalanceReal).toLocaleString()}`],
                ['Total Contributions', `$${Math.round(totalContributions).toLocaleString()}`],
                ['Investment Growth', `$${Math.round(investmentGrowth).toLocaleString()}`],
                ['Net Growth', `$${Math.round(finalBalance - startBalance).toLocaleString()}`]
            ];

            yPos = this.drawKeyValuePairs(doc, yPos, resultsPairs);
            
            // Explanation note
            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(100);
            doc.text('Note: Real values are adjusted for inflation to show purchasing power in today\'s dollars.', 14, yPos + 3);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            yPos += 8;

            // ===== HISTORICAL PERCENTILES (if in historical mode) =====
            if (results.finalBalances.worstCase !== undefined && results.finalBalances.bestCase !== undefined) {
                if (yPos > pageHeight - 50) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFillColor(255, 250, 240);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('Historical Scenario Analysis', 18, yPos + 6);
                doc.setTextColor(0);
                yPos += 12;

                doc.setFontSize(9);
                doc.setFont(undefined, 'normal');
                doc.text('Based on historical market returns, here are the range of possible outcomes:', 14, yPos);
                yPos += 8;

                // Three boxes for percentiles
                const boxWidth = (pageWidth - 28 - 8) / 3;
                const boxHeight = 25;
                const startX = 14;

                // Worst Case (10th percentile)
                doc.setFillColor(254, 226, 226);
                doc.rect(startX, yPos, boxWidth, boxHeight, 'F');
                doc.setDrawColor(220, 38, 38);
                doc.rect(startX, yPos, boxWidth, boxHeight, 'S');
                doc.setFontSize(8);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(153, 27, 27);
                doc.text('Worst (10th %ile)', startX + boxWidth/2, yPos + 8, { align: 'center' });
                doc.setFontSize(14);
                doc.text(`$${Math.round(results.finalBalances.worstCase).toLocaleString()}`, startX + boxWidth/2, yPos + 16, { align: 'center' });
                doc.setFontSize(7);
                doc.setFont(undefined, 'normal');
                doc.text('Poor market conditions', startX + boxWidth/2, yPos + 21, { align: 'center' });

                // Median (50th percentile)
                doc.setFillColor(243, 244, 246);
                doc.rect(startX + boxWidth + 4, yPos, boxWidth, boxHeight, 'F');
                doc.setDrawColor(75, 85, 99);
                doc.rect(startX + boxWidth + 4, yPos, boxWidth, boxHeight, 'S');
                doc.setFont(undefined, 'bold');
                doc.setTextColor(22, 163, 74);
                doc.setFontSize(8);
                doc.text('Median (50th %ile)', startX + boxWidth + 4 + boxWidth/2, yPos + 8, { align: 'center' });
                doc.setFontSize(14);
                doc.text(`$${Math.round(results.finalBalances.median).toLocaleString()}`, startX + boxWidth + 4 + boxWidth/2, yPos + 16, { align: 'center' });
                doc.setFontSize(7);
                doc.setFont(undefined, 'normal');
                doc.text('Typical outcome', startX + boxWidth + 4 + boxWidth/2, yPos + 21, { align: 'center' });

                // Best Case (90th percentile)
                doc.setFillColor(219, 234, 254);
                doc.rect(startX + (boxWidth + 4) * 2, yPos, boxWidth, boxHeight, 'F');
                doc.setDrawColor(37, 99, 235);
                doc.rect(startX + (boxWidth + 4) * 2, yPos, boxWidth, boxHeight, 'S');
                doc.setFont(undefined, 'bold');
                doc.setTextColor(29, 78, 216);
                doc.setFontSize(8);
                doc.text('Best (90th %ile)', startX + (boxWidth + 4) * 2 + boxWidth/2, yPos + 8, { align: 'center' });
                doc.setFontSize(14);
                doc.text(`$${Math.round(results.finalBalances.bestCase).toLocaleString()}`, startX + (boxWidth + 4) * 2 + boxWidth/2, yPos + 16, { align: 'center' });
                doc.setFontSize(7);
                doc.setFont(undefined, 'normal');
                doc.text('Strong market conditions', startX + (boxWidth + 4) * 2 + boxWidth/2, yPos + 21, { align: 'center' });

                doc.setTextColor(0);
                yPos += boxHeight + 10;

                doc.setFontSize(8);
                doc.setFont(undefined, 'italic');
                doc.setTextColor(100);
                doc.text('These percentiles show the range of outcomes based on historical market data. 10% of scenarios were worse than the worst case, 50% were below median, and 10% were better than best case.', 14, yPos);
                doc.setTextColor(0);
                yPos += 10;
            }

            // ===== SPOUSE CONTRIBUTION OFFSET =====
            if (results.totalSpouseContributionOffset && results.totalSpouseContributionOffset > 0) {
                if (yPos > pageHeight - 30) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFillColor(243, 232, 255);
                doc.rect(14, yPos, pageWidth - 28, 22, 'F');
                doc.setDrawColor(168, 85, 247);
                doc.rect(14, yPos, pageWidth - 28, 22, 'S');
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(109, 40, 217);
                doc.text('Estimated Total Spouse Contribution Tax Offset', 18, yPos + 8);
                doc.setFontSize(16);
                doc.text(`$${Math.round(results.totalSpouseContributionOffset).toLocaleString()}`, 18, yPos + 16);
                doc.setTextColor(0);
                doc.setFontSize(8);
                doc.setFont(undefined, 'normal');
                doc.text('Tax offset for contributing to lower-income spouse\'s super (accumulated over projection period)', 18, yPos + 20);
                yPos += 26;
            }

            // ===== GROWTH VS CONTRIBUTIONS ANALYSIS =====
            if (yPos > pageHeight - 70) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFillColor(240, 253, 244);
            doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Growth vs Contributions Analysis', 18, yPos + 6);
            doc.setTextColor(0);
            yPos += 12;

            // Calculate percentages
            const growthPercent = finalBalance > 0 ? ((investmentGrowth / finalBalance) * 100) : 0;
            const contribPercent = finalBalance > 0 ? (((totalContributions + startBalance) / finalBalance) * 100) : 0;

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.text(`Your final balance of $${Math.round(finalBalance).toLocaleString()} comes from two sources:`, 14, yPos);
            yPos += 8;

            // Two boxes side by side
            const analysisBoxWidth = (pageWidth - 28 - 4) / 2;
            const analysisBoxHeight = 35;

            // Investment Growth box
            doc.setFillColor(220, 252, 231);
            doc.rect(14, yPos, analysisBoxWidth, analysisBoxHeight, 'F');
            doc.setDrawColor(34, 197, 94);
            doc.rect(14, yPos, analysisBoxWidth, analysisBoxHeight, 'S');
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(22, 101, 52);
            doc.text('Investment Growth', 14 + analysisBoxWidth/2, yPos + 7, { align: 'center' });
            doc.setFontSize(16);
            doc.text(`$${Math.round(investmentGrowth).toLocaleString()}`, 14 + analysisBoxWidth/2, yPos + 17, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`${growthPercent.toFixed(1)}% of final balance`, 14 + analysisBoxWidth/2, yPos + 25, { align: 'center' });
            doc.setFontSize(7);
            doc.setFont(undefined, 'italic');
            doc.text('Compound returns on investments', 14 + analysisBoxWidth/2, yPos + 31, { align: 'center' });

            // Contributions box
            doc.setFillColor(219, 234, 254);
            doc.rect(14 + analysisBoxWidth + 4, yPos, analysisBoxWidth, analysisBoxHeight, 'F');
            doc.setDrawColor(59, 130, 246);
            doc.rect(14 + analysisBoxWidth + 4, yPos, analysisBoxWidth, analysisBoxHeight, 'S');
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 64, 175);
            doc.text('Contributions + Starting Balance', 14 + analysisBoxWidth + 4 + analysisBoxWidth/2, yPos + 7, { align: 'center' });
            doc.setFontSize(16);
            doc.text(`$${Math.round(totalContributions + startBalance).toLocaleString()}`, 14 + analysisBoxWidth + 4 + analysisBoxWidth/2, yPos + 17, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`${contribPercent.toFixed(1)}% of final balance`, 14 + analysisBoxWidth + 4 + analysisBoxWidth/2, yPos + 25, { align: 'center' });
            doc.setFontSize(7);
            doc.setFont(undefined, 'italic');
            doc.text('Money you put in', 14 + analysisBoxWidth + 4 + analysisBoxWidth/2, yPos + 31, { align: 'center' });

            doc.setTextColor(0);
            yPos += analysisBoxHeight + 10;

            // Key insight
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(59, 130, 246);
            doc.text('Key Insight:', 14, yPos);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0);
            const insightText = growthPercent > 50 
                ? `Investment growth (${growthPercent.toFixed(0)}%) is the primary driver of your retirement savings. This demonstrates the power of compound returns over ${yearsUntilRetirement} years.`
                : `Contributions (${contribPercent.toFixed(0)}%) are the primary driver of your retirement savings. Consider extending your investment timeframe or adjusting asset allocation for more growth potential.`;
            const wrappedInsight = doc.splitTextToSize(insightText, pageWidth - 28);
            doc.text(wrappedInsight, 14, yPos + 5);
            yPos += wrappedInsight.length * 4 + 10;

            // ===== NON-PRODUCTIVE ASSETS =====
            if (results.finalBalances.nonProductiveAssets && results.finalBalances.nonProductiveAssets.total > 0) {
                if (yPos > pageHeight - 80) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFillColor(254, 243, 199);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('Non-Productive Assets at Retirement', 18, yPos + 6);
                doc.setTextColor(0);
                yPos += 12;

                doc.setFontSize(8);
                doc.setFont(undefined, 'italic');
                doc.setTextColor(146, 64, 14);
                doc.text('These assets are not part of your super portfolio but contribute to your total wealth. They don\'t generate regular income like shares/bonds, but may provide rental income or capital growth.', 14, yPos);
                doc.setTextColor(0);
                yPos += 10;

                const npa = results.finalBalances.nonProductiveAssets;
                const nonProdAssets = [];
                if (npa.residentialProperty > 0) nonProdAssets.push(['Residential Property', npa.residentialProperty]);
                if (npa.commercialProperty > 0) nonProdAssets.push(['Commercial Property', npa.commercialProperty]);
                if (npa.collectibles > 0) nonProdAssets.push(['Collectibles', npa.collectibles]);
                if (npa.otherAlternatives > 0) nonProdAssets.push(['Other Alternatives', npa.otherAlternatives]);

                // Display in grid
                const cols = 2;
                const rows = Math.ceil(nonProdAssets.length / cols);
                const assetBoxWidth = (pageWidth - 28 - 4) / cols;
                const assetBoxHeight = 18;

                nonProdAssets.forEach((asset, index) => {
                    const col = index % cols;
                    const row = Math.floor(index / cols);
                    const x = 14 + col * (assetBoxWidth + 4);
                    const y = yPos + row * (assetBoxHeight + 4);

                    doc.setFillColor(255, 255, 255);
                    doc.rect(x, y, assetBoxWidth, assetBoxHeight, 'F');
                    doc.setDrawColor(251, 191, 36);
                    doc.rect(x, y, assetBoxWidth, assetBoxHeight, 'S');
                    
                    doc.setFontSize(8);
                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(120, 113, 108);
                    doc.text(asset[0], x + 3, y + 7);
                    
                    doc.setFontSize(12);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(0);
                    doc.text(`$${Math.round(asset[1]).toLocaleString()}`, x + 3, y + 14);
                });

                yPos += rows * (assetBoxHeight + 4) + 4;

                // Total non-productive
                doc.setFillColor(254, 215, 170);
                doc.rect(14, yPos, pageWidth - 28, 16, 'F');
                doc.setDrawColor(234, 88, 12);
                doc.rect(14, yPos, pageWidth - 28, 16, 'S');
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(124, 45, 18);
                doc.text('Total Non-Productive Assets:', 18, yPos + 7);
                doc.setFontSize(14);
                doc.text(`$${Math.round(npa.total).toLocaleString()}`, 18, yPos + 13);
                doc.setTextColor(0);
                yPos += 20;
            }

            // ===== COMBINED WEALTH SUMMARY =====
            if (yPos > pageHeight - 40) {
                doc.addPage();
                yPos = 20;
            }

            const combinedWealth = results.finalBalances.combinedWealth || finalBalance;
            doc.setFillColor(236, 253, 245);
            doc.rect(14, yPos, pageWidth - 28, 30, 'F');
            doc.setDrawColor(34, 197, 94);
            doc.setLineWidth(2);
            doc.rect(14, yPos, pageWidth - 28, 30, 'S');
            doc.setLineWidth(0.3);
            
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(22, 101, 52);
            doc.text('Total Wealth at Retirement', 18, yPos + 8);
            
            doc.setFontSize(20);
            doc.text(`$${Math.round(combinedWealth).toLocaleString()}`, 18, yPos + 18);
            
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            const wealthDesc = results.finalBalances.nonProductiveAssets && results.finalBalances.nonProductiveAssets.total > 0
                ? 'Productive assets (super portfolio) + Non-productive assets (property, collectibles, etc.)'
                : 'Your super portfolio at retirement (productive assets only)';
            doc.text(wealthDesc, 18, yPos + 25);
            doc.setTextColor(0);
            yPos += 35;
        }

        // ===== BALANCE PROJECTION CHART =====
        if (chartImages && chartImages.balanceChart) {
            if (yPos > pageHeight - 90) {
                doc.addPage();
                yPos = 20;
            }

            this.addSectionHeader(doc, yPos, 'Balance Projection Chart');
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            const chartDescription = `This chart illustrates the projected growth of superannuation over ${yearsUntilRetirement} years, from age ${inputs.currentAge} to ${inputs.retirementAge}. The projection accounts for contributions, investment returns (after fees), and all applicable regulations.`;
            const wrappedDescription = doc.splitTextToSize(chartDescription, pageWidth - 28);
            doc.text(wrappedDescription, 14, yPos);
            yPos += wrappedDescription.length * 5 + 5;

            try {
                const chartWidth = pageWidth - 28;
                const chartHeight = 80; // Larger chart
                doc.addImage(chartImages.balanceChart, 'PNG', 14, yPos, chartWidth, chartHeight);
                yPos += chartHeight + 10;
            } catch (err) {
                console.error('Failed to add chart to PDF:', err);
            }
        }

        // ===== CONTRIBUTION BREAKDOWN ANALYSIS =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'Contribution Breakdown Analysis');
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('This section shows the breakdown of contributions over your accumulation period:', 14, yPos);
        yPos += 8;

        // Calculate contribution components
        const userSalary = inputs.currentSalary || 0;
        const partnerSalary = isCouple ? (inputs.partnerCurrentSalary || 0) : 0;
        const sgRate = inputs.sgRate || 11.5;
        const userSGAnnual = userSalary * (sgRate / 100);
        const partnerSGAnnual = partnerSalary * (sgRate / 100);
        
        const userVoluntaryConcessional = inputs.voluntaryConcessional || 0;
        const partnerVoluntaryConcessional = isCouple ? (inputs.partnerVoluntaryConcessional || 0) : 0;
        
        const userNonConcessional = inputs.nonConcessional || 0;
        const partnerNonConcessional = isCouple ? (inputs.partnerNonConcessional || 0) : 0;
        
        const userSpouseContrib = inputs.spouseContribution || 0;
        const partnerSpouseContrib = isCouple ? (inputs.partnerSpouseContribution || 0) : 0;

        // Annual totals
        const userAnnualTotal = userSGAnnual + userVoluntaryConcessional + userNonConcessional + userSpouseContrib;
        const partnerAnnualTotal = partnerSGAnnual + partnerVoluntaryConcessional + partnerNonConcessional + partnerSpouseContrib;
        const combinedAnnualTotal = userAnnualTotal + partnerAnnualTotal;

        // Over projection period
        const userTotalOverPeriod = userAnnualTotal * yearsUntilRetirement;
        const partnerTotalOverPeriod = partnerAnnualTotal * yearsUntilRetirement;
        const combinedTotalOverPeriod = combinedAnnualTotal * yearsUntilRetirement;

        // Create contribution breakdown table
        const contribHeaders = isCouple 
            ? ['Contribution Type', 'Your Annual', 'Partner Annual', 'Combined Annual', `Total (${yearsUntilRetirement}y)`]
            : ['Contribution Type', 'Annual Amount', `Total (${yearsUntilRetirement}y)`];

        const contribData = [];
        
        if (isCouple) {
            contribData.push([
                'Superannuation Guarantee',
                `$${Math.round(userSGAnnual).toLocaleString()}`,
                `$${Math.round(partnerSGAnnual).toLocaleString()}`,
                `$${Math.round(userSGAnnual + partnerSGAnnual).toLocaleString()}`,
                `$${Math.round((userSGAnnual + partnerSGAnnual) * yearsUntilRetirement).toLocaleString()}`
            ]);
            if (userVoluntaryConcessional > 0 || partnerVoluntaryConcessional > 0) {
                contribData.push([
                    'Voluntary Concessional',
                    `$${Math.round(userVoluntaryConcessional).toLocaleString()}`,
                    `$${Math.round(partnerVoluntaryConcessional).toLocaleString()}`,
                    `$${Math.round(userVoluntaryConcessional + partnerVoluntaryConcessional).toLocaleString()}`,
                    `$${Math.round((userVoluntaryConcessional + partnerVoluntaryConcessional) * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            if (userNonConcessional > 0 || partnerNonConcessional > 0) {
                contribData.push([
                    'Non-Concessional',
                    `$${Math.round(userNonConcessional).toLocaleString()}`,
                    `$${Math.round(partnerNonConcessional).toLocaleString()}`,
                    `$${Math.round(userNonConcessional + partnerNonConcessional).toLocaleString()}`,
                    `$${Math.round((userNonConcessional + partnerNonConcessional) * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            if (userSpouseContrib > 0 || partnerSpouseContrib > 0) {
                contribData.push([
                    'Spouse Contributions',
                    `$${Math.round(userSpouseContrib).toLocaleString()}`,
                    `$${Math.round(partnerSpouseContrib).toLocaleString()}`,
                    `$${Math.round(userSpouseContrib + partnerSpouseContrib).toLocaleString()}`,
                    `$${Math.round((userSpouseContrib + partnerSpouseContrib) * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            // Total row
            contribData.push([
                'TOTAL',
                `$${Math.round(userAnnualTotal).toLocaleString()}`,
                `$${Math.round(partnerAnnualTotal).toLocaleString()}`,
                `$${Math.round(combinedAnnualTotal).toLocaleString()}`,
                `$${Math.round(combinedTotalOverPeriod).toLocaleString()}`
            ]);
        } else {
            contribData.push([
                'Superannuation Guarantee',
                `$${Math.round(userSGAnnual).toLocaleString()}`,
                `$${Math.round(userSGAnnual * yearsUntilRetirement).toLocaleString()}`
            ]);
            if (userVoluntaryConcessional > 0) {
                contribData.push([
                    'Voluntary Concessional',
                    `$${Math.round(userVoluntaryConcessional).toLocaleString()}`,
                    `$${Math.round(userVoluntaryConcessional * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            if (userNonConcessional > 0) {
                contribData.push([
                    'Non-Concessional',
                    `$${Math.round(userNonConcessional).toLocaleString()}`,
                    `$${Math.round(userNonConcessional * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            if (userSpouseContrib > 0) {
                contribData.push([
                    'Spouse Contributions',
                    `$${Math.round(userSpouseContrib).toLocaleString()}`,
                    `$${Math.round(userSpouseContrib * yearsUntilRetirement).toLocaleString()}`
                ]);
            }
            // Total row
            contribData.push([
                'TOTAL',
                `$${Math.round(userAnnualTotal).toLocaleString()}`,
                `$${Math.round(userTotalOverPeriod).toLocaleString()}`
            ]);
        }

        const contribColWidths = isCouple ? [45, 28, 28, 33, 36] : [70, 50, 50];
        yPos = this.drawTable(doc, yPos, contribHeaders, contribData, contribColWidths);
        yPos += 5;

        // Contribution insights
        doc.setFillColor(240, 249, 255);
        doc.rect(14, yPos, pageWidth - 28, 30, 'F');
        doc.setDrawColor(59, 130, 246);
        doc.rect(14, yPos, pageWidth - 28, 30, 'S');
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text('Contribution Insights:', 18, yPos + 7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0);
        
        const annualContribAmount = isCouple ? combinedAnnualTotal : userAnnualTotal;
        const totalContribAmount = isCouple ? combinedTotalOverPeriod : userTotalOverPeriod;
        const contribPercentOfFinal = finalBalance > 0 ? ((totalContribAmount / finalBalance) * 100) : 0;
        
        const insight1 = `• You're contributing $${Math.round(annualContribAmount).toLocaleString()} per year (${isCouple ? 'combined' : 'total'})`;
        const insight2 = `• Over ${yearsUntilRetirement} years, total contributions: $${Math.round(totalContribAmount).toLocaleString()}`;
        const insight3 = `• Contributions represent ${contribPercentOfFinal.toFixed(1)}% of your final balance`;
        
        let insight4;
        if (totalContribAmount === 0) {
            insight4 = `• All growth comes from your starting balance - consider adding regular contributions`;
        } else if (investmentGrowth > totalContribAmount) {
            const growthMultiple = ((investmentGrowth / totalContribAmount - 1) * 100).toFixed(0);
            insight4 = `• Investment growth ($${Math.round(investmentGrowth).toLocaleString()}) will exceed your contributions by ${growthMultiple}%`;
        } else {
            insight4 = `• Focus on maximizing contributions - they're your primary wealth driver`;
        }
        
        doc.setFontSize(8);
        doc.text(insight1, 18, yPos + 12);
        doc.text(insight2, 18, yPos + 17);
        doc.text(insight3, 18, yPos + 22);
        doc.text(insight4, 18, yPos + 27);
        
        yPos += 35;

        // ===== PORTFOLIO ALLOCATION =====
        if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'Portfolio Allocation Strategy');
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('Your investment portfolio is allocated across different asset classes:', 14, yPos);
        yPos += 8;

        // Get portfolio allocation
        const ausSharesPct = inputs.ausSharesPct || 0;
        const intlSharesPct = inputs.intlSharesPct || 0;
        const bondsPct = inputs.bondsPct || 0;
        const cashPct = inputs.cashPct || 0;
        const infrastructurePct = inputs.infrastructurePct || 0;

        const allocData = [];
        if (ausSharesPct > 0) allocData.push(['Australian Shares', `${ausSharesPct}%`, 'Growth', 'Higher risk, higher return']);
        if (intlSharesPct > 0) allocData.push(['International Shares', `${intlSharesPct}%`, 'Growth', 'Diversification, growth']);
        if (bondsPct > 0) allocData.push(['Bonds', `${bondsPct}%`, 'Defensive', 'Stability, income']);
        if (cashPct > 0) allocData.push(['Cash', `${cashPct}%`, 'Defensive', 'Liquidity, capital preservation']);
        if (infrastructurePct > 0) allocData.push(['Infrastructure', `${infrastructurePct}%`, 'Alternative', 'Income, inflation hedge']);

        if (allocData.length > 0) {
            const allocHeaders = ['Asset Class', 'Allocation', 'Type', 'Purpose'];
            const allocColWidths = [45, 25, 30, 70];
            yPos = this.drawTable(doc, yPos, allocHeaders, allocData, allocColWidths);
            yPos += 5;

            // Risk profile assessment
            const growthPct = ausSharesPct + intlSharesPct;
            const defensivePct = bondsPct + cashPct;
            
            let riskProfile = '';
            let riskColor = [0, 0, 0];
            if (growthPct >= 80) {
                riskProfile = 'Aggressive (High Growth)';
                riskColor = [220, 38, 38];
            } else if (growthPct >= 60) {
                riskProfile = 'Growth';
                riskColor = [249, 115, 22];
            } else if (growthPct >= 40) {
                riskProfile = 'Balanced';
                riskColor = [59, 130, 246];
            } else {
                riskProfile = 'Conservative (Defensive)';
                riskColor = [34, 197, 94];
            }

            doc.setFillColor(249, 250, 251);
            doc.rect(14, yPos, pageWidth - 28, 20, 'F');
            doc.setDrawColor(...riskColor);
            doc.rect(14, yPos, pageWidth - 28, 20, 'S');
            
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...riskColor);
            doc.text(`Risk Profile: ${riskProfile}`, 18, yPos + 7);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0);
            doc.setFontSize(8);
            doc.text(`Growth Assets: ${growthPct}% | Defensive Assets: ${defensivePct}% | Alternatives: ${infrastructurePct}%`, 18, yPos + 13);
            doc.text(`This allocation targets ${inputs.assumedReturn || 7.5}% annual return with ${growthPct >= 60 ? 'higher' : 'moderate'} volatility`, 18, yPos + 17);
            
            yPos += 25;
        }

        // ===== CHARTS SECTION =====
        if (chartImages.balanceChart || chartImages.contributionChart) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = 20;
            } else {
                yPos += 10;
            }

            this.addSectionHeader(doc, yPos, 'Visual Analysis');
            yPos += 12;

            // Balance/Projection Chart
            if (chartImages.balanceChart) {
                try {
                    if (yPos > pageHeight - 90) {
                        doc.addPage();
                        yPos = 20;
                    }
                    
                    doc.setFontSize(12);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('1. Super Balance Projection Over Time', 14, yPos);
                    yPos += 6;
                    
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(100);
                    doc.text('Shows how super balance grows throughout the accumulation phase.', 14, yPos);
                    doc.setTextColor(0);
                    yPos += 6;
                    
                    const imgWidth = pageWidth - 28;
                    const chartData = typeof chartImages.balanceChart === 'string' ? chartImages.balanceChart : chartImages.balanceChart.data;
                    const chartWidth = (typeof chartImages.balanceChart === 'object' && chartImages.balanceChart.width) ? chartImages.balanceChart.width : 800;
                    const chartHeight = (typeof chartImages.balanceChart === 'object' && chartImages.balanceChart.height) ? chartImages.balanceChart.height : 300;
                    const aspectRatio = chartHeight / chartWidth;
                    const imgHeight = Math.min(80, imgWidth * aspectRatio);
                    
                    doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, imgHeight);
                    yPos += imgHeight + 10;
                } catch (err) {
                    console.warn('Could not add balance chart to PDF:', err);
                    yPos += 10;
                }
            }

            // Contribution Chart
            if (chartImages.contributionChart) {
                try {
                    if (yPos > pageHeight - 90) {
                        doc.addPage();
                        yPos = 20;
                    }
                    
                    doc.setFontSize(12);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('2. Contribution Breakdown', 14, yPos);
                    yPos += 6;
                    
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(100);
                    doc.text('Breakdown of contributions: employer SG, salary sacrifice, after-tax.', 14, yPos);
                    doc.setTextColor(0);
                    yPos += 6;
                    
                    const imgWidth = pageWidth - 28;
                    const chartData = typeof chartImages.contributionChart === 'string' ? chartImages.contributionChart : chartImages.contributionChart.data;
                    const chartWidth = (typeof chartImages.contributionChart === 'object' && chartImages.contributionChart.width) ? chartImages.contributionChart.width : 800;
                    const chartHeight = (typeof chartImages.contributionChart === 'object' && chartImages.contributionChart.height) ? chartImages.contributionChart.height : 300;
                    const aspectRatio = chartHeight / chartWidth;
                    const imgHeight = Math.min(80, imgWidth * aspectRatio);
                    
                    doc.addImage(chartData, 'PNG', 14, yPos, imgWidth, imgHeight);
                    yPos += imgHeight + 10;
                } catch (err) {
                    console.warn('Could not add contribution chart to PDF:', err);
                    yPos += 10;
                }
            }
        }

        // ===== COMPLETE YEAR-BY-YEAR DATA (ALL YEARS) =====
        const yearlyData = results?.yearlyData || results?.combinedData || [];
        if (yearlyData.length > 0) {
            if (yPos > pageHeight - 50) {
                doc.addPage();
                yPos = 20;
            } else {
                yPos += 5;
            }

            this.addSectionHeader(doc, yPos, 'Year-by-Year Accumulation Breakdown (Complete Data)');
            yPos += 10;

            // Summary statistics
            doc.setFillColor(248, 250, 252);
            doc.rect(14, yPos, pageWidth - 28, 16, 'F');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Summary Statistics:', 18, yPos + 6);
            doc.setFont(undefined, 'normal');
            doc.text(`Total Years: ${yearlyData.length} | Starting: $${Math.round(inputs.currentSuper || 0).toLocaleString()} | Final: $${Math.round(finalBalance).toLocaleString()}`, 18, yPos + 11);
            yPos += 21;

            const tableData = [];
            const step = Math.max(1, Math.ceil(yearlyData.length / 25)); // Show up to 25 rows

            for (let i = 0; i < yearlyData.length; i += step) {
                const year = yearlyData[i];
                const row = [
                    String(year.year || (i + 1)),
                    String(year.userAge || (inputs.currentAge + i)),
                ];
                
                if (isCouple) {
                    row.push(String(year.partnerAge || ''));
                    row.push(`$${Math.round(year.userBalance || 0).toLocaleString()}`);
                    row.push(`$${Math.round(year.partnerBalance || 0).toLocaleString()}`);
                    row.push(`$${Math.round(year.totalBalance || 0).toLocaleString()}`);
                } else {
                    row.push(`$${Math.round(year.userBalance || year.balance || 0).toLocaleString()}`);
                }
                
                tableData.push(row);
            }

            // Define table headers and column widths
            const headers = isCouple 
                ? ['Year', 'Your Age', 'Partner Age', 'Your Balance', 'Partner Balance', 'Total']
                : ['Year', 'Age', 'Balance'];
            const columnWidths = isCouple ? [15, 20, 25, 35, 35, 40] : [30, 30, 70];

            // Draw the table
            yPos = this.drawTable(doc, yPos, headers, tableData, columnWidths);
        }

        // Assumptions
        if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Assumptions', 14, yPos);
        yPos += 8;

        const assumptionsPairs = [
            ['Return Rate (p.a.)', `${inputs.assumedReturn || 7.5}%`],
            ['Inflation (p.a.)', `${inputs.assumedInflation || 2.5}%`],
            ['Wage Growth (p.a.)', `${inputs.wageGrowthRate ?? 0.3}%`],
            ['Admin Fees', `$${inputs.adminFees || 0} + ${inputs.adminFeePercentage || 0}%`]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, assumptionsPairs);

        // Corporate Disclaimer - STRONG legal protection (auto-creates page if needed)
        this.addCorporateDisclaimer(doc, yPos);

        // Save or return base64 for email attachment
        if (arguments[3] === true) {
            return doc.output('datauristring').replace(/^data:application\/pdf(?:;[^,]*)?;base64,/, '');
        }
        doc.save(`SuperCalcPro-Accumulation-${new Date().toISOString().split('T')[0]}.pdf`);
    }

    /**
     * Generate COMPREHENSIVE SMSF PDF - Professional fund analysis report
     * Uses same styling/helpers as other PDFs for consistency
     */
    static exportComprehensiveSMSF(smsfState) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        
        // Professional header (SAME as Advanced PDF)
        let yPos = this.addProfessionalHeader(doc, 'Comprehensive SMSF Fund Analysis', 'Self-Managed Superannuation Fund - Complete Report');

        // ===== EXECUTIVE PRÉCIS / ABSTRACT =====
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('EXECUTIVE PRÉCIS', 14, yPos);
        yPos += 8;

        // Extract data from smsfState
        const smsfData = smsfState?.smsfData;
        const memberProfile = smsfState?.memberProfile;
        const fundBalance = smsfData?.totalAssets || smsfData?.balance || 1250000;
        const members = smsfData?.members || [
            { name: 'John Smith', age: 55, balance: 700000 },
            { name: 'Mary Smith', age: 53, balance: 550000 }
        ];
        const memberCount = members.length;
        const totalFees = (smsfState?.smsfAuditFees || 1500) + (smsfState?.smsfAdminFees || 2500) + (smsfState?.smsfAccountingFees || 3000);
        const feePercent = ((totalFees / fundBalance) * 100).toFixed(2);
        const establishedDate = smsfState?.smsfEstablishedDate || '2020-07-01';
        const fundAge = new Date().getFullYear() - parseInt(establishedDate.split('-')[0]);
        
        // Calculate property values for use throughout document
        const propertyValue = smsfState?.smsfPropertyValue || 0;
        const propertyLoan = smsfState?.smsfPropertyLoan || 0;
        const hasProperty = smsfState?.smsfDirectProperty || smsfData?.hasLRBA || false;
        const interestRate = (smsfState?.smsfPropertyInterestRate || 6.5) / 100;
        const loanTerm = smsfState?.smsfPropertyLoanTerm || 25;
        const monthlyRate = interestRate / 12;
        const numPayments = loanTerm * 12;
        const monthlyPayment = propertyLoan > 0 ? (propertyLoan * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1) : 0;
        const annualPayment = monthlyPayment * 12;
        const rentalYield = smsfState?.smsfPropertyRentalYield || 4.5;
        const annualRental = (propertyValue * rentalYield) / 100;
        const netRentalIncome = annualRental - annualPayment;
        
        // Other commonly used values
        const nonConcessUsed = smsfState?.smsfNonConcessionalUsed || 0;
        const inHousePercent = ((smsfState?.smsfInHouseAssets || 0) / (fundBalance || 1)) * 100;
        const johnTBR = members[0]?.tbr || 0;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const precis = `This comprehensive SMSF analysis presents a complete fund overview for ${smsfData?.fundName || 'Smith Family Superannuation Fund'} (ABN ${smsfData?.abn || '12 345 678 901'}). The fund, established in ${establishedDate.split('-')[0]}, currently holds $${Math.round(fundBalance).toLocaleString()} across ${memberCount} member${memberCount > 1 ? 's' : ''} with an average balance of $${Math.round(fundBalance / memberCount).toLocaleString()} per member. Annual operating fees total $${totalFees.toLocaleString()} (${feePercent}% of fund balance). This report consolidates analysis across 8 key categories covering contribution planning, investment compliance, pension optimization, tax strategies, estate planning, performance analytics, strategic comparisons, and administration. All figures represent illustrative scenarios for educational purposes in accordance with ASIC guidelines and should be reviewed by licensed professionals before implementation.`;

        const wrappedPrecis = doc.splitTextToSize(precis, pageWidth - 28);
        doc.text(wrappedPrecis, 14, yPos);
        yPos += wrappedPrecis.length * 5 + 12;

        // ===== EXECUTIVE SUMMARY (Colored Metric Boxes) =====
        // NOTE: addExecutiveSummary adds its own header, don't add duplicate!

        const keyMetrics = [
            { label: 'Total Fund Balance', value: `$${Math.round(fundBalance).toLocaleString()}` },
            { label: 'Total Annual Fees', value: `$${totalFees.toLocaleString()}` },
            { label: 'Fee as % of Balance', value: `${feePercent}%` },
            { label: 'Number of Members', value: `${memberCount}` }
        ];

        yPos = this.addExecutiveSummary(doc, yPos, keyMetrics);
        yPos += 15;

        // ========================================
        // PORTFOLIO OVERVIEW - VISUAL BREAKDOWN
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'PORTFOLIO OVERVIEW & COMPOSITION');
        yPos += 12;

        // MEMBER BALANCE BREAKDOWN (Visual Table)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Member Balance Distribution', 14, yPos);
        yPos += 8;

        // Member balance table with visual bars
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Member', 18, yPos + 5);
        doc.text('Age', 70, yPos + 5);
        doc.text('Balance', 90, yPos + 5);
        doc.text('% of Fund', 130, yPos + 5);
        doc.text('Visual', 160, yPos + 5);
        yPos += 7;

        const maxBalance = Math.max(...members.map(m => m.balance));
        members.forEach((member, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            }
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            doc.text(member.name, 18, yPos + 5);
            doc.text(`${member.age}`, 70, yPos + 5);
            doc.text(`$${Math.round(member.balance).toLocaleString()}`, 90, yPos + 5);
            doc.text(`${((member.balance / fundBalance) * 100).toFixed(1)}%`, 130, yPos + 5);
            
            // Visual bar
            const barWidth = (member.balance / maxBalance) * 30;
            doc.setFillColor(59, 130, 246);
            doc.rect(160, yPos + 2, barWidth, 4, 'F');
            
            yPos += 8;
        });
        yPos += 12;

        // ASSET ALLOCATION BREAKDOWN (Visual Table)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Current Asset Allocation', 14, yPos);
        yPos += 8;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('(Complete breakdown of $' + Math.round(fundBalance / 1000) + 'k total fund balance)', 14, yPos);
        yPos += 8;

        // Calculate actual allocation including property, collectibles, etc.
        const propValue = smsfState?.smsfPropertyValue || 0;
        const collectValue = (smsfState?.collectibles?.items || []).reduce((sum, item) => sum + (item.value || 0), 0);
        const liquidAssets = fundBalance - propValue - collectValue;
        
        const assetAllocationData = [
            ['Australian Shares', Math.round(liquidAssets * 0.38), [59, 130, 246]],
            ['International Shares', Math.round(liquidAssets * 0.30), [16, 185, 129]],
            ['Direct Property (LRBA)', propValue, [245, 158, 11]],
            ['Collectibles', collectValue, [139, 92, 246]],
            ['Fixed Income/Bonds', Math.round(liquidAssets * 0.22), [107, 114, 128]],
            ['Cash & Equivalents', Math.round(liquidAssets * 0.10), [6, 182, 212]]
        ];

        // Asset allocation table with visual bars
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Asset Class', 18, yPos + 5);
        doc.text('Value', 80, yPos + 5);
        doc.text('% of Fund', 120, yPos + 5);
        doc.text('Allocation Visual', 155, yPos + 5);
        yPos += 7;

        const maxAssetValue = Math.max(...assetAllocationData.map(a => a[1]));
        assetAllocationData.forEach(([label, value, color], index) => {
            if (value === 0) return; // Skip zero values
            
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            }
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            doc.text(label, 18, yPos + 5);
            doc.text(`$${Math.round(value).toLocaleString()}`, 80, yPos + 5);
            doc.text(`${((value / fundBalance) * 100).toFixed(1)}%`, 120, yPos + 5);
            
            // Visual bar (proportional to value)
            const barWidth = (value / fundBalance) * 35; // Max 35px width
            doc.setFillColor(...color);
            doc.rect(155, yPos + 2, barWidth, 4, 'F');
            
            yPos += 8;
        });
        
        // Total row
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('TOTAL FUND ASSETS', 18, yPos + 5);
        doc.text(`$${Math.round(fundBalance).toLocaleString()}`, 80, yPos + 5);
        doc.text('100.0%', 120, yPos + 5);
        yPos += 12;

        // CHART 3: 10-Year Balance Projection (Line Chart)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('10-Year Balance Projection', 14, yPos);
        yPos += 8;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('(Assumes 7% p.a. return, no additional contributions, illustrative only)', 14, yPos);
        yPos += 10;

        // Calculate 10-year projection
        const years = 11;
        const projectionData = [];
        const projectionLabels = [];
        let currentBalance = fundBalance;
        const growthRate = 0.07;

        for (let i = 0; i < years; i++) {
            projectionData.push(currentBalance);
            projectionLabels.push(`Yr ${i}`);
            currentBalance *= (1 + growthRate);
        }

        this.drawLineChart(doc, 14, yPos, pageWidth - 28, 50, projectionData, projectionLabels, [16, 185, 129]);
        yPos += 70;

        // CHART 4: Fee Comparison (Bar Chart)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Annual Fee Comparison: SMSF vs Retail Super', 14, yPos);
        yPos += 8;

        const feeData = [totalFees, fundBalance * 0.015]; // SMSF fixed vs Retail 1.5%
        const feeLabels = ['SMSF (Fixed)', 'Retail (1.5%)'];
        const feeColors = [[16, 185, 129], [239, 68, 68]];

        this.drawBarChart(doc, 14, yPos, (pageWidth - 28) / 2, 40, feeData, feeLabels, feeColors);
        yPos += 60;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        const savingsText = `Potential annual savings: $${Math.round(feeData[1] - feeData[0]).toLocaleString()} (${(((feeData[1] - feeData[0]) / feeData[1]) * 100).toFixed(1)}% cost reduction)`;
        doc.text(savingsText, 14, yPos);
        yPos += 10;

        // ========================================
        // SECTION: ASSET HOLDINGS BREAKDOWN (ASSET VALUATION MANAGER)
        // ========================================
        doc.addPage();
        yPos = 20;
        
        this.addSectionHeader(doc, yPos, 'DETAILED ASSET HOLDINGS & VALUATION');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const assetIntro = `This section provides a comprehensive breakdown of the fund's investment holdings from the Asset Valuation Manager, including detailed valuations for properties, alternative investments, and collectibles. Each asset is tracked individually with specific metrics for accurate portfolio management and compliance reporting.`;
        const wrappedAssetIntro = doc.splitTextToSize(assetIntro, pageWidth - 28);
        doc.text(wrappedAssetIntro, 14, yPos);
        yPos += wrappedAssetIntro.length * 5 + 10;

        // Check for Asset Valuation Manager data
        const assetValuationData = smsfState?.assetValuationData || {};
        const hasDetailedAssets = assetValuationData.assets && Object.keys(assetValuationData.assets).length > 0;

        if (hasDetailedAssets) {
            const assets = assetValuationData.assets;

            // Residential Properties
            if (assets.residentialProperties && assets.residentialProperties.length > 0) {
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('🏘️ Residential Properties', 14, yPos);
                doc.setTextColor(0);
                yPos += 7;

                assets.residentialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.assetDescription || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Current Valuation: $${(prop.currentValuation || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Purchase Price: $${(prop.purchasePrice || 0).toLocaleString()}`, 110, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}% p.a.`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 4).toFixed(1)}% p.a.`, 110, yPos);
                    yPos += 4;
                    doc.text(`Valuation Date: ${prop.valuationDate || 'N/A'}`, 22, yPos);
                    doc.text(`Method: ${prop.valuationMethod || 'N/A'}`, 110, yPos);
                    yPos += 7;
                });
            }

            // Commercial Properties
            if (assets.commercialProperties && assets.commercialProperties.length > 0) {
                if (yPos > pageHeight - 50) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('🏢 Commercial Properties', 14, yPos);
                doc.setTextColor(0);
                yPos += 7;

                assets.commercialProperties.forEach((prop, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${prop.assetDescription || `Property ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Current Valuation: $${(prop.currentValuation || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Purchase Price: $${(prop.purchasePrice || 0).toLocaleString()}`, 110, yPos);
                    yPos += 4;
                    doc.text(`Rental Yield: ${(prop.rentalYield || 0).toFixed(2)}% p.a.`, 22, yPos);
                    doc.text(`Capital Growth: ${(prop.capitalGrowth || 4).toFixed(1)}% p.a.`, 110, yPos);
                    yPos += 4;
                    
                    // LRBA details if present
                    if (prop.lrbaLoan && prop.lrbaLoan > 0) {
                        doc.setFontSize(8);
                        doc.setTextColor(100);
                        doc.text(`LRBA Loan: $${prop.lrbaLoan.toLocaleString()} @ ${(prop.lrbaInterestRate || 0).toFixed(2)}% (LVR: ${((prop.lrbaLoan / prop.currentValuation) * 100).toFixed(1)}%)`, 22, yPos);
                        doc.setTextColor(0);
                        yPos += 4;
                    }
                    
                    doc.setFontSize(9);
                    doc.text(`Valuation Date: ${prop.valuationDate || 'N/A'}`, 22, yPos);
                    yPos += 7;
                });
            }

            // Alternative Assets (Infrastructure, etc.)
            if (assets.alternativeAssets && assets.alternativeAssets.length > 0) {
                if (yPos > pageHeight - 50) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('⚡ Infrastructure & Alternative Investments', 14, yPos);
                doc.setTextColor(0);
                yPos += 7;

                assets.alternativeAssets.forEach((asset, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${asset.assetDescription || `Asset ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Current Value: $${(asset.currentValue || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Purchase Price: $${(asset.purchasePrice || 0).toLocaleString()}`, 110, yPos);
                    yPos += 4;
                    doc.text(`Expected Return: ${(asset.expectedReturn || 0).toFixed(1)}% p.a.`, 22, yPos);
                    doc.text(`Valuation Date: ${asset.valuationDate || 'N/A'}`, 110, yPos);
                    yPos += 7;
                });
            }

            // Collectibles
            if (assets.collectibles && assets.collectibles.length > 0) {
                if (yPos > pageHeight - 50) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('🎨 Collectibles & Art', 14, yPos);
                doc.setTextColor(0);
                yPos += 7;

                doc.setFontSize(8);
                doc.setFont(undefined, 'italic');
                doc.setTextColor(100);
                doc.text('Note: Collectibles are non-productive assets (no income generation) tracked for total fund value.', 14, yPos);
                doc.setTextColor(0);
                yPos += 6;

                assets.collectibles.forEach((item, index) => {
                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`${index + 1}. ${item.assetDescription || `Collectible ${index + 1}`}`, 18, yPos);
                    yPos += 5;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Current Value: $${(item.currentValue || 0).toLocaleString()}`, 22, yPos);
                    doc.text(`Purchase Price: $${(item.purchasePrice || 0).toLocaleString()}`, 110, yPos);
                    yPos += 4;
                    doc.text(`Capital Growth: ${(item.capitalGrowth || 0).toFixed(1)}% p.a.`, 22, yPos);
                    doc.text(`Valuation Date: ${item.valuationDate || 'N/A'}`, 110, yPos);
                    yPos += 7;
                });
            }

            yPos += 5;
        }
        
        // Always define assetHoldings - use fallback if no detailed data
        let assetHoldings = [];
        if (!hasDetailedAssets) {
            // Fallback to generic asset allocation if no detailed data
            assetHoldings = [
                ['Australian Shares', `$${Math.round(fundBalance * 0.36).toLocaleString()}`, '36.0%'],
                ['International Shares', `$${Math.round(fundBalance * 0.28).toLocaleString()}`, '28.0%'],
                ['Property/REITs', `$${Math.round(fundBalance * 0.20).toLocaleString()}`, '20.0%'],
                ['Fixed Income/Bonds', `$${Math.round(fundBalance * 0.11).toLocaleString()}`, '11.0%'],
                ['Cash & Equivalents', `$${Math.round(fundBalance * 0.05).toLocaleString()}`, '5.0%']
            ];
        }

        // Draw table only if we have assetHoldings data
        if (assetHoldings.length > 0) {
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setFillColor(239, 246, 255);
            doc.rect(14, yPos, pageWidth - 28, 7, 'F');
            doc.text('Asset Class', 18, yPos + 5);
            doc.text('Value', 90, yPos + 5);
            doc.text('% of Fund', 140, yPos + 5);
            yPos += 7;

            doc.setFont(undefined, 'normal');
            assetHoldings.forEach((row, index) => {
                if (index % 2 === 1) {
                    doc.setFillColor(248, 250, 252);
                    doc.rect(14, yPos, pageWidth - 28, 6, 'F');
                }
                doc.text(row[0], 18, yPos + 4);
                doc.text(row[1], 90, yPos + 4);
                doc.text(row[2], 140, yPos + 4);
                yPos += 6;
            });
            yPos += 10;
        }

        // Collectibles section (if applicable)
        const hasCollectibles = smsfState?.smsfData?.hasCollectibles || false;
        const collectiblesItems = smsfState?.collectibles?.items || [];
        
        if (hasCollectibles || collectiblesItems.length > 0) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Collectibles & Personal Use Assets', 14, yPos);
            yPos += 8;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            const collectIntro = `SMSFs may invest in collectibles and personal use assets such as artwork, vintage vehicles, wine, and other valuables. However, these assets must meet strict compliance requirements: they cannot be used by members or related parties, must be stored at arms-length, require professional annual valuations, and must be held for genuine investment purposes (sole purpose test). These assets often incur higher insurance and storage costs.`;
            const wrappedCollectIntro = doc.splitTextToSize(collectIntro, pageWidth - 28);
            doc.text(wrappedCollectIntro, 14, yPos);
            yPos += wrappedCollectIntro.length * 5 + 10;

            // Collectibles table
            if (collectiblesItems.length > 0) {
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.setFillColor(239, 246, 255);
                doc.rect(14, yPos, pageWidth - 28, 7, 'F');
                doc.text('Asset Type', 18, yPos + 5);
                doc.text('Description', 70, yPos + 5);
                doc.text('Value', 130, yPos + 5);
                doc.text('Last Valued', 160, yPos + 5);
                yPos += 7;

                doc.setFont(undefined, 'normal');
                doc.setFontSize(8);
                let totalCollectiblesValue = 0;
                
                collectiblesItems.forEach((item, index) => {
                    if (index % 2 === 1) {
                        doc.setFillColor(248, 250, 252);
                        doc.rect(14, yPos, pageWidth - 28, 6, 'F');
                    }
                    doc.text(item.type || 'Unknown', 18, yPos + 4);
                    const desc = item.description && item.description.length > 25 ? item.description.substring(0, 25) + '...' : (item.description || 'N/A');
                    doc.text(desc, 70, yPos + 4);
                    doc.text(`$${Math.round(item.value || 0).toLocaleString()}`, 130, yPos + 4);
                    doc.text(item.valuationDate || 'Not specified', 160, yPos + 4);
                    yPos += 6;
                    totalCollectiblesValue += (item.value || 0);
                });
                
                // Total row
                doc.setFont(undefined, 'bold');
                doc.setFillColor(239, 246, 255);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
                doc.text('TOTAL COLLECTIBLES', 18, yPos + 4);
                doc.text(`$${Math.round(totalCollectiblesValue).toLocaleString()}`, 130, yPos + 4);
                doc.text(`${((totalCollectiblesValue / fundBalance) * 100).toFixed(1)}% of fund`, 160, yPos + 4);
                yPos += 10;
            }

            this.addInfoBox(doc, yPos,
                'COMPLIANCE REMINDER: All collectibles must be professionally valued annually, insured, and stored at arms-length from members. ' +
                'Members and related parties cannot use or benefit from these assets. Annual valuations must be documented and kept with fund records.',
                'warning'
            );
            yPos += 25;
        }

        // ========================================
        // SECTION 2: FUND & MEMBER DETAILS
        // ========================================
        doc.addPage();
        yPos = 20;
        
        this.addSectionHeader(doc, yPos, '2. FUND & MEMBER DETAILS');
        yPos += 10;

        const fundDetails = [
            ['Fund Name', smsfData?.fundName || 'Not Specified'],
            ['ABN', smsfData?.abn || 'Not Provided'],
            ['TFN', smsfData?.tfn || 'Not Provided'],
            ['Trustee Type', smsfData?.trusteeType || 'Individual'],
            ['Number of Trustees', `${smsfState?.smsfTrusteeCount || 2}`],
            ['Establishment Date', establishedDate]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, fundDetails);
        yPos += 10;

        // ===== MEMBER DETAILS =====
        if (yPos > pageHeight - 80) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'MEMBER DETAILS & BALANCES');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const memberIntro = `An SMSF can have up to ${smsfData?.hasLRBA ? '6' : '4'} members (or 6 if established before 2021 with an LRBA). Each member maintains their own account balance within the fund, though assets are held collectively. Understanding individual member balances is important for contribution planning, pension conversions, and death benefit distributions.`;
        const wrappedIntro = doc.splitTextToSize(memberIntro, pageWidth - 28);
        doc.text(wrappedIntro, 14, yPos);
        yPos += wrappedIntro.length * 5 + 8;

        // members variable already declared earlier
        if (members.length > 0) {
            members.forEach((member, index) => {
                if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.text(`Member ${index + 1}: ${member.name || 'Not Specified'}`, 14, yPos);
                yPos += 7;

                const memberDetails = [
                    ['Age', `${member.age || 'Not Specified'}`],
                    ['Member Balance', `$${Math.round(member.balance || 0).toLocaleString()}`],
                    ['Transfer Balance Cap Used', `$${Math.round(member.tbr || 0).toLocaleString()}`],
                    ['% of Total Fund', `${((member.balance || 0) / (fundBalance || 1) * 100).toFixed(1)}%`]
                ];

                doc.setFontSize(10);
                memberDetails.forEach(([label, value]) => {
                    doc.setFont(undefined, 'normal');
                    doc.text(`  ${label}:`, 18, yPos);
                    doc.setFont(undefined, 'bold');
                    doc.text(value, 80, yPos);
                    yPos += 6;
                });
                yPos += 6;
            });
        } else {
            doc.setFontSize(10);
            doc.setFont(undefined, 'italic');
            doc.text('This illustrative scenario does not include specific member data.', 18, yPos);
            yPos += 10;
        }

        // ========================================
        // SECTION 3: CONTRIBUTION PLANNING
        // ========================================
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'CONTRIBUTION PLANNING & STRATEGIES');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const contribIntro = `Contributions to superannuation are governed by annual caps set by the Australian Taxation Office (ATO). There are two main types: concessional (before-tax) contributions which are taxed at 15% within the fund, and non-concessional (after-tax) contributions which are not taxed. Understanding these caps and utilizing carry-forward provisions can significantly enhance retirement savings while remaining compliant with regulations. The following analysis examines contribution capacity across multiple scenarios and strategies.`;
        const wrappedContribIntro = doc.splitTextToSize(contribIntro, pageWidth - 28);
        doc.text(wrappedContribIntro, 14, yPos);
        yPos += wrappedContribIntro.length * 5 + 10;

        // Contribution Caps Monitor
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Current Year Contribution Caps', 14, yPos);
        yPos += 7;

        const concessionalCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
        const nonConcessionalCap = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;
        const bringForwardCap = FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION;

        const contributionDetails = [
            ['Concessional Cap (Annual)', `$${concessionalCap.toLocaleString()}`],
            ['Non-Concessional Cap (Annual)', `$${nonConcessionalCap.toLocaleString()}`],
            ['Bring-Forward Cap (3 years)', `$${bringForwardCap.toLocaleString()}`],
            ['Concessional Used (Current FY)', `$${Math.round(smsfState?.smsfConcessionalUsed || 0).toLocaleString()}`],
            ['Non-Concessional Used (Current FY)', `$${Math.round(smsfState?.smsfNonConcessionalUsed || 0).toLocaleString()}`],
            ['Remaining Concessional', `$${(concessionalCap - (smsfState?.smsfConcessionalUsed || 0)).toLocaleString()}`],
            ['Remaining Non-Concessional', `$${(nonConcessionalCap - (smsfState?.smsfNonConcessionalUsed || 0)).toLocaleString()}`]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, contributionDetails);
        yPos += 8;

        // Visual usage bars
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Contribution Cap Usage (Visual)', 14, yPos);
        yPos += 7;

        // Concessional usage bar
        const concessUsed = smsfState?.smsfConcessionalUsed || 0;
        const concessPercent = (concessUsed / concessionalCap) * 100;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('Concessional:', 18, yPos);
        doc.text(`$${Math.round(concessUsed).toLocaleString()} / $${concessionalCap.toLocaleString()}`, 60, yPos);
        
        // Draw bar
        doc.setDrawColor(200, 200, 200);
        doc.rect(120, yPos - 3, 70, 5, 'S');
        doc.setFillColor(concessPercent > 90 ? 239 : concessPercent > 70 ? 245 : 16, concessPercent > 90 ? 68 : concessPercent > 70 ? 158 : 185, concessPercent > 90 ? 68 : concessPercent > 70 ? 11 : 129);
        doc.rect(120, yPos - 3, (concessPercent / 100) * 70, 5, 'F');
        doc.text(`${concessPercent.toFixed(0)}%`, 193, yPos);
        yPos += 8;

        // Non-concessional usage bar (using value defined at top)
        const nonConcessPercent = (nonConcessUsed / nonConcessionalCap) * 100;
        doc.text('Non-Concessional:', 18, yPos);
        doc.text(`$${Math.round(nonConcessUsed).toLocaleString()} / $${nonConcessionalCap.toLocaleString()}`, 60, yPos);
        
        doc.setDrawColor(200, 200, 200);
        doc.rect(120, yPos - 3, 70, 5, 'S');
        doc.setFillColor(nonConcessPercent > 90 ? 239 : nonConcessPercent > 70 ? 245 : 16, nonConcessPercent > 90 ? 68 : nonConcessPercent > 70 ? 158 : 185, nonConcessPercent > 90 ? 68 : nonConcessPercent > 70 ? 11 : 129);
        doc.rect(120, yPos - 3, (nonConcessPercent / 100) * 70, 5, 'F');
        doc.text(`${nonConcessPercent.toFixed(0)}%`, 193, yPos);
        yPos += 12;

        this.addInfoBox(doc, yPos,
            'Concessional contributions are taxed at 15% within the fund. Non-concessional contributions are tax-free but count towards caps. ' +
            'Excess contributions are taxed at higher rates and may be refundable.',
            'info'
        );
        yPos += 25;

        // ===== TOOL 2: CARRY-FORWARD PROVISIONS =====
        doc.addPage();
        yPos = 20;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 2: Carry-Forward Unused Concessional Caps', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const carryForwardIntro = `Since 2018-19, individuals with Total Super Balance (TSB) below $500,000 on June 30 of the previous financial year can carry forward unused concessional contribution cap amounts for up to 5 years. This allows "catch-up" contributions in years when income is higher. Unused amounts expire after 5 years on a first-in-first-out basis. This is particularly valuable for individuals with variable income or those who could not maximize contributions in earlier years due to lower earnings.`;
        const wrappedCFIntro = doc.splitTextToSize(carryForwardIntro, pageWidth - 28);
        doc.text(wrappedCFIntro, 14, yPos);
        yPos += wrappedCFIntro.length * 5 + 10;

        // 5-year carry-forward history table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Financial Year', 18, yPos + 5);
        doc.text('Cap', 65, yPos + 5);
        doc.text('Used', 90, yPos + 5);
        doc.text('Unused', 115, yPos + 5);
        doc.text('Carried Forward', 145, yPos + 5);
        doc.text('Status', 175, yPos + 5);
        yPos += 7;

        const carryForwardYears = [
            ['2019-20', 25000, 12000, 13000, 13000, 'Expired'],
            ['2020-21', 27500, 18500, 9000, 9000, 'Available'],
            ['2021-22', 27500, 27500, 0, 0, 'Fully Used'],
            ['2022-23', 27500, 15000, 12500, 12500, 'Available'],
            ['2023-24', 30000, 22000, 8000, 8000, 'Available']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        carryForwardYears.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(`$${row[1].toLocaleString()}`, 65, yPos + 4);
            doc.text(`$${row[2].toLocaleString()}`, 90, yPos + 4);
            doc.text(`$${row[3].toLocaleString()}`, 115, yPos + 4);
            doc.text(`$${row[4].toLocaleString()}`, 145, yPos + 4);
            doc.setTextColor(row[5] === 'Expired' ? 150 : row[5] === 'Available' ? 16 : 100, row[5] === 'Expired' ? 150 : row[5] === 'Available' ? 185 : 100, row[5] === 'Expired' ? 150 : row[5] === 'Available' ? 129 : 100);
            doc.text(row[5], 175, yPos + 4);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
        });
        
        // Total available
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 6, 'F');
        doc.text('TOTAL AVAILABLE FOR CARRY-FORWARD', 18, yPos + 4);
        const totalCF = 9000 + 12500 + 8000;
        doc.text(`$${totalCF.toLocaleString()}`, 145, yPos + 4);
        yPos += 12;

        this.addInfoBox(doc, yPos,
            'Eligibility Requirement: Total Super Balance must be below $500,000 on June 30 of the previous financial year to access carry-forward amounts. ' +
            'This example shows $29,500 available carry-forward, allowing contributions up to $59,500 in the current year.',
            'info'
        );
        yPos += 28;

        // ===== TOOL 3: BRING-FORWARD NON-CONCESSIONAL =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 3: Bring-Forward Non-Concessional Contributions', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const nccCap = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;
        const bfMax = FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION;
        const tbc = FinancialConfig.TRANSFER_BALANCE_CAP;
        const bfTrigger = FinancialConfig.TSB_THRESHOLDS.BRING_FORWARD_TRIGGER;
        const bringForwardIntro = `The bring-forward rule allows eligible individuals to make up to 3 years worth of non-concessional contributions in a single year ($${bfMax.toLocaleString()} total at current rates). This is particularly useful when receiving large one-off payments such as inheritance, sale of property, or business sale proceeds. Once triggered, the bring-forward period covers the next 3 financial years. Eligibility requires age under 75 and Total Super Balance below the general transfer balance cap ($${(tbc / 1e6).toFixed(1)}M). This strategy can significantly accelerate wealth building within the tax-advantaged super environment.`;
        const wrappedBFIntro = doc.splitTextToSize(bringForwardIntro, pageWidth - 28);
        doc.text(wrappedBFIntro, 14, yPos);
        yPos += wrappedBFIntro.length * 5 + 10;

        // Bring-forward scenario table
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Example Bring-Forward Scenarios:', 14, yPos);
        yPos += 7;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Scenario', 18, yPos + 5);
        doc.text('Age', 80, yPos + 5);
        doc.text('TSB', 100, yPos + 5);
        doc.text('Max Available', 135, yPos + 5);
        doc.text('Tax Saving*', 170, yPos + 5);
        yPos += 7;

        const bringForwardScenarios = [
            ['Inheritance received', '62', '$1.2M', '$240,000', '$27,200'],
            ['Business sale proceeds', '58', '$800k', `$${bfMax.toLocaleString()}`, '$40,800'],
            ['Property sale windfall', '54', '$450k', `$${bfMax.toLocaleString()}`, '$40,800'],
            ['Investment maturity', '48', '$350k', `$${bfMax.toLocaleString()}`, '$40,800']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        bringForwardScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 80, yPos + 4);
            doc.text(row[2], 100, yPos + 4);
            doc.text(row[3], 135, yPos + 4);
            doc.text(row[4], 170, yPos + 4);
            yPos += 6;
        });
        yPos += 8;

        doc.setFontSize(7);
        doc.setFont(undefined, 'italic');
        doc.text('*Tax saving based on 47% marginal tax rate vs 15% super tax (assumes earnings on amount over 10 years)', 18, yPos);
        yPos += 10;

        this.addInfoBox(doc, yPos,
            `Bring-forward is triggered automatically when non-concessional contributions exceed $${nccCap.toLocaleString()} in a single year. ` +
            'Once triggered, the individual has access to 3 years of caps but cannot trigger it again until the period ends. ' +
            `Age and TSB restrictions apply: full bring-forward typically requires TSB below $${(bfTrigger / 1e6).toFixed(2)}M; reduced amounts apply between that level and the general TBC.`,
            'info'
        );
        yPos += 28;

        // ===== TOOL 4: SPOUSE CONTRIBUTION SPLITTING =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 4: Spouse Contribution Splitting', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const spouseSplitIntro = `Spouse contribution splitting allows you to split up to 85% of your concessional contributions to your spouse's super account. This strategy helps equalize balances between spouses, can provide earlier access to super if one spouse is older, and may reduce tax on death benefits paid to adult children (by maximizing tax-free components). Splitting must occur in the financial year after contributions are made, and split amounts count towards the recipient spouse's contribution caps.`;
        const wrappedSSIntro = doc.splitTextToSize(spouseSplitIntro, pageWidth - 28);
        doc.text(wrappedSSIntro, 14, yPos);
        yPos += wrappedSSIntro.length * 5 + 10;

        // Splitting scenario example
        const memberContribution = 30000;
        const splitableAmount = memberContribution * 0.85;
        const afterSplitYou = memberContribution - splitableAmount;
        const afterSplitSpouse = splitableAmount;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Example Splitting Scenario:', 14, yPos);
        yPos += 7;

        const splittingData = [
            ['Your Concessional Contributions', `$${memberContribution.toLocaleString()}`],
            ['Maximum Splitable Amount (85%)', `$${splitableAmount.toLocaleString()}`],
            ['Your Balance After Split', `$${afterSplitYou.toLocaleString()}`],
            ['Spouse Receives', `$${afterSplitSpouse.toLocaleString()}`],
            ['Tax Paid by Fund (15% of contributions)', `$${(memberContribution * 0.15).toLocaleString()}`]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, splittingData);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('Benefits: Balances retirement savings, potential earlier access if spouse is older, estate planning optimization.', 18, yPos);
        yPos += 10;

        // ===== TOOL 5: GOVERNMENT CO-CONTRIBUTION =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 5: Government Co-Contribution Eligibility', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const coContribIntro = `The government co-contribution scheme provides a financial incentive for low to middle-income earners to make personal (after-tax) super contributions. For 2024-25, the maximum co-contribution is $500 (50 cents for every dollar contributed up to $1,000), available to those earning less than $43,445. The benefit phases out between $43,445 and $58,445. Eligibility requires at least 10% of income from employment or carrying on a business, and you must be under 71 years old. This is effectively "free money" from the government to boost retirement savings.`;
        const wrappedCoContrib = doc.splitTextToSize(coContribIntro, pageWidth - 28);
        doc.text(wrappedCoContrib, 14, yPos);
        yPos += wrappedCoContrib.length * 5 + 10;

        // Co-contribution eligibility table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Income', 18, yPos + 5);
        doc.text('Your Contribution', 60, yPos + 5);
        doc.text('Govt Co-Contribution', 115, yPos + 5);
        doc.text('Total Benefit', 165, yPos + 5);
        yPos += 7;

        const coContribScenarios = [
            ['$35,000', '$1,000', '$500', '$1,500'],
            ['$43,445', '$1,000', '$500', '$1,500'],
            ['$50,000', '$1,000', '$250', '$1,250'],
            ['$58,445', '$1,000', '$0', '$1,000'],
            ['$65,000', '$1,000', '$0', '$1,000']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        coContribScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 70, yPos + 4);
            doc.text(row[2], 125, yPos + 4);
            doc.setFont(undefined, 'bold');
            doc.text(row[3], 170, yPos + 4);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 12;

        // ===== TOOL 6: DOWNSIZER CONTRIBUTION =====
        doc.addPage();
        yPos = 20;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 6: Downsizer Contribution Eligibility', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const downsizerIntro = `The downsizer contribution allows individuals aged 55 or over to contribute up to $300,000 per person from the proceeds of selling their home into superannuation. This is a once-in-a-lifetime contribution that does not count towards contribution caps and can be made even if Total Super Balance exceeds the general transfer balance cap ($${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M). Key requirements: the home must have been owned for at least 10 years, be in Australia, and not be a caravan, houseboat, or mobile home. This strategy is particularly valuable for retirees looking to boost super without affecting Age Pension eligibility (after downsizer exemption period).`;
        const wrappedDownsizerIntro = doc.splitTextToSize(downsizerIntro, pageWidth - 28);
        doc.text(wrappedDownsizerIntro, 14, yPos);
        yPos += wrappedDownsizerIntro.length * 5 + 10;

        // Downsizer example
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Example Downsizer Scenario:', 14, yPos);
        yPos += 7;

        const downsizerExample = [
            ['Home Sale Price', '$1,200,000'],
            ['Mortgage Payout', '$150,000'],
            ['Sale Costs (agent, legal)', '$35,000'],
            ['Net Proceeds', '$1,015,000'],
            ['Purchase New Home', '$650,000'],
            ['Available for Super', '$365,000'],
            ['Maximum Downsizer (couple)', '$600,000'],
            ['Amount to Super (this scenario)', '$300,000 (each spouse)'],
            ['Remaining Cash', '$65,000']
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, downsizerExample);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('This couple can each contribute $300,000, boosting combined super by $600,000 without affecting caps.', 18, yPos);
        yPos += 10;

        // ===== TOOL 7: WORK TEST CALCULATOR =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 7: Work Test Requirements (Ages 67-74)', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const workTestIntro = `For individuals aged 67-74, voluntary contributions (both concessional and non-concessional) are subject to a work test. To satisfy the work test, you must be gainfully employed for at least 40 hours within a consecutive 30-day period during the financial year in which the contribution is made. "Gainfully employed" means working for payment in any business, trade, profession, vocation, calling, occupation, or employment. From age 75, only mandated employer contributions (super guarantee) and downsizer contributions are permitted. This calculator helps determine eligibility and optimal contribution timing based on work patterns.`;
        const wrappedWorkTestIntro = doc.splitTextToSize(workTestIntro, pageWidth - 28);
        doc.text(wrappedWorkTestIntro, 14, yPos);
        yPos += wrappedWorkTestIntro.length * 5 + 10;

        // Work test scenarios
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Age', 18, yPos + 5);
        doc.text('Work Pattern', 45, yPos + 5);
        doc.text('Hours/Period', 100, yPos + 5);
        doc.text('Eligible?', 140, yPos + 5);
        doc.text('Strategy', 165, yPos + 5);
        yPos += 7;

        const workTestScenarios = [
            ['67', 'Part-time consulting', '10 hrs/week', 'YES', 'Contribute before June 30'],
            ['69', 'Casual work', '45 hrs in Nov', 'YES', 'Make contribution in same FY'],
            ['71', 'Semi-retired', '20 hrs/month', 'YES', 'Maximise while working'],
            ['73', 'Fully retired', '0 hrs', 'NO', 'Cannot make voluntary contribs'],
            ['75', 'Any status', 'N/A', 'NO', 'SG & downsizer only']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        workTestScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 20, yPos + 4);
            doc.text(row[1], 45, yPos + 4);
            doc.text(row[2], 100, yPos + 4);
            doc.setTextColor(row[3] === 'YES' ? 16 : 239, row[3] === 'YES' ? 185 : 68, row[3] === 'YES' ? 129 : 68);
            doc.setFont(undefined, 'bold');
            doc.text(row[3], 142, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            doc.text(row[4], 165, yPos + 4);
            yPos += 6;
        });
        yPos += 10;

        // ===== TOOL 8: PERSONAL DEDUCTIBLE CONTRIBUTIONS =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 8: Personal Deductible Contribution Tax Benefit', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const deductibleIntro = `Self-employed individuals and employees who don't receive employer contributions exceeding the concessional cap can claim a tax deduction for personal super contributions. This converts after-tax money into concessional contributions, providing an immediate tax deduction at your marginal tax rate (up to 47%) while the fund pays only 15% contributions tax. The net tax benefit can be up to 32%. This strategy is particularly valuable for high-income earners, business owners, and those with variable income who can make lump-sum contributions in high-income years.`;
        const wrappedDeductibleIntro = doc.splitTextToSize(deductibleIntro, pageWidth - 28);
        doc.text(wrappedDeductibleIntro, 14, yPos);
        yPos += wrappedDeductibleIntro.length * 5 + 10;

        // Tax benefit calculation table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Taxable Income', 18, yPos + 5);
        doc.text('Marginal Rate', 65, yPos + 5);
        doc.text('Contribution', 110, yPos + 5);
        doc.text('Tax Refund', 145, yPos + 5);
        doc.text('Net Benefit', 170, yPos + 5);
        yPos += 7;

        const deductibleScenarios = [
            ['$50,000', '32.5%', '$10,000', '$3,250', '$1,750'],
            ['$100,000', '37%', '$20,000', '$7,400', '$4,400'],
            ['$150,000', '45%', '$30,000', '$13,500', '$10,500'],
            ['$200,000', '47%', '$30,000', '$14,100', '$11,100']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        deductibleScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 70, yPos + 4);
            doc.text(row[2], 115, yPos + 4);
            doc.text(row[3], 148, yPos + 4);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(16, 185, 129);
            doc.text(row[4], 173, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 8;

        doc.setFontSize(7);
        doc.setFont(undefined, 'italic');
        doc.text('Net benefit = Tax refund (marginal rate) minus fund contributions tax (15%)', 18, yPos);
        yPos += 10;

        this.addInfoBox(doc, yPos,
            'IMPORTANT: You must lodge a "Notice of Intent to Claim a Deduction" (NAT 71121) with your fund BEFORE claiming the deduction ' +
            'in your tax return or before certain events (e.g., starting a pension). Failure to lodge the notice means you cannot claim the deduction.',
            'warning'
        );
        yPos += 28;

        // ===== INVESTMENT LIMITS & COMPLIANCE =====
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'INVESTMENT LIMITS & COMPLIANCE');
        yPos += 10;

        const inHouseAssets = smsfState?.smsfInHouseAssets || 0;
        const relatedPartyLoans = smsfState?.smsfRelatedPartyLoans || 0;
        const totalAssets = fundBalance;
        // inHousePercent already defined at top

        const complianceDetails = [
            ['Total Fund Assets', `$${Math.round(totalAssets).toLocaleString()}`],
            ['In-House Assets', `$${Math.round(inHouseAssets).toLocaleString()}`],
            ['In-House Assets %', `${inHousePercent.toFixed(2)}%`],
            ['Regulatory Limit', '5% of total assets'],
            ['Status', inHousePercent <= 5 ? 'COMPLIANT' : 'EXCEEDS LIMIT'],
            ['Related Party Loans', `$${Math.round(relatedPartyLoans).toLocaleString()}`]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, complianceDetails);
        yPos += 8;

        if (inHousePercent > 5) {
            this.addInfoBox(doc, yPos, 
                'WARNING: In-house assets exceed the 5% regulatory limit. The fund must reduce in-house assets to comply with SIS regulations.',
                'warning'
            );
            yPos += 20;
        }

        // ===== PROPERTY & LRBA =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'PROPERTY INVESTMENT & BORROWING');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const propertyIntro = `SMSFs are permitted to borrow to acquire a single acquirable asset (typically property) under a Limited Recourse Borrowing Arrangement (LRBA). The loan must be limited recourse (the lender can only claim against the acquired asset, not other fund assets) and on commercial terms. This allows funds to gain property exposure with leverage, potentially enhancing returns, though it also introduces additional risk and compliance requirements including higher fees and stricter reporting.`;
        const wrappedPropIntro = doc.splitTextToSize(propertyIntro, pageWidth - 28);
        doc.text(wrappedPropIntro, 14, yPos);
        yPos += wrappedPropIntro.length * 5 + 10;

        // Use values already calculated at top of function
        const propertyEquity = propertyValue - propertyLoan;
        const propertyLVR = propertyValue > 0 ? (propertyLoan / propertyValue) * 100 : 0;

        if (hasProperty || propertyValue > 0) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Property Holdings in This Scenario:', 14, yPos);
            yPos += 7;

            const propertyDetails = [
                ['Property Value', `$${Math.round(propertyValue).toLocaleString()}`],
                ['Outstanding Loan (LRBA)', `$${Math.round(propertyLoan).toLocaleString()}`],
                ['Equity in Property', `$${Math.round(propertyEquity).toLocaleString()}`],
                ['Loan-to-Value Ratio (LVR)', `${propertyLVR.toFixed(1)}%`],
                ['Interest Rate', `${(interestRate * 100).toFixed(2)}% p.a.`],
                ['Loan Term', `${loanTerm} years`],
                ['Monthly Loan Payment', `$${Math.round(monthlyPayment).toLocaleString()}`],
                ['Annual Loan Payments', `$${Math.round(annualPayment).toLocaleString()}`],
                ['Rental Yield', `${rentalYield}% p.a.`],
                ['Annual Rental Income', `$${Math.round(annualRental).toLocaleString()}`],
                ['Net Rental Income', `$${Math.round(netRentalIncome).toLocaleString()}`],
                ['Property as % of Fund', `${((propertyValue / fundBalance) * 100).toFixed(1)}%`]
            ];

            yPos = this.drawKeyValuePairs(doc, yPos, propertyDetails);
            yPos += 10;

            // Cash flow analysis
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('LRBA Cash Flow Analysis:', 14, yPos);
            yPos += 7;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            const cashFlowText = netRentalIncome >= 0 
                ? `This property generates positive cash flow of $${Math.round(netRentalIncome).toLocaleString()}/year after loan payments, contributing to fund liquidity.`
                : `This property has negative cash flow of $${Math.round(Math.abs(netRentalIncome)).toLocaleString()}/year, requiring fund reserves to cover the shortfall.`;
            const wrappedCashFlow = doc.splitTextToSize(cashFlowText, pageWidth - 28);
            doc.text(wrappedCashFlow, 14, yPos);
            yPos += wrappedCashFlow.length * 5 + 8;

            doc.setFont(undefined, 'italic');
            doc.setFontSize(8);
            doc.text(`Regulatory requirements: LVR must not exceed 80%, loan must be limited recourse with commercial terms.`, 14, yPos);
            yPos += 5;
            doc.text(`Property held on single acquirable asset basis. Insurance required. Professional valuation recommended annually.`, 14, yPos);
            yPos += 12;
        } else {
            doc.setFontSize(10);
            doc.setFont(undefined, 'italic');
            doc.text('This scenario does not include direct property holdings or LRBA arrangements.', 18, yPos);
            yPos += 6;
            doc.setFont(undefined, 'normal');
            doc.text('Note: Property investment via LRBA requires additional compliance, higher costs, and careful cash flow management.', 18, yPos);
            yPos += 12;
        }

        // ========================================
        // CATEGORY 3: PENSION & RETIREMENT INCOME (Tools 9-15)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'PENSION & RETIREMENT INCOME OPTIMIZATION');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const pensionCategoryIntro = `Moving from accumulation to pension phase represents a significant transition in SMSF management. Account-based pensions provide tax-free earnings on assets supporting the pension (0% vs 15% in accumulation), making this one of the most tax-effective retirement income strategies available. However, pensions come with minimum annual withdrawal requirements, Transfer Balance Cap restrictions ($${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M per person at current rates), and specific compliance obligations. This section analyzes pension optimization strategies, minimum withdrawal calculations, reversionary pension options, transition-to-retirement (TTR) strategies, and tax optimization for pension phase members.`;
        const wrappedPensionCatIntro = doc.splitTextToSize(pensionCategoryIntro, pageWidth - 28);
        doc.text(wrappedPensionCatIntro, 14, yPos);
        yPos += wrappedPensionCatIntro.length * 5 + 12;

        // ===== TOOL 9: MINIMUM PENSION WITHDRAWAL =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 9: Minimum Annual Pension Withdrawals', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const minPensionIntro = `Account-based pensions have legislated minimum withdrawal percentages based on the member's age at July 1. These minimums ensure pensions are used for retirement income, not indefinite tax-free accumulation. The percentage applies to the pension account balance at July 1 each year (or commencement balance for new pensions). Withdrawals can be taken as regular payments or lump sums, with no maximum limit (unlike transition-to-retirement pensions which are capped at 10%). All withdrawals are tax-free for members aged 60+.`;
        const wrappedMinPension = doc.splitTextToSize(minPensionIntro, pageWidth - 28);
        doc.text(wrappedMinPension, 14, yPos);
        yPos += wrappedMinPension.length * 5 + 10;

        // Minimum withdrawal rate table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Age', 18, yPos + 5);
        doc.text('Min %', 50, yPos + 5);
        doc.text('Example: $500k', 85, yPos + 5);
        doc.text('Example: $1M', 130, yPos + 5);
        doc.text('Example: $1.5M', 170, yPos + 5);
        yPos += 7;

        const minWithdrawalRates = [
            ['60-64', '4%', '$20,000', '$40,000', '$60,000'],
            ['65-74', '5%', '$25,000', '$50,000', '$75,000'],
            ['75-79', '6%', '$30,000', '$60,000', '$90,000'],
            ['80-84', '7%', '$35,000', '$70,000', '$105,000'],
            ['85-89', '9%', '$45,000', '$90,000', '$135,000'],
            ['90-94', '11%', '$55,000', '$110,000', '$165,000'],
            ['95+', '14%', '$70,000', '$140,000', '$210,000']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        minWithdrawalRates.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 52, yPos + 4);
            doc.text(row[2], 90, yPos + 4);
            doc.text(row[3], 135, yPos + 4);
            doc.text(row[4], 175, yPos + 4);
            yPos += 6;
        });
        yPos += 10;

        // Current member pension analysis (using johnTBR defined at top)
        const johnAge = members[0]?.age || 58;
        const johnBalance = members[0]?.balance || 1100000;
        
        if (johnTBR > 0) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Current Pension Phase Analysis (John Smith):', 14, yPos);
            yPos += 7;

            const johnMinRate = johnAge < 65 ? 0.04 : johnAge < 75 ? 0.05 : johnAge < 80 ? 0.06 : 0.07;
            const johnMinWithdrawal = johnTBR * johnMinRate;

            const johnPensionData = [
                ['Member', 'John Smith'],
                ['Age', `${johnAge}`],
                ['Pension Balance', `$${Math.round(johnTBR).toLocaleString()}`],
                ['Minimum Withdrawal %', `${(johnMinRate * 100).toFixed(0)}%`],
                ['Minimum Annual Withdrawal', `$${Math.round(johnMinWithdrawal).toLocaleString()}`],
                ['Monthly Minimum', `$${Math.round(johnMinWithdrawal / 12).toLocaleString()}`],
                ['Fortnightly Minimum', `$${Math.round(johnMinWithdrawal / 26).toLocaleString()}`]
            ];

            yPos = this.drawKeyValuePairs(doc, yPos, johnPensionData);
            yPos += 10;

            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.text(`This member must withdraw at least $${Math.round(johnMinWithdrawal).toLocaleString()}/year to maintain pension phase tax benefits.`, 18, yPos);
            yPos += 12;
        }

        // ===== TOOL 10: REVERSIONARY PENSION =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 10: Reversionary Pension Nominations', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const reversionaryIntro = `A reversionary pension automatically continues to a nominated reversionary beneficiary (typically spouse) upon the member's death, without cashing and restarting the pension. This provides continuity of tax-free pension phase benefits and may preserve the deceased's Transfer Balance Cap indexation. The reversionary beneficiary must be a dependant for tax purposes (spouse, child under 18, or financially dependent adult child). Non-reversionary pensions revert to accumulation phase on death, requiring the trustee to pay a death benefit which may have tax implications for non-dependent beneficiaries.`;
        const wrappedReversionary = doc.splitTextToSize(reversionaryIntro, pageWidth - 28);
        doc.text(wrappedReversionary, 14, yPos);
        yPos += wrappedReversionary.length * 5 + 10;

        const reversionaryEnabled = smsfState?.reversionaryPensionEnabled || false;
        const reversionaryAmount = smsfState?.reversionaryPensionAmount || 0;

        if (reversionaryEnabled) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Reversionary Pension in This Scenario:', 14, yPos);
            yPos += 7;

            const revData = [
                ['Status', 'Enabled'],
                ['Current Pension Balance', `$${Math.round(reversionaryAmount).toLocaleString()}`],
                ['Nominated Reversionary', 'Mary Smith (spouse)'],
                ['Reversionary Age', '56'],
                ['Tax-Free Component', `$${Math.round(smsfState?.taxFreeComponent || 0).toLocaleString()}`],
                ['Taxable Component', `$${Math.round(smsfState?.taxableComponent || 0).toLocaleString()}`],
                ['Tax on Reversal', '$0 (spouse beneficiary)']
            ];

            yPos = this.drawKeyValuePairs(doc, yPos, revData);
            yPos += 10;

            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.text('Upon death, Mary will continue receiving pension payments with 0% tax on earnings.', 18, yPos);
            yPos += 12;
        } else {
            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.text('This scenario does not have a reversionary pension nomination.', 18, yPos);
            yPos += 10;
        }

        this.addInfoBox(doc, yPos,
            'Reversionary pensions bypass the estate and provide immediate continuity. However, the reversionary beneficiary ' +
            'cannot commute the pension to a lump sum for 6 months. Consider comparing with non-reversionary pension plus ' +
            'binding death benefit nomination for greater flexibility.',
            'info'
        );
        yPos += 28;

        // ===== TOOL 11: TRANSITION TO RETIREMENT (TTR) =====
        doc.addPage();
        yPos = 20;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 11: Transition to Retirement (TTR) Strategy', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const ttrIntro = `Transition to Retirement (TTR) allows individuals who have reached preservation age (60 for most) to access their super as a non-commutable income stream while still working. This strategy enables salary sacrifice of pre-tax income into super while drawing an equivalent amount from the TTR pension, effectively converting taxed income into tax-advantaged super. Key restrictions: maximum 10% annual withdrawal, cannot convert to lump sum while working, and earnings on TTR assets are taxed at 15% (not 0% like regular pensions). This strategy can reduce tax and boost retirement savings simultaneously.`;
        const wrappedTTR = doc.splitTextToSize(ttrIntro, pageWidth - 28);
        doc.text(wrappedTTR, 14, yPos);
        yPos += wrappedTTR.length * 5 + 10;

        // TTR Example
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Example TTR Strategy (Age 62, Salary $120,000, Super $600,000):', 14, yPos);
        yPos += 7;

        const ttrScenario = [
            ['Current Salary', '$120,000'],
            ['Salary Sacrifice to Super', '$20,000'],
            ['Reduced Salary', '$100,000'],
            ['TTR Pension Withdrawal', '$20,000'],
            ['Net Cash Position', '$0 change'],
            ['Tax on Salary (before)', '$33,600'],
            ['Tax on Salary (after sacrifice)', '$25,200'],
            ['Tax Saved', '$8,400/year'],
            ['Additional Super Growth', '$20,000 - $3,000 (15% tax) = $17,000']
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, ttrScenario);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('Net benefit: $8,400 tax saving + $17,000 additional super = $25,400/year wealth creation', 18, yPos);
        yPos += 12;

        // ===== TOOL 12: TRANSFER BALANCE CAP MANAGEMENT =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 12: Transfer Balance Cap (TBC) Management', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const tbcIntro = `The Transfer Balance Cap ($${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M at current rates) limits the total amount that can be transferred into tax-free pension phase. Each person has their own cap which indexes if not fully utilized. Excess amounts must remain in accumulation phase (taxed at 15%) or be withdrawn from super. The cap applies to all retirement phase income streams combined, not per pension. Careful TBC management is critical for couples with large balances, as strategic pension timing can preserve indexation and maximize tax-free earnings. Commutations, pension commencements, and death benefit rollovers all affect the cap.`;
        const wrappedTBC = doc.splitTextToSize(tbcIntro, pageWidth - 28);
        doc.text(wrappedTBC, 14, yPos);
        yPos += wrappedTBC.length * 5 + 10;

        // TBC usage table for members
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Member', 18, yPos + 5);
        doc.text('Pension Balance', 70, yPos + 5);
        doc.text('TBC Used', 115, yPos + 5);
        doc.text('TBC Remaining', 150, yPos + 5);
        doc.text('Indexation', 180, yPos + 5);
        yPos += 7;

        members.forEach((member, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            
            const tbcCap = 1900000;
            const tbcUsed = member.tbr || 0;
            const tbcRemaining = tbcCap - tbcUsed;
            const hasIndexation = tbcUsed < tbcCap;
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            doc.text(member.name, 18, yPos + 4);
            doc.text(`$${Math.round(tbcUsed).toLocaleString()}`, 75, yPos + 4);
            doc.text(`$${Math.round(tbcUsed).toLocaleString()}`, 118, yPos + 4);
            doc.text(`$${Math.round(tbcRemaining).toLocaleString()}`, 153, yPos + 4);
            doc.setTextColor(hasIndexation ? 16 : 150, hasIndexation ? 185 : 150, hasIndexation ? 129 : 150);
            doc.text(hasIndexation ? 'Yes' : 'No', 183, yPos + 4);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
        });
        yPos += 12;

        // ========================================
        // CATEGORY 4: ESTATE PLANNING & SUCCESSION (Tools 13-17)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'ESTATE PLANNING & DEATH BENEFIT STRATEGIES');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const estateIntro = `Estate planning within an SMSF is complex because superannuation does not automatically form part of the deceased estate and is not controlled by a will. Instead, death benefits are distributed according to superannuation law, the fund's trust deed, and any valid death benefit nominations. The tax treatment varies dramatically based on who receives the benefit: spouses receive it tax-free, while adult non-dependent children may pay up to 17% tax on the taxable component. Proper estate planning requires understanding binding vs non-binding nominations, reversionary vs non-reversionary pensions, dependant definitions, tax-free vs taxable component optimization, and succession planning for the fund itself.`;
        const wrappedEstateIntro = doc.splitTextToSize(estateIntro, pageWidth - 28);
        doc.text(wrappedEstateIntro, 14, yPos);
        yPos += wrappedEstateIntro.length * 5 + 12;

        // ===== TOOL 13: BINDING DEATH BENEFIT NOMINATIONS =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 13: Binding Death Benefit Nominations', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const bdbIntro = `Binding death benefit nominations (BDBNs) legally direct the trustee to pay death benefits to specified beneficiaries in specified proportions. Unlike non-binding nominations (which are recommendations only), binding nominations remove trustee discretion if properly executed. Key requirements: must be in writing, signed by member and two witnesses (who are not beneficiaries), and nominate SIS-dependants or legal personal representative (LPR/estate). Non-lapsing BDBNs remain valid indefinitely unless revoked; lapsing BDBNs expire after 3 years and must be renewed. Proper nominations prevent family disputes and ensure benefits go to intended recipients.`;
        const wrappedBDB = doc.splitTextToSize(bdbIntro, pageWidth - 28);
        doc.text(wrappedBDB, 14, yPos);
        yPos += wrappedBDB.length * 5 + 10;

        const hasNominations = smsfState?.deathBenefitNominations || false;
        const estatebeneficiaries = smsfState?.beneficiaries || [];

        if (hasNominations && estatebeneficiaries.length > 0) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Current Beneficiary Nominations:', 14, yPos);
            yPos += 7;

            // Beneficiary table
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setFillColor(239, 246, 255);
            doc.rect(14, yPos, pageWidth - 28, 7, 'F');
            doc.text('Beneficiary', 18, yPos + 5);
            doc.text('Relationship', 70, yPos + 5);
            doc.text('Age', 115, yPos + 5);
            doc.text('Dependant?', 135, yPos + 5);
            doc.text('Allocation %', 170, yPos + 5);
            yPos += 7;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(8);
            estatebeneficiaries.forEach((beneficiary, index) => {
                if (index % 2 === 1) {
                    doc.setFillColor(248, 250, 252);
                    doc.rect(14, yPos, pageWidth - 28, 6, 'F');
                }
                doc.text(beneficiary.name || 'Not Named', 18, yPos + 4);
                doc.text(beneficiary.relationship || 'Not Specified', 70, yPos + 4);
                doc.text(`${beneficiary.age || 'N/A'}`, 118, yPos + 4);
                doc.setTextColor(beneficiary.isDependent ? 16 : 239, beneficiary.isDependent ? 185 : 68, beneficiary.isDependent ? 129 : 68);
                doc.text(beneficiary.isDependent ? 'Yes' : 'No', 140, yPos + 4);
                doc.setTextColor(0, 0, 0);
                doc.text(`${beneficiary.percentage || 0}%`, 175, yPos + 4);
                yPos += 6;
            });
            yPos += 8;

            doc.setFontSize(7);
            doc.setFont(undefined, 'italic');
            doc.text('Note: "Dependant" for tax purposes includes spouse, children <18, and financially dependent adult children.', 18, yPos);
            yPos += 10;
        } else {
            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.text('This scenario does not include specific death benefit nominations.', 18, yPos);
            yPos += 10;
        }

        // ===== TOOL 14: DEATH BENEFIT TAX CALCULATOR =====
        doc.addPage();
        yPos = 20;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 14: Death Benefit Tax Analysis & Optimization', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const dbTaxIntro = `Death benefit taxation is one of the most complex areas of superannuation law. The tax payable depends on three factors: (1) the beneficiary's relationship to the deceased, (2) whether they are a tax dependant, and (3) the proportion of tax-free vs taxable components. Spouses and financial dependants pay zero tax. Adult non-dependent children pay 17% (15% + 2% Medicare) on the taxable component only. Payments to the estate are taxed at the beneficiary's marginal rate when distributed. Strategic planning can significantly reduce tax by maximizing tax-free components through non-concessional contributions, optimizing who receives benefits, and using insurance bonds strategically.`;
        const wrappedDBTax = doc.splitTextToSize(dbTaxIntro, pageWidth - 28);
        doc.text(wrappedDBTax, 14, yPos);
        yPos += wrappedDBTax.length * 5 + 10;

        // Death benefit tax scenarios table
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Tax Analysis for John's $${Math.round(johnBalance).toLocaleString()} Balance:`, 14, yPos);
        yPos += 7;

        const pensionTaxFreeComp = smsfState?.taxFreeComponent || 650000;
        const pensionTaxableComp = smsfState?.taxableComponent || 450000;
        const totalBalance = pensionTaxFreeComp + pensionTaxableComp;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Beneficiary Type', 18, yPos + 5);
        doc.text('Tax-Free Comp', 80, yPos + 5);
        doc.text('Taxable Comp', 125, yPos + 5);
        doc.text('Tax Rate', 165, yPos + 5);
        doc.text('Tax Payable', 185, yPos + 5);
        yPos += 7;

        const dbTaxScenarios = [
            ['Spouse (Mary)', pensionTaxFreeComp, pensionTaxableComp, '0%', 0],
            ['Dependent child (<18)', pensionTaxFreeComp, pensionTaxableComp, '0%', 0],
            ['Adult child (non-dep)', pensionTaxFreeComp, pensionTaxableComp, '17%', pensionTaxableComp * 0.17],
            ['Estate → adult children', pensionTaxFreeComp, pensionTaxableComp, '17%', pensionTaxableComp * 0.17]
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        dbTaxScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(`$${Math.round(row[1]).toLocaleString()}`, 83, yPos + 4);
            doc.text(`$${Math.round(row[2]).toLocaleString()}`, 128, yPos + 4);
            doc.text(row[3], 168, yPos + 4);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(row[4] === 0 ? 16 : 239, row[4] === 0 ? 185 : 68, row[4] === 0 ? 129 : 68);
            doc.text(`$${Math.round(row[4]).toLocaleString()}`, 188, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(`STRATEGY: Nominating Mary (spouse) as beneficiary saves $${Math.round(pensionTaxableComp * 0.17).toLocaleString()} in death benefit tax!`, 18, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        // ===== TOOL 15: FUND SUCCESSION PLANNING =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 15: Fund Succession & Enduring Power of Attorney', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const successionIntro = `Fund succession planning addresses what happens to the SMSF itself when a member dies or loses capacity. For individual trustees, the death of a member means the fund may need to wind up unless a new eligible member (e.g., adult child) joins. Corporate trustees provide greater flexibility as directors can be replaced without affecting the fund structure. Enduring Power of Attorney (EPOA) allows a nominated person to act as trustee if you lose mental capacity. Without proper succession planning, the fund may face forced wind-up, asset fire sales, or costly legal disputes. This is particularly critical for single-member funds or where all members are of similar age.`;
        const wrappedSuccession = doc.splitTextToSize(successionIntro, pageWidth - 28);
        doc.text(wrappedSuccession, 14, yPos);
        yPos += wrappedSuccession.length * 5 + 10;

        const trusteeType = smsfData?.trusteeType || 'individual';
        const numMembers = members.length;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Current Fund Structure & Succession Risk:', 14, yPos);
        yPos += 7;

        const successionData = [
            ['Trustee Structure', trusteeType === 'individual' ? 'Individual Trustees' : 'Corporate Trustee'],
            ['Number of Members', `${numMembers}`],
            ['Youngest Member Age', `${Math.min(...members.map(m => m.age))}`],
            ['Succession Risk', trusteeType === 'individual' && numMembers <= 2 ? 'HIGHER' : 'LOWER'],
            ['EPOA in Place?', 'Not specified in this scenario'],
            ['Successor Member Identified?', numMembers >= 3 ? 'Yes (Sarah Smith age 28)' : 'Not specified']
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, successionData);
        yPos += 10;

        this.addInfoBox(doc, yPos,
            trusteeType === 'individual' 
                ? 'RECOMMENDATION: Consider establishing a corporate trustee ($1,500-$2,000 setup) for greater succession flexibility and continuity.'
                : 'Corporate trustee structure provides good succession flexibility. Ensure directorship succession is documented.',
            trusteeType === 'individual' ? 'warning' : 'info'
        );
        yPos += 25;

        // ========================================
        // CATEGORY 5: TAX OPTIMIZATION & PLANNING (Tools 16-22)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'TAX OPTIMIZATION & STRATEGIC PLANNING');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const taxCategoryIntro = `Tax optimization is one of the primary benefits of SMSFs, offering multiple levers to minimize tax and maximize wealth creation. SMSFs enjoy concessional tax treatment compared to personal tax rates: accumulation phase earnings taxed at 15% (vs up to 47% personal), capital gains on assets held 12+ months taxed at 10%, and pension phase earnings tax-free (0%). Strategic tax planning involves optimizing accumulation vs pension phase balances, maximizing tax-free components, timing capital gains, managing franking credits, utilizing losses, and coordinating with personal tax positions. This section analyzes tax strategies across different scenarios to illustrate potential savings and optimization opportunities.`;
        const wrappedTaxCat = doc.splitTextToSize(taxCategoryIntro, pageWidth - 28);
        doc.text(wrappedTaxCat, 14, yPos);
        yPos += wrappedTaxCat.length * 5 + 12;

        // ===== TOOL 16: ACCUMULATION VS PENSION PHASE TAX =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 16: Accumulation vs Pension Phase Tax Comparison', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const accVsPensionIntro = `The tax difference between accumulation and pension phase is substantial. Accumulation phase: earnings taxed at 15%, capital gains at 10% (if held 12+ months). Pension phase: 0% tax on all earnings and capital gains. For a $1M balance earning 7% annually, this difference equals $10,500/year in tax savings ($70,000 earnings × 15% = $10,500 saved). Over 20 years of retirement, this compounds to over $400,000 in additional wealth. This makes maximizing pension phase balances (within Transfer Balance Cap limits) one of the most powerful wealth creation strategies available.`;
        const wrappedAccVsPension = doc.splitTextToSize(accVsPensionIntro, pageWidth - 28);
        doc.text(wrappedAccVsPension, 14, yPos);
        yPos += wrappedAccVsPension.length * 5 + 10;

        // Tax comparison table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Balance', 18, yPos + 5);
        doc.text('Annual Return', 60, yPos + 5);
        doc.text('Earnings', 105, yPos + 5);
        doc.text('Accum Tax (15%)', 140, yPos + 5);
        doc.text('Pension Tax (0%)', 175, yPos + 5);
        yPos += 7;

        const taxComparisonData = [
            [500000, 0.07, 35000],
            [1000000, 0.07, 70000],
            [1500000, 0.07, 105000],
            [1900000, 0.07, 133000]
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        taxComparisonData.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            const balance = row[0];
            const returns = row[1];
            const earnings = row[2];
            const accumTax = earnings * 0.15;
            
            doc.text(`$${Math.round(balance / 1000)}k`, 18, yPos + 4);
            doc.text(`${(returns * 100).toFixed(0)}%`, 70, yPos + 4);
            doc.text(`$${Math.round(earnings).toLocaleString()}`, 108, yPos + 4);
            doc.setTextColor(239, 68, 68);
            doc.text(`$${Math.round(accumTax).toLocaleString()}`, 148, yPos + 4);
            doc.setTextColor(16, 185, 129);
            doc.setFont(undefined, 'bold');
            doc.text('$0', 183, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(`Pension-phase earnings on assets up to the Transfer Balance Cap ($${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M) are taxed at 0% vs 15% in accumulation — model the dollar impact in the calculator.`, 18, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        // ===== TOOL 17: CAPITAL GAINS TAX OPTIMIZATION =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 17: Capital Gains Tax (CGT) Optimization Strategies', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const cgtIntro = `Capital gains in SMSFs receive preferential treatment: assets held for 12+ months qualify for the 1/3 CGT discount, effectively reducing the tax rate from 15% to 10%. For pension phase assets, capital gains are entirely tax-free regardless of holding period. Strategic CGT planning involves timing asset sales for maximum tax efficiency, utilizing capital losses to offset gains, segregating pension vs accumulation assets, and coordinating rebalancing with pension commencements. For a $500,000 capital gain, the difference between short-term (15%) and long-term (10%) is $25,000 in tax - and pension phase saves the full $50,000.`;
        const wrappedCGT = doc.splitTextToSize(cgtIntro, pageWidth - 28);
        doc.text(wrappedCGT, 14, yPos);
        yPos += wrappedCGT.length * 5 + 10;

        // CGT scenarios table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Scenario', 18, yPos + 5);
        doc.text('Capital Gain', 75, yPos + 5);
        doc.text('Holding Period', 120, yPos + 5);
        doc.text('Tax Rate', 160, yPos + 5);
        doc.text('Tax Payable', 180, yPos + 5);
        yPos += 7;

        const cgtScenarios = [
            ['Accumulation, <12 months', 100000, '10 months', '15%', 15000],
            ['Accumulation, >12 months', 100000, '18 months', '10%', 10000],
            ['Pension phase, <12 months', 100000, '10 months', '0%', 0],
            ['Pension phase, >12 months', 100000, '18 months', '0%', 0]
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        cgtScenarios.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(`$${Math.round(row[1]).toLocaleString()}`, 78, yPos + 4);
            doc.text(row[2], 123, yPos + 4);
            doc.text(row[3], 163, yPos + 4);
            doc.setTextColor(row[4] === 0 ? 16 : 239, row[4] === 0 ? 185 : 68, row[4] === 0 ? 129 : 68);
            doc.setFont(undefined, 'bold');
            doc.text(`$${Math.round(row[4]).toLocaleString()}`, 183, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text('STRATEGY: Defer asset sales until pension phase to save $15,000 tax per $100k gain!', 18, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        // ===== TOOL 18: FRANKING CREDITS & DIVIDEND STRATEGY =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 18: Franking Credits & Dividend Income Optimization', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const frankingIntro = `Franking credits represent company tax already paid on dividends from Australian shares. SMSFs can claim franking credits as refunds if the fund's tax rate is below 30%. In accumulation phase (15% tax rate), excess franking credits are refunded. In pension phase (0% tax rate), ALL franking credits are refunded, providing additional income. For a portfolio with $500,000 Australian shares yielding 4% fully franked dividends, the annual franking credit refund in pension phase is approximately $8,571. This makes Australian dividend-paying shares particularly attractive for SMSF pension accounts.`;
        const wrappedFranking = doc.splitTextToSize(frankingIntro, pageWidth - 28);
        doc.text(wrappedFranking, 14, yPos);
        yPos += wrappedFranking.length * 5 + 10;

        // Franking credit example
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Description', 18, yPos + 5);
        doc.text('Accumulation', 90, yPos + 5);
        doc.text('Pension Phase', 145, yPos + 5);
        doc.text('Benefit', 180, yPos + 5);
        yPos += 7;

        const frankingExample = [
            ['Australian Shares', '$500,000', '$500,000', '-'],
            ['Dividend Yield (4%)', '$20,000', '$20,000', '-'],
            ['Franking Credits (30%)', '$8,571', '$8,571', '-'],
            ['Gross Dividend', '$28,571', '$28,571', '-'],
            ['Fund Tax', '$4,286 (15%)', '$0 (0%)', '-'],
            ['Franking Credit Refund', '$4,286', '$8,571', '+$4,286']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        frankingExample.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 95, yPos + 4);
            doc.text(row[2], 150, yPos + 4);
            if (row[3] !== '-') {
                doc.setTextColor(16, 185, 129);
                doc.setFont(undefined, 'bold');
                doc.text(row[3], 183, yPos + 4);
                doc.setTextColor(0, 0, 0);
                doc.setFont(undefined, 'normal');
            }
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text('Extra $4,286/year from franking in pension phase = $85,720 over 20 years!', 18, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 15;

        // Current fund tax position summary
        const totalComponents = pensionTaxFreeComp + pensionTaxableComp;
        const taxFreePercent = totalComponents > 0 ? (pensionTaxFreeComp / totalComponents) * 100 : 0;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Current Fund Tax Position Summary:', 14, yPos);
        yPos += 7;

        const taxDetails = [
            ['Total Fund Balance', `$${Math.round(fundBalance).toLocaleString()}`],
            ['Pension Phase Balance', `$${Math.round(johnTBR).toLocaleString()}`],
            ['Accumulation Balance', `$${Math.round(fundBalance - johnTBR).toLocaleString()}`],
            ['Tax-Free Component (Total)', `$${Math.round(pensionTaxFreeComp).toLocaleString()}`],
            ['Taxable Component (Total)', `$${Math.round(pensionTaxableComp).toLocaleString()}`],
            ['Tax-Free Percentage', `${taxFreePercent.toFixed(1)}%`],
            ['Estimated Annual Fund Tax', `$${Math.round((fundBalance - johnTBR) * 0.07 * 0.15).toLocaleString()}`]
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, taxDetails);
        yPos += 12;

        // ========================================
        // CATEGORY 6: INVESTMENT ANALYSIS & MONITORING (Tools 19-25)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'INVESTMENT ANALYSIS & PERFORMANCE MONITORING');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const investmentCatIntro = `Effective investment management is the core responsibility of SMSF trustees. Every SMSF must maintain a documented investment strategy addressing risk, return, diversification, liquidity, and the ability to pay benefits when due. Beyond the legal requirement, sophisticated investment analysis helps optimize returns, manage risk, benchmark performance against alternatives, and make informed rebalancing decisions. This section covers investment strategy documentation, performance benchmarking, rebalancing strategies, diversification analysis, liquidity management, and insurance considerations - all critical for long-term fund success.`;
        const wrappedInvestCat = doc.splitTextToSize(investmentCatIntro, pageWidth - 28);
        doc.text(wrappedInvestCat, 14, yPos);
        yPos += wrappedInvestCat.length * 5 + 12;

        // ===== TOOL 19: INVESTMENT STRATEGY DOCUMENTATION =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 19: Investment Strategy Review & Documentation', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const investStratIntro = `SIS Regulation 4.09 mandates that SMSF trustees formulate, review, and give effect to an investment strategy. The strategy must consider: (1) risk and return objectives, (2) diversification, (3) liquidity requirements, (4) ability to discharge liabilities, and (5) whether to hold insurance. Review is required annually or when circumstances change significantly (e.g., member retirement, large contribution, market crash). Failure to maintain a documented, reviewed strategy can result in ATO penalties, trustee liability, and potential fund wind-up. The strategy should be tailored to member demographics, not generic.`;
        const wrappedInvestStrat = doc.splitTextToSize(investStratIntro, pageWidth - 28);
        doc.text(wrappedInvestStrat, 14, yPos);
        yPos += wrappedInvestStrat.length * 5 + 10;

        const riskProfile = memberProfile?.riskTolerance || 'MEDIUM';
        const timeHorizon = memberProfile?.timeHorizon || 20;
        const hasLRBA = smsfData?.hasLRBA || false;

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Current Investment Strategy Parameters:', 14, yPos);
        yPos += 7;

        const investmentDetails = [
            ['Risk Profile', riskProfile],
            ['Investment Time Horizon', `${timeHorizon} years`],
            ['Property Holdings (LRBA)', hasLRBA ? 'Yes ($850k direct property)' : 'No'],
            ['Collectibles', `Yes ($250k vintage cars, art, wine)`],
            ['Target Return (p.a.)', '7.5% (above CPI + 5%)'],
            ['Strategy Review Date', 'Annual (last: June 2024)'],
            ['Primary Objective', 'Balance growth and capital preservation'],
            ['Liquidity Reserve Target', '$50,000 (2% of fund)']
        ];

        yPos = this.drawKeyValuePairs(doc, yPos, investmentDetails);
        yPos += 10;

        // ===== TOOL 20: PERFORMANCE BENCHMARKING =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 20: Performance Benchmarking & Comparison', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const perfBenchIntro = `Benchmarking fund performance helps trustees assess whether their investment strategy is effective. Comparisons should be made against: (1) target return objectives, (2) relevant market indices weighted by asset allocation, (3) industry super funds with similar risk profiles, and (4) retail funds with comparable strategies. A balanced SMSF (50/30/20 shares/fixed/cash) should benchmark against a similar mix of ASX 200, international indices, and bond indices. Underperformance may indicate high fees, poor asset selection, excessive cash drag, or inappropriate strategy for member demographics.`;
        const wrappedPerfBench = doc.splitTextToSize(perfBenchIntro, pageWidth - 28);
        doc.text(wrappedPerfBench, 14, yPos);
        yPos += wrappedPerfBench.length * 5 + 10;

        // Performance comparison table (illustrative)
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Period', 18, yPos + 5);
        doc.text('This Fund', 60, yPos + 5);
        doc.text('Target', 95, yPos + 5);
        doc.text('Balanced Index', 125, yPos + 5);
        doc.text('Industry Avg', 165, yPos + 5);
        doc.text('Status', 185, yPos + 5);
        yPos += 7;

        const performanceData = [
            ['1 Year', '8.2%', '7.5%', '7.8%', '7.1%', 'Above'],
            ['3 Year (p.a.)', '7.6%', '7.5%', '7.4%', '6.9%', 'Above'],
            ['5 Year (p.a.)', '7.1%', '7.5%', '7.2%', '6.8%', 'On Target'],
            ['10 Year (p.a.)', '7.9%', '7.5%', '7.6%', '7.0%', 'Above']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        performanceData.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.setFont(undefined, 'bold');
            doc.text(row[1], 65, yPos + 4);
            doc.setFont(undefined, 'normal');
            doc.text(row[2], 98, yPos + 4);
            doc.text(row[3], 132, yPos + 4);
            doc.text(row[4], 170, yPos + 4);
            doc.setTextColor(row[5] === 'Above' ? 16 : 59, row[5] === 'Above' ? 185 : 130, row[5] === 'Above' ? 129 : 246);
            doc.text(row[5], 188, yPos + 4);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text('Fund performance meets or exceeds targets across all periods. Strong performance relative to industry averages.', 18, yPos);
        yPos += 15;

        // ===== TOOL 21: ASSET REBALANCING ANALYSIS =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 21: Asset Rebalancing & Drift Analysis', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const rebalanceIntro = `Asset allocation drift occurs when market movements cause the portfolio to deviate from target allocations. For example, if shares outperform bonds, the portfolio becomes more aggressive than intended, increasing risk. Rebalancing back to targets maintains the desired risk profile and can enhance returns through disciplined "buy low, sell high" execution. Best practice is to rebalance when allocations drift beyond ±5% of targets, or annually. SMSFs have an advantage: rebalancing within pension phase incurs zero CGT, unlike personal portfolios. This tool helps identify when rebalancing is warranted and calculates the trades required.`;
        const wrappedRebalance = doc.splitTextToSize(rebalanceIntro, pageWidth - 28);
        doc.text(wrappedRebalance, 14, yPos);
        yPos += wrappedRebalance.length * 5 + 10;

        // Rebalancing analysis table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Asset Class', 18, yPos + 5);
        doc.text('Target %', 75, yPos + 5);
        doc.text('Current %', 110, yPos + 5);
        doc.text('Drift', 145, yPos + 5);
        doc.text('Action Needed', 165, yPos + 5);
        yPos += 7;

        const rebalanceData = [
            ['Australian Shares', 38, 42, +4, 'Trim $98k'],
            ['International Shares', 30, 28, -2, 'Add $49k'],
            ['Property/Direct', 20, 20, 0, 'Hold'],
            ['Collectibles', 7, 10, +3, 'Above target'],
            ['Fixed Income', 10, 8, -2, 'Add $49k'],
            ['Cash', 5, 2, -3, 'Add $73k']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        rebalanceData.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(`${row[1]}%`, 80, yPos + 4);
            doc.setFont(undefined, 'bold');
            doc.text(`${row[2]}%`, 115, yPos + 4);
            doc.setFont(undefined, 'normal');
            const driftColor = Math.abs(row[3]) >= 5 ? [239, 68, 68] : Math.abs(row[3]) >= 3 ? [245, 158, 11] : [100, 100, 100];
            doc.setTextColor(...driftColor);
            doc.text(`${row[3] > 0 ? '+' : ''}${row[3]}%`, 148, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.text(row[4], 168, yPos + 4);
            yPos += 6;
        });
        yPos += 10;

        this.addInfoBox(doc, yPos,
            'REBALANCING TRIGGER: Collectibles at 10% (target 7%) and Australian Shares at 42% (target 38%) suggest rebalancing. ' +
            'Consider selling $98k shares and reducing collectible exposure when prudent. Rebalance within pension phase to avoid CGT.',
            'warning'
        );
        yPos += 28;

        // ===== COMPLIANCE CHECKLIST =====
        if (yPos > pageHeight - 120) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'COMPLIANCE CHECKLIST');
        yPos += 10;

        const complianceChecklist = [
            ['Investment Strategy', 'Must be documented and reviewed annually'],
            ['Annual Return', 'Lodge by 28 February (31 Oct if using tax agent)'],
            ['Audit', 'Independent audit required annually'],
            ['Contribution Caps', 'Monitor concessional ($30k) and non-concessional ($120k)'],
            ['In-House Assets', 'Must not exceed 5% of total assets'],
            ['LRBA Compliance', 'Loans must be limited recourse and commercial terms'],
            ['Sole Purpose Test', 'Fund maintained for retirement benefits only'],
            ['Arm\'s Length', 'All transactions at market rates'],
            ['Member Statements', 'Provide annually to all members'],
            ['Trustee Minutes', 'Document all investment and administrative decisions']
        ];

        complianceChecklist.forEach(([item, requirement]) => {
            if (yPos > pageHeight - 20) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`• ${item}`, 14, yPos);
            yPos += 5;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            const wrappedText = doc.splitTextToSize(`  ${requirement}`, pageWidth - 30);
            doc.text(wrappedText, 18, yPos);
            yPos += wrappedText.length * 4 + 4;
        });
        yPos += 10;

        // ===== KEY ACTIONS & CONSIDERATIONS =====
        if (yPos > pageHeight - 80) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'KEY ACTIONS & CONSIDERATIONS');
        yPos += 10;

        const recommendations = [
            'Review investment strategy annually and document changes',
            'Monitor contribution caps to avoid excess contributions tax',
            'Ensure in-house assets remain below 5% regulatory limit',
            'Maintain proper documentation for all trustee decisions',
            'Lodge annual return and complete audit before deadlines',
            'Review death benefit nominations annually',
            'Ensure all transactions are at arm\'s length with proper documentation',
            'Consider pension phase conversion when members reach 60-65',
            'Regular fee review - ensure SMSF remains cost-effective for fund size',
            'Estate planning - update nominations after life changes'
        ];

        recommendations.forEach((rec, index) => {
            if (yPos > pageHeight - 15) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`${index + 1}. ${rec}`, 14, yPos);
            yPos += 7;
        });
        yPos += 10;

        // ===== IMPORTANT NOTES =====
        if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = 20;
        }

        this.addInfoBox(doc, yPos,
            'COMPREHENSIVE ANALYSIS NOTE: This report consolidates data across all 36 specialized SMSF tools covering 8 categories: ' +
            'Contribution Planning, Investment & Compliance, Pension Optimization, Tax Planning, Estate Planning, Advanced Structuring, ' +
            'Strategic Analysis, and Administration. All figures are illustrative scenarios for educational purposes only.',
            'info'
        );
        yPos += 30;

        // ========================================
        // CATEGORY 7: FEE OPTIMIZATION & COST ANALYSIS (Tools 22-24)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'FEE OPTIMIZATION & COST-BENEFIT ANALYSIS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const feeCategoryIntro = `SMSF costs are largely fixed (audit, accounting, admin), making them cost-effective for balances above $200,000-$500,000 but potentially expensive for smaller funds. Unlike retail super which charges percentage-based fees (typically 0.5%-2.5% annually), SMSF costs don't increase with balance, creating significant savings for larger funds. However, SMSFs require substantial time commitment (20-50 hours/year) for administration, compliance, and investment management. This section provides detailed cost analysis, breakeven calculations comparing SMSF vs retail/industry funds, and long-term cost projections to help assess whether an SMSF remains cost-effective.`;
        const wrappedFeeCat = doc.splitTextToSize(feeCategoryIntro, pageWidth - 28);
        doc.text(wrappedFeeCat, 14, yPos);
        yPos += wrappedFeeCat.length * 5 + 12;

        // ===== TOOL 22: DETAILED FEE BREAKDOWN =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 22: Comprehensive Fee Breakdown & Analysis', 14, yPos);
        yPos += 8;

        // Annual fee table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Fee Type', 18, yPos + 5);
        doc.text('Annual Cost', 80, yPos + 5);
        doc.text('% of Fund', 125, yPos + 5);
        doc.text('Notes', 160, yPos + 5);
        yPos += 7;

        const auditFees = smsfState?.smsfAuditFees || 2200;
        const adminFees = smsfState?.smsfAdminFees || 3500;
        const accountingFees = smsfState?.smsfAccountingFees || 4200;
        const asicFee = 76;
        const insuranceFees = 1200; // Example insurance
        const advisoryFees = 2500; // Example
        const totalAnnualFees = auditFees + adminFees + accountingFees + asicFee + insuranceFees + advisoryFees;

        const feeBreakdown = [
            ['Annual Audit', auditFees, 'Independent SMSF auditor'],
            ['Accounting & Tax Return', accountingFees, 'Financial statements + tax'],
            ['Administration', adminFees, 'SMSF admin service'],
            ['ASIC Annual Review', asicFee, 'Regulatory fee'],
            ['Insurance Premiums', insuranceFees, 'Life/TPD for members'],
            ['Investment Advisory', advisoryFees, 'Optional - strategy advice']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        feeBreakdown.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(`$${Math.round(row[1]).toLocaleString()}`, 83, yPos + 4);
            doc.text(`${((row[1] / fundBalance) * 100).toFixed(3)}%`, 128, yPos + 4);
            doc.text(row[2], 160, yPos + 4);
            yPos += 6;
        });

        // Total row
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 6, 'F');
        doc.text('TOTAL ANNUAL FEES', 18, yPos + 4);
        doc.text(`$${Math.round(totalAnnualFees).toLocaleString()}`, 83, yPos + 4);
        doc.text(`${((totalAnnualFees / fundBalance) * 100).toFixed(2)}%`, 128, yPos + 4);
        yPos += 12;

        // ===== TOOL 23: SMSF VS RETAIL SUPER BREAKEVEN =====
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 23: SMSF vs Retail Super Cost Comparison & Breakeven', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const costCompIntro = `SMSF fees are largely fixed, while retail/industry super fees are percentage-based. This creates a crossover point: below certain balances, retail super is cheaper; above it, SMSFs save money. The breakeven point depends on specific fee structures, but typically falls between $200,000-$500,000. For this fund with $${Math.round(fundBalance / 1000)}k balance, the annual savings vs retail super (assuming 1.5% total fees) is approximately $${Math.round((fundBalance * 0.015) - totalAnnualFees).toLocaleString()}/year. Over 20 years, this compounds to substantial wealth preservation.`;
        const wrappedCostComp = doc.splitTextToSize(costCompIntro, pageWidth - 28);
        doc.text(wrappedCostComp, 14, yPos);
        yPos += wrappedCostComp.length * 5 + 10;

        // Cost comparison table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Balance', 18, yPos + 5);
        doc.text('SMSF Fees', 65, yPos + 5);
        doc.text('Retail (1.5%)', 110, yPos + 5);
        doc.text('Difference', 150, yPos + 5);
        doc.text('20-Yr Impact', 180, yPos + 5);
        yPos += 7;

        const balanceScenarios = [200000, 500000, 1000000, 1500000, 2000000, 2450000];
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        balanceScenarios.forEach((balance, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            const smsfCost = totalAnnualFees;
            const retailCost = balance * 0.015;
            const diff = retailCost - smsfCost;
            const twentyYr = diff * 20 * 1.4; // Rough compound effect
            
            doc.text(`$${Math.round(balance / 1000)}k`, 18, yPos + 4);
            doc.text(`$${Math.round(smsfCost).toLocaleString()}`, 68, yPos + 4);
            doc.text(`$${Math.round(retailCost).toLocaleString()}`, 113, yPos + 4);
            doc.setTextColor(diff > 0 ? 16 : 239, diff > 0 ? 185 : 68, diff > 0 ? 129 : 68);
            doc.setFont(undefined, 'bold');
            doc.text(`${diff > 0 ? '+' : ''}$${Math.round(diff).toLocaleString()}`, 153, yPos + 4);
            doc.text(`${diff > 0 ? '+' : ''}$${Math.round(twentyYr / 1000)}k`, 183, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(`At $2.45M balance: SMSF saves $${Math.round((fundBalance * 0.015) - totalAnnualFees).toLocaleString()}/year = $${Math.round((((fundBalance * 0.015) - totalAnnualFees) * 20 * 1.4) / 1000)}k over 20 years!`, 18, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 15;

        // ========================================
        // CATEGORY 8: COMPLIANCE & ADMINISTRATION (Tools 25-30)
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'COMPLIANCE & ADMINISTRATIVE REQUIREMENTS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const adminIntro = `SMSFs face significant compliance obligations under the Superannuation Industry (Supervision) Act and Regulations. Trustees are personally liable for breaches, which can result in penalties, fund disqualification, and potential criminal charges for serious contraventions. Key compliance areas include: annual financial statements and audit, annual return lodgement with ATO, investment strategy reviews, trustee declarations, member benefit separation, arms-length transactions, and sole purpose test adherence. This section outlines the annual compliance calendar, key deadlines, documentation requirements, and common pitfalls to avoid.`;
        const wrappedAdmin = doc.splitTextToSize(adminIntro, pageWidth - 28);
        doc.text(wrappedAdmin, 14, yPos);
        yPos += wrappedAdmin.length * 5 + 12;

        // ===== TOOL 25: ANNUAL COMPLIANCE CALENDAR =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Tool 25: Annual Compliance Calendar & Key Deadlines', 14, yPos);
        yPos += 8;

        // Compliance calendar table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Deadline', 18, yPos + 5);
        doc.text('Requirement', 60, yPos + 5);
        doc.text('Responsibility', 125, yPos + 5);
        doc.text('Penalty if Missed', 165, yPos + 5);
        yPos += 7;

        const complianceCalendar = [
            ['31 Oct', 'Financial Statements', 'Accountant', 'ATO penalties'],
            ['31 Oct', 'Annual Audit', 'Auditor', 'Fund disqualification'],
            ['28 Feb', 'Annual Return (SAR)', 'Trustee/Accountant', '$1,110+ penalty'],
            ['30 Jun', 'Investment Strategy Review', 'Trustees', 'Trustee liability'],
            ['Ongoing', 'Member Separation', 'Trustees', 'Loss of complying status'],
            ['Ongoing', 'Sole Purpose Test', 'Trustees', 'Fund disqualification'],
            ['30 Jun', 'In-House Assets <5%', 'Trustees', 'Excess tax + penalties'],
            ['Ongoing', 'Arms-Length Transactions', 'Trustees', 'Market value penalties']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        complianceCalendar.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            doc.text(row[1], 60, yPos + 4);
            doc.text(row[2], 125, yPos + 4);
            doc.setTextColor(239, 68, 68);
            doc.text(row[3], 168, yPos + 4);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
        });
        yPos += 12;

        // ========================================
        // COMPLIANCE STATUS DASHBOARD
        // ========================================
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'COMPLIANCE STATUS DASHBOARD - CURRENT YEAR');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const complianceIntro = `This dashboard provides a quick visual overview of key compliance requirements. All SMSFs must maintain compliance with SIS Act regulations, including investment restrictions, reporting requirements, and trustee obligations.`;
        const wrappedCompIntro = doc.splitTextToSize(complianceIntro, pageWidth - 28);
        doc.text(wrappedCompIntro, 14, yPos);
        yPos += wrappedCompIntro.length * 5 + 10;

        // Compliance Items with Visual Indicators
        const complianceItems = [
            { item: 'Annual Financial Statements Prepared', status: 'compliant', note: 'Due by 31 October' },
            { item: 'Annual Audit Completed', status: 'compliant', note: 'Independent auditor required' },
            { item: 'Annual Return Lodged with ATO', status: 'compliant', note: 'Due by 28 February' },
            { item: 'Investment Strategy Reviewed', status: 'compliant', note: 'Required annually' },
            { item: 'In-House Assets < 5% Limit', status: inHousePercent <= 5 ? 'compliant' : 'warning', note: `Currently ${inHousePercent.toFixed(1)}%` },
            { item: 'LRBA on Commercial Terms', status: hasProperty ? 'compliant' : 'na', note: hasProperty ? 'LVR within limits' : 'Not applicable' },
            { item: 'Member Benefits Separated', status: 'compliant', note: 'Required for 2+ members' },
            { item: 'Insurance Coverage Review', status: 'review', note: 'Annual review recommended' }
        ];

        // Draw compliance table with visual indicators
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Compliance Item', 18, yPos + 5);
        doc.text('Status', 120, yPos + 5);
        doc.text('Notes', 145, yPos + 5);
        yPos += 7;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        complianceItems.forEach((item, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            }

            // Item text
            doc.setTextColor(0, 0, 0);
            doc.text(item.item, 18, yPos + 5);

            // Status indicator (colored circle + text)
            if (item.status === 'compliant') {
                doc.setFillColor(16, 185, 129); // Green
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(16, 185, 129);
                doc.text('✓ Compliant', 128, yPos + 5);
            } else if (item.status === 'warning') {
                doc.setFillColor(245, 158, 11); // Amber
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(245, 158, 11);
                doc.text('⚠ Review', 128, yPos + 5);
            } else if (item.status === 'review') {
                doc.setFillColor(59, 130, 246); // Blue
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(59, 130, 246);
                doc.text('→ Action', 128, yPos + 5);
            } else {
                doc.setTextColor(150, 150, 150);
                doc.text('N/A', 128, yPos + 5);
            }

            // Notes
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(7);
            doc.text(item.note, 145, yPos + 5);
            doc.setFontSize(8);

            yPos += 8;
        });
        yPos += 15;

        // ========================================
        // EXECUTIVE SUMMARY OF KEY FINDINGS
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'EXECUTIVE SUMMARY OF KEY FINDINGS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const findingsIntro = `This comprehensive analysis has examined ${members.length} member fund with $${Math.round(fundBalance).toLocaleString()} in assets across ${totalCF > 0 ? 'contribution optimization, ' : ''}property investment${hasCollectibles ? ', collectibles management' : ''}, pension strategies, estate planning, tax optimization, and compliance management. The following key findings and recommendations emerge from this analysis:`;
        const wrappedFindings = doc.splitTextToSize(findingsIntro, pageWidth - 28);
        doc.text(wrappedFindings, 14, yPos);
        yPos += wrappedFindings.length * 5 + 12;

        // Key Financial Metrics Summary
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Key Financial Metrics:', 14, yPos);
        yPos += 7;

        const keyFindings = [
            ['Total Fund Balance', `$${Math.round(fundBalance).toLocaleString()}`, 'Strong asset base'],
            ['Annual Cost to Operate', `$${Math.round(totalAnnualFees).toLocaleString()}`, `${((totalAnnualFees/fundBalance)*100).toFixed(2)}% of fund`],
            ['Cost Advantage vs Retail', `+$${Math.round((fundBalance * 0.015) - totalAnnualFees).toLocaleString()}/year`, '20-year savings: $' + Math.round((((fundBalance * 0.015) - totalAnnualFees) * 28) / 1000) + 'k'],
            ['Pension Phase Assets', `$${Math.round(johnTBR).toLocaleString()}`, `Tax-free earnings`],
            ['Annual Tax Saved (Pension)', `$${Math.round(johnTBR * 0.07 * 0.15).toLocaleString()}`, 'vs accumulation phase'],
            ['Available Contribution Space', `$${(29500 + (120000 - nonConcessUsed)).toLocaleString()}`, 'Carry-forward + non-conc'],
            ['Property Cash Flow', netRentalIncome >= 0 ? '+$' + Math.round(netRentalIncome).toLocaleString() : '-$' + Math.round(Math.abs(netRentalIncome)).toLocaleString(), netRentalIncome >= 0 ? 'Positive' : 'Requires reserves']
        ];

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        keyFindings.forEach((row, index) => {
            doc.text(`• ${row[0]}: `, 18, yPos);
            doc.setFont(undefined, 'bold');
            doc.text(row[1], 90, yPos);
            doc.setFont(undefined, 'italic');
            doc.setFontSize(8);
            doc.text(`(${row[2]})`, 145, yPos);
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            yPos += 6;
        });
        yPos += 12;

        // ========================================
        // RECOMMENDED PRIORITY ACTIONS
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'RECOMMENDED PRIORITY ACTIONS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const actionsIntro = `Based on this comprehensive analysis, the following actions are recommended in priority order. These recommendations are educational scenarios only and should be reviewed with licensed financial and legal professionals before implementation:`;
        const wrappedActions = doc.splitTextToSize(actionsIntro, pageWidth - 28);
        doc.text(wrappedActions, 14, yPos);
        yPos += wrappedActions.length * 5 + 10;

        // Priority actions table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('#', 18, yPos + 5);
        doc.text('Action', 30, yPos + 5);
        doc.text('Priority', 115, yPos + 5);
        doc.text('Est. Benefit', 145, yPos + 5);
        doc.text('Timeline', 180, yPos + 5);
        yPos += 7;

        const priorityActions = [
            ['1', 'Review & update binding death benefit nominations', 'HIGH', '$' + Math.round(pensionTaxableComp * 0.17 / 1000) + 'k tax saved', 'Immediate'],
            ['2', 'Complete annual investment strategy review', 'HIGH', 'Compliance', '30 days'],
            ['3', 'Rebalance away from collectibles (10% → 7%)', 'MEDIUM', 'Risk reduction', '90 days'],
            ['4', 'Maximize Mary & Sarah TBC usage', 'MEDIUM', '$' + Math.round((1900000 - 0) * 0.07 * 0.15 / 1000) + 'k/yr tax', '12 months'],
            ['5', 'Review corporate trustee conversion', 'MEDIUM', 'Succession', 'Annual review'],
            ['6', 'Utilize carry-forward contributions', 'LOW', '$' + Math.round(totalCF * 0.32 / 1000) + 'k tax benefit', 'If eligible'],
            ['7', 'Property LRBA review (cash flow negative)', 'MEDIUM', 'Risk mgmt', '6 months'],
            ['8', 'Update EPOA documentation', 'MEDIUM', 'Capacity protection', '90 days']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        priorityActions.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 20, yPos + 4);
            doc.text(row[1], 30, yPos + 4);
            const priorityColor = row[2] === 'HIGH' ? [239, 68, 68] : row[2] === 'MEDIUM' ? [245, 158, 11] : [59, 130, 246];
            doc.setTextColor(...priorityColor);
            doc.setFont(undefined, 'bold');
            doc.text(row[2], 118, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            doc.text(row[3], 148, yPos + 4);
            doc.text(row[4], 183, yPos + 4);
            yPos += 6;
        });
        yPos += 15;

        // ========================================
        // RISK ANALYSIS & CONSIDERATIONS
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'RISK ANALYSIS & KEY CONSIDERATIONS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const riskIntro = `Every investment strategy carries risks. For SMSFs, risks extend beyond market volatility to include compliance risk, liquidity risk, concentration risk, and trustee liability. Understanding and managing these risks is critical for long-term fund sustainability.`;
        const wrappedRisk = doc.splitTextToSize(riskIntro, pageWidth - 28);
        doc.text(wrappedRisk, 14, yPos);
        yPos += wrappedRisk.length * 5 + 10;

        // Risk assessment table
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Risk Type', 18, yPos + 5);
        doc.text('Assessment', 75, yPos + 5);
        doc.text('Current Status', 115, yPos + 5);
        doc.text('Mitigation', 160, yPos + 5);
        yPos += 7;

        const riskAssessment = [
            ['Market/Investment Risk', 'MODERATE', 'Diversified portfolio', 'Regular rebalancing'],
            ['Concentration Risk', 'LOW-MED', 'Property 35%, Collect 10%', 'Monitor allocations'],
            ['Liquidity Risk', 'LOW', `$${Math.round(fundBalance * 0.05 / 1000)}k cash`, 'Adequate reserves'],
            ['Compliance Risk', 'LOW', 'On track for FY24-25', 'Annual reviews'],
            ['Trustee Succession', trusteeType === 'individual' ? 'MEDIUM' : 'LOW', numMembers + ' members', trusteeType === 'individual' ? 'Consider corporate' : 'Documented'],
            ['LRBA Cash Flow', netRentalIncome < 0 ? 'MEDIUM' : 'LOW', netRentalIncome < 0 ? 'Negative $' + Math.round(Math.abs(netRentalIncome) / 1000) + 'k' : 'Positive', 'Monitor property']
        ];

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        riskAssessment.forEach((row, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 6, 'F');
            }
            doc.text(row[0], 18, yPos + 4);
            const riskColor = row[1].includes('HIGH') ? [239, 68, 68] : row[1].includes('MED') ? [245, 158, 11] : [16, 185, 129];
            doc.setTextColor(...riskColor);
            doc.setFont(undefined, 'bold');
            doc.text(row[1], 78, yPos + 4);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
            doc.text(row[2], 118, yPos + 4);
            doc.text(row[3], 163, yPos + 4);
            yPos += 6;
        });
        yPos += 15;

        // ========================================
        // COMPLIANCE STATUS DASHBOARD
        // ========================================
        if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'COMPLIANCE STATUS DASHBOARD - CURRENT YEAR');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const dashboardIntro = `This dashboard provides a quick visual overview of key compliance requirements for the current financial year:`;
        const wrappedDashIntro = doc.splitTextToSize(dashboardIntro, pageWidth - 28);
        doc.text(wrappedDashIntro, 14, yPos);
        yPos += wrappedDashIntro.length * 5 + 10;

        // Compliance Dashboard Items with Visual Indicators
        const dashboardItems = [
            { item: 'Annual Financial Statements Prepared', status: 'compliant', note: 'Due by 31 October' },
            { item: 'Annual Audit Completed', status: 'compliant', note: 'Independent auditor required' },
            { item: 'Annual Return Lodged with ATO', status: 'compliant', note: 'Due by 28 February' },
            { item: 'Investment Strategy Reviewed', status: 'compliant', note: 'Required annually' },
            { item: 'In-House Assets < 5% Limit', status: inHousePercent <= 5 ? 'compliant' : 'warning', note: `Currently ${inHousePercent.toFixed(1)}%` },
            { item: 'LRBA on Commercial Terms', status: hasProperty ? 'compliant' : 'na', note: hasProperty ? 'LVR within limits' : 'Not applicable' },
            { item: 'Member Benefits Separated', status: 'compliant', note: 'Required for 2+ members' },
            { item: 'Insurance Coverage Review', status: 'review', note: 'Annual review recommended' }
        ];

        // Draw compliance table with visual indicators
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, pageWidth - 28, 7, 'F');
        doc.text('Compliance Item', 18, yPos + 5);
        doc.text('Status', 120, yPos + 5);
        doc.text('Notes', 145, yPos + 5);
        yPos += 7;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        dashboardItems.forEach((item, index) => {
            if (index % 2 === 1) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos, pageWidth - 28, 8, 'F');
            }

            // Item text
            doc.setTextColor(0, 0, 0);
            doc.text(item.item, 18, yPos + 5);

            // Status indicator (colored circle + text)
            if (item.status === 'compliant') {
                doc.setFillColor(16, 185, 129); // Green
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(16, 185, 129);
                doc.text('Compliant', 128, yPos + 5);
            } else if (item.status === 'warning') {
                doc.setFillColor(245, 158, 11); // Amber
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(245, 158, 11);
                doc.text('Review', 128, yPos + 5);
            } else if (item.status === 'review') {
                doc.setFillColor(59, 130, 246); // Blue
                doc.circle(124, yPos + 3, 2, 'F');
                doc.setTextColor(59, 130, 246);
                doc.text('Action', 128, yPos + 5);
            } else {
                doc.setTextColor(150, 150, 150);
                doc.text('N/A', 128, yPos + 5);
            }

            // Notes
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(7);
            doc.text(item.note, 145, yPos + 5);
            doc.setFontSize(8);

            yPos += 8;
        });
        yPos += 15;

        // ========================================
        // RETIREMENT PLANNING TOOLS
        // ========================================
        doc.addPage();
        yPos = 20;

        this.addSectionHeader(doc, yPos, 'RETIREMENT PLANNING TOOLS');
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const retirePlanIntro = `The SMSF Calculator includes retirement planning tools to help you estimate pension income and plan your transition from accumulation to pension phase.`;
        const wrappedRetirePlan = doc.splitTextToSize(retirePlanIntro, pageWidth - 28);
        doc.text(wrappedRetirePlan, 14, yPos);
        yPos += wrappedRetirePlan.length * 5 + 10;

        // ===== QUICK RETIREMENT CHECK =====
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Quick Retirement Check (30-Second Snapshot)', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const quickCheckIntro = `The Quick Retirement Check provides a simplified 3-scenario analysis of your retirement prospects in under 30 seconds. This is NOT a comprehensive analysis (use the Advanced Calculator for that), but rather a quick sanity check to understand ballpark outcomes.`;
        const wrappedQC = doc.splitTextToSize(quickCheckIntro, pageWidth - 28);
        doc.text(wrappedQC, 14, yPos);
        yPos += wrappedQC.length * 5 + 8;

        // Quick Check Features
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('Features:', 18, yPos);
        yPos += 5;
        
        doc.setFont(undefined, 'normal');
        const qcFeatures = [
            '• 3-Scenario Analysis: Tests your retirement under Optimistic (9% returns), Realistic (7% returns), and Pessimistic (5% returns) conditions',
            '• Pension Type Comparison: Compares Account-Based Pension, Transition to Retirement (TTR) Pension, and Lifetime Annuity options',
            '• Lump Sum Planning: Shows the impact of taking lump sum withdrawals from your super balance',
            '• 10-Year Projection Chart: Visualizes how your balance changes over the first 10 years of retirement',
            '• Call-to-Action: Links directly to the Advanced Calculator for comprehensive historical simulation and Monte Carlo analysis'
        ];
        
        qcFeatures.forEach((feature) => {
            if (yPos > pageHeight - 15) {
                doc.addPage();
                yPos = 20;
            }
            const wrappedFeature = doc.splitTextToSize(feature, pageWidth - 40);
            doc.text(wrappedFeature, 22, yPos);
            yPos += wrappedFeature.length * 4.5 + 3;
        });
        yPos += 5;

        this.addInfoBox(doc, yPos,
            '⚠️ Important: The Quick Check uses SIMPLIFIED assumptions and is for indicative purposes only. For accurate retirement planning with historical data (1928-2025), Monte Carlo simulation, and comprehensive tax/pension modeling, use the Advanced Calculator.',
            'warning'
        );
        yPos += 27;

        // ===== TRANSFER TO ADVANCED CALCULATOR =====
        if (yPos > pageHeight - 120) {
            doc.addPage();
            yPos = 20;
        }

        this.addSectionHeader(doc, yPos, 'Transfer to Advanced Calculator (Proportional Asset Allocation)');
        yPos += 12;

        // Important notice box
        this.addInfoBox(doc, yPos,
            '⚠️ Important: The Quick Check uses SIMPLIFIED assumptions. For accurate retirement planning with historical data (1928-2025), Monte Carlo simulation, and comprehensive tax/pension modeling, use the Advanced Calculator.',
            'warning'
        );
        yPos += 30;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const transferIntro = `When transferring a member's balance from the SMSF Calculator to the Advanced Calculator, the system performs a PROPORTIONAL transfer based on each member's allocated percentage of fund assets (as defined in the Asset Valuation Manager).`;
        const wrappedTransfer = doc.splitTextToSize(transferIntro, pageWidth - 28);
        doc.text(wrappedTransfer, 14, yPos);
        yPos += wrappedTransfer.length * 5 + 10;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Transfer Logic:', 14, yPos);
        doc.setTextColor(0);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const transferLogic = [
            '1. Member Share Calculation: Each member receives their allocated % of total fund assets (NOT the entire fund)',
            '2. Proportional Asset Allocation: All asset classes (shares, property, infrastructure, collectibles) are transferred proportionally',
            '3. Fee Allocation: Admin and investment fees are proportionally allocated based on the member\'s share',
            '4. Rental Income: If the fund holds income-producing property, the member\'s share of rental income is calculated',
            '5. No Double-Counting: Super balances in the SMSF are converted to portfolio assets; they are not transferred as separate "super" amounts'
        ];
        
        transferLogic.forEach((logic, index) => {
            if (yPos > pageHeight - 20) {
                doc.addPage();
                yPos = 20;
            }
            // Add subtle background for readability
            if (index % 2 === 0) {
                doc.setFillColor(248, 250, 252);
                doc.rect(14, yPos - 3, pageWidth - 28, 6, 'F');
            }
            const wrappedLogic = doc.splitTextToSize(logic, pageWidth - 36);
            doc.text(wrappedLogic, 18, yPos);
            yPos += wrappedLogic.length * 5 + 4;
        });
        yPos += 8;

        this.addInfoBox(doc, yPos,
            '✅ Accurate Transfer: This proportional transfer ensures each member\'s Advanced Calculator analysis reflects only THEIR share of the SMSF assets, preventing overstatement and ensuring accurate retirement projections.',
            'success'
        );
        yPos += 30;

        // Corporate Disclaimer
        this.addCorporateDisclaimer(doc, yPos);

        // Save
        doc.save(`SuperCalcPro-SMSF-Comprehensive-${new Date().toISOString().split('T')[0]}.pdf`);
    }

    /**
     * Helper: Get strategy name
     */
    static getStrategyName(strategy) {
        const names = {
            'fixed': 'Fixed Sustainable Income',
            'DynamicSWR': 'Dynamic SWR',
            'VanguardGuardrails': 'Vanguard Guardrails',
            'FloorAndCeiling': 'Floor and Ceiling'
        };
        return names[strategy] || strategy;
    }

    /**
     * Helper: Get strategy description
     */
    static getStrategyDescription(strategy, inputs) {
        const descriptions = {
            'fixed': `Fixed Sustainable Income: Withdraws a consistent inflation-adjusted amount each year. Prioritizes predictable income.`,
            'DynamicSWR': `Dynamic SWR: Adjusts withdrawals annually based on portfolio value (${inputs.swrRate || 4}% rate). Adapts to market conditions.`,
            'VanguardGuardrails': `Vanguard Guardrails: Uses ±20% bands around initial ${inputs.vanguardInitialRate || 4}% rate. Balances flexibility with stability.`,
            'FloorAndCeiling': `Floor and Ceiling: Minimum ${inputs.floorCeilingFloorRate || 3}% floor, maximum ${inputs.floorCeilingCeilingRate || 6}% ceiling. Protects essential spending.`
        };
        return descriptions[strategy] || 'Custom withdrawal strategy.';
    }

    /**
     * Helper: Interpret success rate
     */
    static getSuccessRateInterpretation(successRate) {
        if (successRate >= 95) {
            return `Excellent (${successRate.toFixed(1)}%): Very high probability of success. Portfolio lasted in 95+ out of 100 scenarios.`;
        } else if (successRate >= 85) {
            return `Good (${successRate.toFixed(1)}%): Good probability of success. Portfolio lasted in about ${Math.round(successRate)} out of 100 scenarios.`;
        } else if (successRate >= 75) {
            return `Moderate (${successRate.toFixed(1)}%): Moderate success rate. Consider reducing expenses or increasing savings.`;
        } else if (successRate >= 50) {
            return `Concerning (${successRate.toFixed(1)}%): Only 50-75% success rate in this scenario. Consider consulting a financial adviser to explore alternatives.`;
        } else {
            return `High Risk (${successRate.toFixed(1)}%): Less than 50% success in this illustrative scenario. Portfolio depleted in most simulations. Consult a financial adviser to explore alternative scenarios.`;
        }
    }
}

export default PDFExportManager;
